import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, TextField, Typography, InputAdornment, Skeleton, Chip, Alert, AlertTitle, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton,
  RadioGroup, FormControlLabel, Radio, Divider, IconButton, Tooltip,
  Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Stack,
} from '@mui/material';
import {
  Search as SearchIcon, Inventory2 as InventoryIcon,
  OpenInNew as OpenInNewIcon, ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import { canViewProductLinks } from '../config/permissions';
import api from '../services/api';
import productLinksService from '../services/ProductLinksService';
import {
  printItemLabels, isPrintBridgeAvailable, deriveProductType,
} from '../services/printBridgeService';
import UnifiedOrderCard from '../components/receiving/UnifiedOrderCard';
import { notifyOrderChanged, ORDER_CHANGED } from '../components/noke/restock-v2/useOrderWorkflow';
import { submitCounterReceipt, submitCounterCancelLine, submitCounterCancelOrder } from '../components/receiving/counterReceipt';
import ColorPlateDialog from '../components/receiving/ColorPlateDialog';
import HoverZoomImage from '../components/HoverZoomImage';
import ShippingPreparationModal from '../components/ShippingPreparationModal';
import SFInternationalShippingModal, { SFShippingResult } from '../components/SFInternationalShippingModal';
import ParxlShippingModal, { ParxlShippingResult } from '../components/ParxlShippingModal';
// HomeDeliveryModal removed — home delivery now uses direct API call like StorefrontOrdersPage
import {
  UnifiedOrder, UnifiedOrderItem, OrderSlotInfo, Platform, PLATFORM_LABELS, PLATFORM_COLORS,
  isReadyToShipOrder, isCounterComplete,
} from '../components/receiving/types';
import { allocateByOrderTime, DemandLine } from '../components/receiving/shortShipAllocation';
import {
  normalizePhoneDigits, orderTextMatches, orderIdentityMatches,
} from '../components/receiving/orderSearch';
import { isIntlOrder, intlCountryOf, intlRegionOf, isSfIntlOrder, sfCountryOf } from '../config/intlRegions';
import { CategoryKey, CATEGORY_DEFS, CATEGORY_LABELS, classifyProductName } from '../utils/productCategory';
import { withSinsangOrder, SinsangOptionMap } from '../components/receiving/sinsangAutofill';
import { DemandGroup, buildDemandGroups, cartEligibleSkus as packCartEligibleSkus, normalizeVariant, styleKeyOfKey } from '../components/receiving/demandGrouping';

declare global {
  interface Window {
    __trackedItems?: Set<string>;
  }
}

// One 款 in 叫貨 mode: the product plus every pending colour/size under it.
// Now carries `demandSource`, so a 門市 line and a customer line for the same SKU
// are two cards and two totals — see components/receiving/demandGrouping.ts for why
// that separation lives in the type rather than in an `if` here.
type ProductGroup = DemandGroup;

// 叫貨 list rows. With 多帳號模式 off the list is all cards; with it on, an
// account header is spliced in ahead of each cart's products so one flat map
// renders both shapes.
type ProductRow =
  | { kind: 'header'; account: number | null; groups: ProductGroup[] }
  | { kind: 'card'; group: ProductGroup };

// GET /api/storefront/admin/variant-options — powers the 斷貨 follow-up dialog.
// `disabled` = every variant carrying that option value is already sold out;
// `locked` = it comes from the backend's hardcoded DISABLED_VARIANT_COLORS map
// (undoing that one needs a deploy, so the checkbox stays read-only).
type VariantOptionValue = { value: string; variantCount: number; disabled: boolean; locked: boolean };
type VariantOptionGroup = { name: string; position: number; values: VariantOptionValue[] };
type VariantOptionsResp = {
  sku: string;
  storefront: {
    productId: number; handle: string; title: string; status: string;
    variantCount: number; enabledVariantCount: number; optionGroups: VariantOptionGroup[];
  } | null;
  shopee: { itemId: number; title: string; status: string } | null;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (d: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Tracking-row identity. `sku` is part of the key so two line items in one order
// that share product_name + variant (different SKU) don't collide onto one row.
// sku='' = the legacy / no-sku key (used as a fallback for historical rows).
const trackingKeyFor = (orderId: string, productName: string, variant: string, sku: string = '') =>
  `${orderId}_${productName}_${variant || 'Default'}_${sku || ''}`;

// normalizePhoneDigits / phoneMatchesQuery / orderTextMatches / orderIdentityMatches now
// live in components/receiving/orderSearch.ts. They moved because THREE places in this
// file asked 「does this order match what was typed?」 by calling
// `order.customerName.toLowerCase()` bare — and a 門市 parcel has no customer, so the
// first counter card would have thrown out of a useMemo and blanked the whole queue. One
// function, one guard, and a test that can actually reach it.

// ─── 寄件編號 / 追蹤號碼 search ───────────────────────────────────────────────
// Staff read the code off a physical label (7-11 交貨便 寄件編號, 全家, 新竹, 順豐,
// Shopee) and type it with stray spaces or dashes, so everything is compared in a
// stripped, lower-cased form. 7-11 prints 寄貨編號+驗證碼 as one 12-char string
// (F78087564702) while ECPay stores them as two fields, so the concatenation is a
// candidate too.
const normalizeShipCode = (s: string): string => (s || '').replace(/[^0-9a-z]/gi, '').toLowerCase();

const shipmentCodesOf = (order: UnifiedOrder): string[] => {
  const raw = order.original || {};
  const codes: (string | null | undefined)[] = [
    order.trackingNumber, order.paymentNo, order.cvsValidationNo, order.ecpayLogisticsId,
    order.paymentNo && order.cvsValidationNo ? `${order.paymentNo}${order.cvsValidationNo}` : null,
    // Raw carrier columns the normalizers collapse into one trackingNumber — kept
    // here so the client matches exactly what the server search matches on.
    raw.tracking_number, raw.payment_no, raw.cvs_payment_no, raw.cvs_validation_no,
    raw.ecpay_logistics_id, raw.hsinchu_tracking_number, raw.sf_waybill_no, raw.parxl_tracking_no,
  ];
  // 補寄 parcels carry their own codes — the label in hand may be a reship's.
  (order.reships || []).forEach(r => {
    codes.push(r.trackingNumber, r.paymentNo, r.validationNo, r.ecpayLogisticsId,
      r.paymentNo && r.validationNo ? `${r.paymentNo}${r.validationNo}` : null);
  });
  return codes.map(c => normalizeShipCode(c || '')).filter(Boolean);
};

// codeQ = normalizeShipCode(query), precomputed once per search. ≥4 chars required —
// 驗證碼 is 4 digits, and anything shorter would false-match SKUs and sizes.
const shipmentCodeMatches = (order: UnifiedOrder, codeQ: string): boolean =>
  codeQ.length >= 4 && shipmentCodesOf(order).some(c => c.includes(codeQ));

// True when the typed string looks like a bare shipment code rather than a name or
// SKU — used to auto-fire the all-time server search, because a shipped order is
// usually outside the page's live (unshipped) window.
const looksLikeShipmentCode = (q: string): boolean => {
  const c = (q || '').trim().replace(/[\s-]/g, '');
  // ORD-19044 fits the shape but is an order id, not a parcel code — searching one
  // stays a local search unless 「搜尋全部時間」 is explicitly on.
  if (/^ord\d+$/i.test(c)) return false;
  return c.length >= 6 && /^[A-Za-z]{0,3}\d{5,}[A-Za-z0-9]*$/.test(c);
};

const formatAddress = (address: any): string => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  return [address.address1, address.address2, address.city, address.province, address.zip]
    .filter(Boolean).join(', ');
};

// ─── Storefront proportional pricing ──────────────────────────────────────────
// Mirror of the backend out-of-stock-notify proportional calculation
// (storefront.routes.js out-of-stock-notify). Distributes the order-level promo
// (discount_amount) across items proportionally to sellingPrice × qty so each
// item carries its true paid share. Shared by the refund dialog AND the 超商出貨
// modal — the latter uses it to exclude 斷貨 items from the collected goods amount.
//
// Fixed 現折 tiers (Magnus 2026-10-14): when the order's promo is fixed_amount and
// the items left after 斷貨 still meet its min_order, the customer keeps the whole
// 250/500 — 斷貨 lines refund at full price and the discount is spread over the
// remaining lines only. Below the threshold the proration above stays. Mirrors
// backend utils/oosPaidShares.js — change both together.
export interface StorefrontPriceCtx {
  itemLineTotals: Map<string, number>; // per-item paid line total (item + order-level promo applied)
  itemsSubtotal: number;               // Σ sellingPrice × qty (item-level discount already applied)
  discountAmount: number;              // order-level promo
  shippingFee: number;
  originalTotal: number;               // floor(itemsSubtotal) - discountAmount + shippingFee
  storeCreditUsed: number;             // 購物金 applied at checkout (orders.store_credit_used)
  paidTotal: number;                   // originalTotal - storeCreditUsed = what the customer pays / was charged
  currentTotal: number;                // order.totalAmount (may be reduced by a prior 斷貨通知)
  oosDeductionApplied: number;         // amount a prior 斷貨通知 already took off total_amount
  keepsTier: boolean;                  // fixed 現折 tier still met without the 斷貨 lines
}

// Exchange-order ship guard: the customer must drop off the old item at 7-11
// before we ship the replacement. Returns false when shipping should abort.
const confirmShipUnreturnedExchange = (order: UnifiedOrder): boolean => {
  if (order.exchangeRequest && !order.exchangeRequest.customerShippedReturn) {
    return window.confirm('🚫 換貨訂單：客人還未寄回舊商品，請不要出貨！\n\n確定仍要強制出貨嗎？');
  }
  return true;
};

// MdoC 「新官網優惠 −10%」, live from backend 488ab72 to its removal in 6b51187 /
// storefront b4b6f057. Checkout charged every MdoC line floor(price × 0.9), but the
// cart snapshot (orders.items) and COD order_items kept the plain price, and no
// column records the 10% — only total_amount has it. ORD-20145: 640×2 − 115 promo
// rebuilt to 1165, charged 1037 (1280×0.9 − 115). Re-applied below only where the
// snapshot shows the plain price: a line already carrying a lower price (加購 6折,
// VIP 半價, or a snapshot that stored the −10% price itself, as from 2026-10-11)
// is left alone. Manual resends are excluded — their snapshot lines carry no
// discountPrice and staff typed the total. 任2件 (promos id 7, now 85折) is an
// ordinary promo and already lives in discount_amount.
const MDOC_SITE_PROMO_RATE = 0.1;
const MDOC_SITE_PROMO_START = Date.parse('2026-04-11T14:48:41+08:00');
const MDOC_SITE_PROMO_END = Date.parse('2026-10-14T00:47:57+08:00');
const MDOC_ITEM_RE = /mdoc|doing\s*what/i;

const pricedWithMdocSitePromo = (orig: any): boolean => {
  const createdAt = Date.parse(orig.created_at);
  if (!(createdAt >= MDOC_SITE_PROMO_START && createdAt < MDOC_SITE_PROMO_END)) return false;
  let cart: any[] = [];
  try {
    cart = typeof orig.items === 'string' ? JSON.parse(orig.items) : (orig.items || []);
  } catch { cart = []; }
  return Array.isArray(cart) && cart.some((l: any) => l && (l.discountPrice != null || l.discount_price != null));
};

const buildStorefrontPriceCtx = (order: UnifiedOrder): StorefrontPriceCtx => {
  const orig: any = order.original || {};
  const discountAmount = parseFloat(orig.discount_amount) || 0;
  const shippingFee = parseFloat(orig.shipping_fee) || 0;
  const mdocSitePromo = pricedWithMdocSitePromo(orig);

  const unitOf = (item: UnifiedOrderItem) => {
    // 贈品 lines are NT$0 by design — never let catalog/backfilled prices leak in,
    // or the gift absorbs a share of the paid total (wrong COD amount + refunds).
    if (item.originalItem?.is_gift) return 0;
    const unit = parseFloat(
      item.originalItem?.cart_discount_price ??
      item.originalItem?.cart_price ??
      item.originalItem?.discount_price ??
      item.originalItem?.price ?? 0
    ) || 0;
    if (mdocSitePromo && unit > 0 && MDOC_ITEM_RE.test(item.productName || '')) {
      const listUnit = parseFloat(item.originalItem?.cart_price ?? item.originalItem?.price ?? 0) || 0;
      if (unit >= listUnit) return Math.floor(unit * (1 - MDOC_SITE_PROMO_RATE));
    }
    return unit;
  };

  const lineList = (i: UnifiedOrderItem) => unitOf(i) * (i.quantity || 1);
  const itemsSubtotal = order.items.reduce((s, i) => s + lineList(i), 0);

  const originalTotal = Math.floor(itemsSubtotal) - discountAmount + shippingFee;

  const keepsTier = keepsTierAfterOos(orig.promo_rule, discountAmount, order.items, lineList);
  // The lines the order-level promo is spread over.
  const baseLines = keepsTier ? order.items.filter(i => !i.outOfStock) : order.items;
  const baseSubtotal = baseLines.reduce((s, i) => s + lineList(i), 0);
  const baseEffective = Math.floor(baseSubtotal) - discountAmount;

  const itemLineTotals = new Map<string, number>();
  for (const item of order.items) {
    const listLine = lineList(item);
    const paidLine = keepsTier && item.outOfStock
      ? Math.round(listLine)
      : baseSubtotal > 0
        ? Math.round(baseEffective * (listLine / baseSubtotal))
        : Math.floor(listLine);
    itemLineTotals.set(item.id, paidLine);
  }

  // 購物金 is paid before checkout, so card/LINE Pay was charged — and COD
  // collects — originalTotal minus the credit. Every refund cap and every
  // collected amount must start from paidTotal, never originalTotal.
  const storeCreditUsed = Math.round(parseFloat(orig.store_credit_used) || 0);
  const paidTotal = Math.max(0, originalTotal - storeCreditUsed);

  const currentTotal = Math.floor(order.totalAmount || 0);
  // Prefer the recorded deduction; orders notified before that column existed
  // fall back to the gap between what was owed and the current total.
  const recordedOos = Math.round(parseFloat(orig.oos_deducted_amount) || 0);
  const oosDeductionApplied = recordedOos > 0 ? recordedOos : Math.max(0, paidTotal - currentTotal);

  return { itemLineTotals, itemsSubtotal, discountAmount, shippingFee, originalTotal, storeCreditUsed, paidTotal, currentTotal, oosDeductionApplied, keepsTier };
};

// promo_rule comes from GET /api/storefront/orders (the order's promos row).
// Eligible = storefront lines of the promo's vendor (MdoC/outlet/gifts never
// count toward a metanoia threshold), same as checkout-pricing.
const keepsTierAfterOos = (
  promo: any, discountAmount: number, items: UnifiedOrderItem[],
  lineList: (i: UnifiedOrderItem) => number,
): boolean => {
  if (!promo || promo.type !== 'fixed_amount') return false;
  const discount = Math.round(discountAmount);
  if (discount <= 0 || discount !== Math.round(parseFloat(promo.value) || 0)) return false; // VIP etc. won
  const oos = items.filter(i => i.outOfStock);
  if (oos.length === 0 || oos.length === items.length) return false;
  const filter = String(promo.vendor_filter || '').trim().toLowerCase();
  const vendorOf = (i: UnifiedOrderItem) => {
    const t = String(i.productName || '').toLowerCase();
    return t.includes('mdoc') || t.includes('m°c') || t.includes('doing what') ? 'mdoc' : 'metanoia';
  };
  const eligible = (i: UnifiedOrderItem) => {
    if (i.originalItem?.is_gift) return false;
    const type = i.originalItem?.item_type || 'storefront';
    return type === 'storefront' && (!filter || vendorOf(i) === filter);
  };
  const remaining = items.filter(i => !i.outOfStock && eligible(i)).reduce((s, i) => s + lineList(i), 0);
  return remaining >= (parseFloat(promo.min_order) || 0);
};

// Determine delivery method using same logic as ShopifyOrdersPage getLogisticsTypeLabel
const detectDeliveryMethod = (order: any): string => {
  // International orders (SF Express, HK/MO …) — detect early so they don't fall
  // through to the Hsinchu Taiwan home-delivery branch. Legacy intl_hk and new
  // intl (+ shipping_country) rows both resolve here via the shared registry.
  if (isIntlOrder(order)) return 'intl';

  const storeId = order.store_id || '';
  const logisticsSubType = order.logistics_sub_type || '';
  if (logisticsSubType === 'FAMIC2C' || logisticsSubType === 'UNIMARTC2C') return 'pickup';
  if (storeId) return 'pickup';
  const addr = typeof order.shipping_address === 'string' ? order.shipping_address
    : order.shipping_address?.address1 || '';
  const addrLower = addr.toLowerCase();
  if (addrLower.includes('全家') || addrLower.includes('family') || addrLower.includes('7-eleven') ||
      addrLower.includes('7-11') || addrLower.includes('711') || addrLower.includes('超商') ||
      addrLower.includes('unimart')) return 'pickup';
  // If delivery_method is explicitly set, use it
  if (order.delivery_method === 'shipping' || order.delivery_method === 'home_delivery') return 'home_delivery';
  if (order.delivery_method === 'pickup') return 'pickup';
  // Default: if no store_id and no CVS keywords → home delivery
  return 'home_delivery';
};

// MdoC / Doing What are cosmetics — not part of the Korean clothing supply chain
// and always held in local stock, so there's nothing to "receive" off a shipment.
// Auto-mark them received so the order is immediately shippable and staff never
// have to tick them. Mirrors the cosmetics half of the backend bin-exclusion
// regex (slot.service.js EXCLUDE_NAME_RE). 贈品 lines (is_gift, e.g. the 滿額贈 中筒襪)
// come from local gift stock too, so they're auto-received the same way.
const AUTO_RECEIVE_RE = /mdoc|doing\s*what/i;
const isAutoReceiveItem = (productName: string, isGift = false): boolean =>
  isGift || (!!productName && AUTO_RECEIVE_RE.test(productName));

const extractColor = (variantOrSpec: string): string => {
  if (!variantOrSpec || !variantOrSpec.trim()) return '';
  const v = variantOrSpec.trim();
  // Split on common separators: dash, slash, comma (with optional surrounding spaces)
  const parts = v.split(/\s*[-\/,]\s*/);
  return parts[0] || v;
};

const getProductKey = (sku: string, productName: string, itemSpec: string) => {
  const color = extractColor(itemSpec);
  if (sku && sku.trim()) {
    return color ? `${sku.trim()}|${color}` : sku.trim();
  }
  return `${productName}|${color || 'default'}`.toLowerCase().trim();
};

// Legacy key for fallback lookup (old format without color)
const getLegacyProductKey = (sku: string) => {
  return sku && sku.trim() ? sku.trim() : null;
};

// ─── Normalizers ────────────────────────────────────────────────────────────

function normalizeShopeeOrders(
  orderDetails: any[],
  trackingData: any[],
  metadataMap: Record<string, any>,
  itemDetails: Record<string, any> = {},
  skuTagMap: Record<string, string> = {}
): UnifiedOrder[] {
  const trackingMap = new Map<string, any>();
  trackingData.forEach(t => {
    const osn = t.order_sn || t.orderSn;
    const pn = t.product_name || t.productName || '';
    const spec = t.item_spec || t.itemSpec || '';
    if (osn) trackingMap.set(`${osn}_${pn}_${spec}`, t);
  });

  return orderDetails
    .filter(o => {
      const s = (o.order_status || '').toUpperCase();
      return !['COMPLETED', 'SHIPPED'].includes(s);
    })
    .map(order => {
      const items: UnifiedOrderItem[] = (order.item_list || []).map((item: any) => {
        const key = `${order.order_sn}_${item.item_name}_${item.model_name || ''}`;
        const tr = trackingMap.get(key) || {};
        const pk = getProductKey(item.model_sku || item.item_sku || '', item.item_name, item.model_name || '');
        const pkLegacy = getLegacyProductKey(item.model_sku || item.item_sku || '');
        const meta = metadataMap[pk] || (pkLegacy ? metadataMap[pkLegacy] : null) || {};
        // Build all_images from product details (like old OrdersPage)
        const allImages: string[] = [];
        if (item.image_info?.image_url) allImages.push(item.image_info.image_url);
        if (item.item_id && itemDetails[item.item_id]) {
          const detailed = itemDetails[item.item_id];
          if (detailed.image?.image_url_list) {
            detailed.image.image_url_list.forEach((url: string) => {
              if (!allImages.includes(url)) allImages.push(url);
            });
          }
        }

        return {
          id: `shopee-${order.order_sn}-${item.item_id}-${item.model_id}`,
          productName: item.item_name || '',
          variantOrSpec: item.model_name || '',
          sku: item.model_sku || item.item_sku || '',
          quantity: item.model_quantity_purchased || 1,
          imageUrl: item.image_info?.image_url || '',
          received: isAutoReceiveItem(item.item_name || '') || tr.received || false,
          outOfStock: tr.outOfStock || tr.out_of_stock || false,
          specOverride: tr.specOverride || tr.spec_override || null,
          missingCount: tr.missingCount || tr.missing_count || 0,
          note: tr.note || '',
          tag: meta.tag || tr.tag || skuTagMap[(item.model_sku || item.item_sku || '').trim()] || '',
          referenceImage: meta.reference_image || tr.referenceImage,
          receivedBy: tr.receivedBy || tr.received_by,
          receivedAt: tr.receivedAt || tr.received_at,
          originalItem: { ...item, all_images: JSON.stringify(allImages) },
        };
      });

      const receivedCount = items.filter(i => i.received).length;
      const orderDate = new Date(order.create_time * 1000);

      // Shopee tracking: check order-level, then package_list, then any package with a tracking number
      const shopeeTracking = order.tracking_number
        || order.package_list?.find((p: any) => p.tracking_number)?.tracking_number
        || null;

      const shippingCarrier = order.shipping_carrier
        || order.checkout_shipping_carrier
        || order.package_list?.[0]?.shipping_carrier
        || '';

      return {
        id: `shopee-${order.order_sn}`,
        platform: 'shopee' as Platform,
        orderId: order.order_sn.toString(),
        orderNumber: order.order_sn.toString(),
        customerName: order.buyer_username || order.recipient_address?.name || '',
        customerPhone: order.recipient_address?.phone || '',
        orderDate,
        orderDateDisplay: fmtDate(orderDate),
        status: order.order_status,
        deliveryMethod: 'shopee_shipping',
        shippingCarrier,
        shippingAddress: '',
        trackingNumber: shopeeTracking,
        hasTrackingInfo: !!shopeeTracking,
        items,
        receivedCount,
        totalCount: items.length,
        allReceived: items.length > 0 && items.every(i => i.received || i.outOfStock),
        original: order,
      };
    });
}

function normalizeShopifyOrders(
  orders: any[],
  trackingData: any[],
  metadataMap: Record<string, any>,
  skuTagMap: Record<string, string> = {}
): UnifiedOrder[] {
  const trackingMap = new Map<string, any>();
  trackingData.forEach(t => {
    const oid = t.orderId || t.order_id || t.orderSn || t.order_sn;
    const pn = t.productName || t.product_name || '';
    const vt = t.variantTitle || t.variant_title || t.itemSpec || 'Default';
    if (oid) trackingMap.set(`${oid}_${pn}_${vt}`, t);
  });

  return orders
    .filter(o => {
      const fs = (o.fulfillment_status || 'unfulfilled').toLowerCase();
      const fin = (o.financial_status || '').toLowerCase();
      return fs !== 'fulfilled' && !o.is_hidden &&
        fin !== 'refunded' && fin !== 'voided' &&
        !o.cancelled_at;
    })
    .map(order => {
      const orderNum = (order.order_number || order.orderNumber || order.id || '').toString();
      let displayNum = orderNum;
      if (order.shopify_store_name === 'mdoc') displayNum = `mdoc-${orderNum}`;

      const lineItems = order.items || order.line_items || [];
      const items: UnifiedOrderItem[] = lineItems.map((item: any, idx: number) => {
        const pn = item.product_name || item.title || item.name || '';
        const vt = item.variant_title || item.itemSpec || 'Default';
        const lookupKey = `${orderNum}_${pn}_${vt}`;
        const tr = trackingMap.get(lookupKey) || {};
        const pk = getProductKey(item.sku || '', pn, vt);
        const pkLegacy = getLegacyProductKey(item.sku || '');
        const meta = metadataMap[pk] || (pkLegacy ? metadataMap[pkLegacy] : null) || {};

        return {
          id: `shopify-${orderNum}-${item.shopify_line_item_id || item.id || idx}`,
          productName: pn,
          variantOrSpec: vt === 'Default' ? '' : vt,
          sku: item.sku || '',
          quantity: item.quantity || 1,
          imageUrl: item.image_url || '',
          received: isAutoReceiveItem(pn, !!item.is_gift) || tr.received || false,
          missingCount: tr.missing_count || tr.missingCount || 0,
          note: tr.note || '',
          tag: meta.tag || tr.tag || skuTagMap[(item.sku || '').trim()] || '',
          referenceImage: meta.reference_image || tr.reference_image,
          receivedBy: tr.received_by || tr.receivedBy,
          receivedAt: tr.received_at || tr.receivedAt,
          originalItem: (() => {
            // Build all_images for gallery
            const imgs: string[] = [];
            if (item.image_url) imgs.push(item.image_url);
            if (item.product_images) item.product_images.forEach((pi: any) => {
              if (pi.src && !imgs.includes(pi.src)) imgs.push(pi.src);
            });
            return { ...item, all_images: JSON.stringify(imgs) };
          })(),
        };
      });

      const receivedCount = items.filter(i => i.received).length;
      const orderDate = order.created_at ? new Date(order.created_at) : new Date();

      const customerName = order.customer_name ||
        [order.customer?.last_name, order.customer?.first_name].filter(Boolean).join('') ||
        'Customer';

      const tn = order.tracking_number || null;
      const pn = order.payment_no || null;
      const cvn = order.cvs_validation_no || null;
      const eli = order.ecpay_logistics_id || null;

      return {
        id: `shopify-${orderNum}`,
        platform: 'shopify' as Platform,
        orderId: orderNum,
        orderNumber: displayNum,
        customerName,
        customerPhone: order.customer_phone || '',
        customerEmail: order.customer_email || '',
        orderDate,
        orderDateDisplay: fmtDate(orderDate),
        status: order.financial_status || 'pending',
        deliveryMethod: detectDeliveryMethod(order),
        storeId: order.store_id,
        storeName: order.store_name,
        shippingAddress: formatAddress(order.shipping_address),
        totalAmount: parseFloat(order.total_price) || 0,
        trackingNumber: tn,
        paymentNo: pn,
        cvsValidationNo: cvn,
        ecpayLogisticsId: eli,
        logisticsSubType: order.logistics_sub_type,
        hasTrackingInfo: !!(tn || pn || eli),
        items,
        receivedCount,
        totalCount: items.length,
        allReceived: items.length > 0 && receivedCount === items.length,
        original: order,
      };
    });
}

function normalizeStorefrontOrders(
  orders: any[],
  trackingData: any[],
  metadataMap: Record<string, any>,
  skuTagMap: Record<string, string> = {},
  includeShipped: boolean = false,
  includeCancelled: boolean = false
): UnifiedOrder[] {
  const trackingMap = new Map<string, any>();
  trackingData.forEach(t => {
    const key = trackingKeyFor(t.orderId, t.productName, t.variantTitle, t.sku || '');
    trackingMap.set(key, t);
  });

  return orders
    .filter(o => {
      const s = (o.status || '').toUpperCase();
      if (!includeCancelled && s === 'CANCELLED') return false;
      if (!includeShipped && (s === 'SHIPPED' || s === 'DELIVERED')) return false;
      return true;
    })
    .map(order => {
      // Parse cart JSON for real transaction prices (order_items may have 0/catalog prices)
      let cartItems: any[] = [];
      try {
        cartItems = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
      } catch { cartItems = []; }

      const getCartPrice = (itemId: number, variantId: string | null) => {
        const match = cartItems.find((ci: any) =>
          (ci.itemId === itemId || ci.id === itemId) &&
          (!variantId || ci.variantId === variantId || ci.variant_id === variantId)
        ) || cartItems.find((ci: any) => ci.itemId === itemId || ci.id === itemId);
        if (match) {
          return {
            price: parseFloat(match.price || match.compare_at_price || 0),
            discountPrice: parseFloat(match.discountPrice || match.discount_price || match.price || 0),
          };
        }
        return null;
      };

      // Debug first order's cart data
      if (cartItems.length > 0 && order === orders.filter((o: any) => !['SHIPPED','DELIVERED','CANCELLED'].includes((o.status||'').toUpperCase()))[0]) {
        console.log('[Receiving] Storefront cart JSON sample:', JSON.stringify(cartItems[0]).substring(0, 300));
        console.log('[Receiving] Storefront order_items[0]:', JSON.stringify((order.order_items || [])[0])?.substring(0, 300));
      }

      const orderItems = order.order_items || [];
      const items: UnifiedOrderItem[] = orderItems.map((item: any, idx: number) => {
        const pn = item.title || '';
        const vt = item.variant_title || item.option1 || 'Default';
        // Match the SKU-specific tracking row first; fall back to the legacy
        // (no-sku) row so a historical received item still reads as received
        // until it's next toggled (which writes its own SKU-keyed row).
        const tr = trackingMap.get(trackingKeyFor(order.order_id, pn, vt, item.sku || ''))
                || trackingMap.get(trackingKeyFor(order.order_id, pn, vt, ''))
                || {};
        const pk = getProductKey(item.sku || '', pn, vt);
        const pkLegacy = getLegacyProductKey(item.sku || '');
        const meta = metadataMap[pk] || (pkLegacy ? metadataMap[pkLegacy] : null) || {};
        const cartPrice = getCartPrice(item.item_id, item.variant_id);

        return {
          id: `sf-${order.order_id}-${item.id || idx}`,
          productName: pn,
          variantOrSpec: vt === 'Default' ? '' : vt,
          sku: item.sku || '',
          quantity: item.quantity || 1,
          imageUrl: item.image_url || '',
          received: isAutoReceiveItem(pn, !!item.is_gift) || tr.received || false,
          outOfStock: tr.outOfStock || false,
          missingCount: tr.missingCount || tr.missing_count || 0,
          note: tr.note || '',
          tag: meta.tag || tr.tag || skuTagMap[(item.sku || '').trim()] || '',
          referenceImage: meta.reference_image || tr.referenceImage,
          receivedBy: tr.receivedBy || tr.received_by,
          receivedAt: tr.receivedAt || tr.received_at,
          originalItem: (() => {
            const imgs: string[] = [];
            if (item.image_url) imgs.push(item.image_url);
            if (item.all_images) {
              const parsed = typeof item.all_images === 'string' ? (() => { try { return JSON.parse(item.all_images); } catch { return []; } })() : item.all_images;
              if (Array.isArray(parsed)) parsed.forEach((u: string) => { if (u && !imgs.includes(u)) imgs.push(u); });
            }
            // Attach cart prices (real transaction prices)
            const origPrice = cartPrice?.price || item.price || 0;
            const sellPrice = cartPrice?.discountPrice || cartPrice?.price || item.discount_price || item.price || 0;
            if (idx === 0 && order === orders.filter((o: any) => !['SHIPPED','DELIVERED','CANCELLED'].includes((o.status||'').toUpperCase()))[0]) {
              console.log('[Receiving] Price debug:', { cartPrice, itemPrice: item.price, itemDiscountPrice: item.discount_price, origPrice, sellPrice });
            }
            return {
              ...item,
              all_images: JSON.stringify(imgs),
              cart_price: origPrice,
              cart_discount_price: sellPrice,
            };
          })(),
        };
      });

      const receivedCount = items.filter(i => i.received).length;
      // 斷貨 items don't need 已收到 — treat as fulfilled for shipping purposes
      const allReceived = items.length > 0 && items.every(i => i.received || i.outOfStock);
      const orderDate = order.created_at ? new Date(order.created_at) : new Date();

      return {
        id: `sf-${order.order_id}`,
        platform: 'storefront' as Platform,
        orderId: order.order_id,
        orderNumber: order.order_id,
        customerName: order.customer_name || '',
        customerPhone: order.customer_phone || '',
        customerEmail: order.customer_email || '',
        orderDate,
        orderDateDisplay: fmtDate(orderDate),
        status: order.status,
        deliveryMethod: detectDeliveryMethod(order),
        storeId: order.store_id,
        storeName: order.store_name,
        storeAddress: order.store_address,
        shippingAddress: formatAddress(order.shipping_address),
        totalAmount: order.total_amount,
        paymentMethod: order.payment_method,
        paymentStatus: order.payment_status,
        // A carrier waybill counts as tracking — treats intl orders the same as
        // TW 宅配/超商 once a label exists, so the card shows the 列印 button +
        // tracking chip without extra plumbing. parxl_tracking_no is the SG
        // (Parxl) equivalent of sf_waybill_no; the two never coexist on an order.
        trackingNumber: order.tracking_number || order.hsinchu_tracking_number || order.sf_waybill_no || order.parxl_tracking_no,
        paymentNo: order.cvs_payment_no,
        cvsValidationNo: order.cvs_validation_no,
        ecpayLogisticsId: order.ecpay_logistics_id,
        logisticsSubType: order.elo_logistics_sub_type || order.logistics_sub_type,
        hasTrackingInfo: !!(order.tracking_number || order.cvs_payment_no || order.ecpay_logistics_id || order.hsinchu_tracking_number || order.sf_waybill_no || order.parxl_tracking_no),
        items,
        receivedCount,
        totalCount: items.length,
        allReceived,
        original: order,
      };
    });
}

// ─── 門市 orders → receiving cards ───────────────────────────────────────────
//
// Magnus 2026-09-18: 「for the 訂貨 i want to make it that for the items that i select..
// it creates a new 門市 order in the receiving page. shows as a new type of card. will be
// for 門市 or else 出貨人員 will not know if it is 門市 or for what people or order」.
//
// The feed (GET /api/receiving/counter-orders, adminAuth) carries no customer column of
// any kind — there is no customer — so this builder leaves customerName/customerPhone
// UNSET rather than filling them with ''. An empty string would have let every consumer
// keep assuming a customer exists; `undefined` makes the compiler point at each one.
//
// A 門市 order never gets a waybill, so hasTrackingInfo is permanently false and the card
// must NOT be judged by 可以出貨 (`allReceived && !hasTracking`) — see isReadyToShipOrder
// in components/receiving/types.ts.
// `skuTagMap` is the LIVE 進貨店家 index (product metadata, keyed by SKU). `source_tag`
// on the line is only the snapshot taken when the parcel was ordered, so an edit made on
// the card would be undone by the next 30s refresh if the snapshot won. Live tag first.
function buildCounterOrders(counterOrders: any[], skuTagMap: Record<string, string> = {}): UnifiedOrder[] {
  return (counterOrders || []).map((o: any) => {
    const lines: any[] = Array.isArray(o.lines) ? o.lines : [];
    const items: UnifiedOrderItem[] = lines
      .filter((l: any) => l && !l.cancelled_at)
      .map((l: any) => {
        const ordered = Number(l.qty) || 0;
        const cancelled = Number(l.cancelled_qty) || 0;
        // NULL received_qty on a received line = a row stamped before the column
        // existed; treat it as "arrived in full" rather than inventing a shortfall.
        // Not when it was closed by 移除 (cancelled_qty) — that line received nothing.
        const got = l.received_qty == null ? (l.received_at && !cancelled ? ordered : 0) : Number(l.received_qty) || 0;
        const remaining = l.received_at ? 0 : Math.max(0, ordered - got - cancelled);
        return {
          id: `counter-line-${l.id}`,
          productName: l.title || `SKU ${l.sku}`,
          variantOrSpec: [l.color, l.size].filter(Boolean).join(' / '),
          sku: String(l.sku || ''),
          quantity: ordered,
          imageUrl: l.image_url || '',
          received: !!l.received_at,
          // missingCount is the page's existing "short" field, so 短少 reuses it and
          // every existing readout of it keeps meaning the same thing.
          missingCount: l.received_at ? Math.max(0, ordered - got - cancelled) : 0,
          note: '',
          tag: skuTagMap[String(l.sku || '').trim()] || l.source_tag || '',
          receivedBy: l.received_by || undefined,
          receivedAt: l.received_at || undefined,
          counterLine: {
            lineId: Number(l.id),
            variantId: Number(l.variant_id),
            orderedAt: l.ordered_at,
            receivedQty: got,
            remainingQty: remaining,
            cancelledQty: cancelled,
          },
          originalItem: l,
        };
      });
    const receivedCount = items.filter(i => i.received).length;
    const orderDate = o.created_at ? new Date(o.created_at) : new Date();
    return {
      id: `counter-${o.id}`,
      platform: 'counter' as Platform,
      demandSource: 'counter' as const,
      orderId: String(o.id),
      orderNumber: `門市 #${o.id}`,
      orderDate,
      orderDateDisplay: fmtDate(orderDate),
      status: o.cancelled_at ? 'CANCELLED' : 'COUNTER_OPEN',
      deliveryMethod: 'counter',
      // No waybill exists or ever will. Kept explicit so nothing infers one later.
      hasTrackingInfo: false,
      items,
      receivedCount,
      totalCount: items.length,
      allReceived: items.length > 0 && items.every(i => i.received),
      counterOrder: {
        id: Number(o.id),
        clientRef: String(o.client_ref || ''),
        label: o.label || null,
        note: o.note || null,
        createdBy: o.created_by || null,
        sourceTags: Array.isArray(o.source_tags) ? o.source_tags : [],
        piecePlanned: Number(o.piece_count) || 0,
        pieceReceived: Number(o.received_pieces) || 0,
        pieceOpen: Number(o.open_pieces) || 0,
        pieceShort: Number(o.short_pieces) || 0,
        cancelledAt: o.cancelled_at || null,
      },
      original: o,
    };
  });
}

// ─── Page Component ──────────────��──────────────────────────────────────────

// ─── Search input (isolated to keep keystrokes off the giant page tree) ─────

interface SearchInputProps {
  initialValue?: string;
  onDebouncedChange: (value: string) => void;
  delay?: number;
  autoFocus?: boolean;
  // Fires when Enter is pressed — flushes the current value immediately (bypassing debounce).
  onEnter?: (value: string) => void;
  // Imperative value setter: bump nonce to force the field to `value` (收貨 mode
  // clears after each receive; 查看訂單 preloads the order id before jumping views).
  setSignal?: { nonce: number; value: string };
  placeholder?: string;
}
const SearchInput = React.memo(React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ initialValue = '', onDebouncedChange, delay = 250, autoFocus, onEnter, setSignal, placeholder }, ref) => {
    const [value, setValue] = useState(initialValue);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const cbRef = useRef(onDebouncedChange);
    useEffect(() => { cbRef.current = onDebouncedChange; }, [onDebouncedChange]);
    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
    // Apply imperative value sets from the parent (skip initial mount).
    const firstSet = useRef(true);
    const setValueRef = useRef(setSignal?.value ?? '');
    setValueRef.current = setSignal?.value ?? '';
    useEffect(() => {
      if (firstSet.current) { firstSet.current = false; return; }
      const v = setValueRef.current;
      setValue(v);
      if (timerRef.current) clearTimeout(timerRef.current);
      cbRef.current(v);
    }, [setSignal?.nonce]);
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setValue(v);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => cbRef.current(v), delay);
    }, [delay]);
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (timerRef.current) clearTimeout(timerRef.current);
        const v = (e.target as HTMLInputElement).value;
        cbRef.current(v);
        onEnter?.(v);
      }
    }, [onEnter]);
    return (
      <TextField
        inputRef={ref}
        fullWidth
        autoFocus={autoFocus}
        placeholder={placeholder ?? "掃描或輸入 SKU / 標籤 / 商品名稱 / 備註 搜尋訂單..."}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#999' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            borderRadius: '8px',
            fontSize: '1rem',
          },
        }}
      />
    );
  }
));
SearchInput.displayName = 'SearchInput';

// ─── 收貨 (receiving) mode types ─────────────────────────────────────────────
// One candidate = one unreceived line (an order's line item) that could be credited.
interface ReceiveCandidate { order: UnifiedOrder; item: UnifiedOrderItem; }
// One variant button inside a style card. `candidates` is sorted oldest-first (FIFO).
interface ReceiveVariantGroup {
  variant: string;      // normalized display variant ("color / size")
  color: string;        // parsed color ('' if none)
  size: string;         // parsed size ('' if none)
  lineCount: number;    // unreceived lines for this variant
  candidates: ReceiveCandidate[];
}
// One disambiguation card = a distinct STYLE (sku||productName), aggregated across
// all matching unshipped orders. The human matches the style from the photo, then
// picks the variant read off the garment's own label — so ONE card per style with
// variant buttons inside, not one card per variant.
interface ReceiveStyleGroup {
  key: string;
  sku: string;
  productName: string;
  imageUrl: string;
  tag: string;          // representative tag for the chip
  totalCount: number;   // total unreceived lines across all variants
  variants: ReceiveVariantGroup[];
}
// Snapshot taken at the moment a variant is tapped, so 復原 / 換訂單 stay reachable
// after the search box is cleared.
interface ReceiveConfirmation {
  productName: string;
  variant: string;
  order: UnifiedOrder;              // currently-credited order
  item: UnifiedOrderItem;           // currently-credited line
  candidates: ReceiveCandidate[];   // variant's candidates at tap time (oldest-first)
}

// 收貨 variant-button ordering: letter sizes S<M<L<XL<XXL…, numeric / "(n)" sizes
// ascending after letters, F/FREE last. Unknown tokens sort just before FREE.
const RECEIVE_SIZE_RANK: Record<string, number> = {
  XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, '2XL': 6, '3XL': 7, '4XL': 8,
};
const receiveSizeRank = (sizeToken: string): number => {
  const t = (sizeToken || '').trim().toUpperCase();
  if (!t || t === '-') return 950;
  if (t === 'F' || t === 'FREE') return 900;
  if (RECEIVE_SIZE_RANK[t] != null) return RECEIVE_SIZE_RANK[t];
  const num = t.match(/^[(（]?(\d+(?:\.\d+)?)[)）]?$/);
  if (num) return 100 + parseFloat(num[1]);
  return 800;
};
// Trailing "(n)" suffix on a variant (e.g. "深藍 / XL(4)") — secondary sort key,
// since parseVariant strips the paren suffix from the size token.
const receiveParenSuffix = (variant: string): number => {
  const m = variant.match(/[(（](\d+)[)）]\s*$/);
  return m ? parseInt(m[1], 10) : 0;
};
// Product image list for the 收貨 gallery — same sources the 訂單 view's 相簿 uses
// (UnifiedOrderCard.getItemImages / ProductImageGallery): cover + all_images
// (shopee/storefront) or product_images (shopify) from the raw order line.
const extractItemImages = (item: UnifiedOrderItem): string[] => {
  const parse = (raw: any): string[] | null => {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed) && parsed.length > 0) {
        const urls = parsed.filter((u: any) => typeof u === 'string' && u.length > 0);
        if (urls.length > 0) return urls;
      }
    } catch { /* fall through */ }
    return null;
  };
  if (item.originalItem?.all_images) {
    const imgs = parse(item.originalItem.all_images);
    if (imgs) return imgs;
  }
  if (item.originalItem?.product_images) {
    const imgs = parse(item.originalItem.product_images);
    if (imgs) return imgs;
  }
  return item.imageUrl ? [item.imageUrl] : [];
};

// Parse "海軍藍 / XL" / "白色,M" / "F" / "深藍 / XL(4)" into { color, size }. Heuristic:
// strip trailing parenthetical notes, then anything matching SIZE_REGEX is treated as size;
// the remainder is color. Falls back to positional split.
const SIZE_REGEX = /^(F|FREE|XS|S|M|L|XL|XXL|2XL|3XL|4XL|\d{2,3}|F\d|加大)$/i;
const stripParenSuffix = (s: string) => s.replace(/[\s]*[(（].*?[)）]\s*$/u, '').trim();
const parseVariant = (raw: string): { color: string; size: string } => {
  if (!raw) return { color: '', size: '' };
  const parts = raw.split(/[\/,,]/).map(s => s.trim()).filter(Boolean);
  if (parts.length === 0) return { color: '', size: '' };
  let color = '', size = '';
  for (const p of parts) {
    const core = stripParenSuffix(p);
    if (!size && SIZE_REGEX.test(core)) size = core.toUpperCase();
    else if (!color) color = core || p;
  }
  if (!color && !size) {
    color = stripParenSuffix(parts[0]) || parts[0];
    if (parts[1]) size = stripParenSuffix(parts[1]) || parts[1];
  }
  return { color, size };
};

// ── 叫貨 cycle state shared across tabs ─────────────────────────────────────
const GROUPING_AFTER_DATE_KEY = 'receivingGroupingAfterDate';
const MULTI_ACCOUNT_MODE_KEY = 'sinsangMultiAccountMode';
// How long the date field must sit untouched before the list follows it.
const DATE_COMMIT_DELAY_MS = 1500;
// How long the eligible 款 set must stay unchanged before auto-assign claims
// cart slots for it. Slots are sticky for the whole cycle, so a transient list
// (half-typed date, a filter mid-change) must never reach the server.
const ASSIGN_SETTLE_MS = 1500;
// Other tabs are told to re-read the map after any assign / clear.
const CART_MARKS_CHANNEL = 'sinsang-cart-marks';
// Background re-read of the map while the tab is visible — covers other
// devices, which BroadcastChannel can't reach.
const CART_MARKS_POLL_MS = 30000;

const ReceivingQueuePage: React.FC = () => {
  const { currentUser, shopeeAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const searchRef = useRef<HTMLInputElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const [allOrders, setAllOrders] = useState<UnifiedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  // 現貨 auto-receive: items already persisted this session, so a reload/poll doesn't re-POST them.
  const autoReceivePersistedRef = useRef<Set<string>>(new Set());
  // SKU → 進貨店家, from /api/products/metadata. Held in a ref because the 門市 feed
  // refreshes on its own timer and must rebuild its cards with the current tags —
  // including one just typed into a card (handleUpdateField writes here too).
  const skuTagMapRef = useRef<Record<string, string>>({});

  // 貨架格 (receiving bins) — fetched from the backend, merged into orders for display.
  const [slotData, setSlotData] = useState<{
    poolSize: number; targetPerBin: number; staleDays: number;
    items: Array<{ platform: string; orderId: string; productName: string; variantSpec: string; slotNumber: number; receivedAt: string | null; cancelled?: boolean; shipped?: boolean; imageUrl?: string }>;
  } | null>(null);
  const slotRefetchTimer = useRef<any>(null);
  const fetchSlots = useCallback(async (retryOnFail = true) => {
    try {
      const res = await api.get('/api/receiving/slots');
      setSlotData(res.data);
    } catch {
      // Non-fatal, but retry once — a transient blip here silently leaves the
      // bin badges stale until the next socket event or a full reload.
      if (retryOnFail) setTimeout(() => { fetchSlots(false); }, 1500);
    }
  }, []);
  const scheduleSlotRefetch = useCallback(() => {
    if (slotRefetchTimer.current) clearTimeout(slotRefetchTimer.current);
    slotRefetchTimer.current = setTimeout(() => { fetchSlots(); }, 600);
  }, [fetchSlots]);
  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // Known-cancelled storefront order ids. A bin whose order simply vanished from the fetch
  // is NOT proof of cancellation — shipped/old orders vanish too (the page only loads
  // status=unshipped). We treat a vanished bin as an orphan ONLY if its order is in this
  // set (genuinely CANCELLED). Loaded once on mount; `loaded` gates the reclaim pass so we
  // never release a cancelled order's bin before we know it's cancelled.
  const [cancelledStorefrontIds, setCancelledStorefrontIds] = useState<Set<string>>(new Set());
  const [cancelledStorefrontLoaded, setCancelledStorefrontLoaded] = useState(false);

  // Load the recent CANCELLED storefront orders so we can tell a genuinely-cancelled bin
  // (item may still be on the shelf → orphan) from a shipped/old one (left the shelf →
  // release). Recent-only (limit 500): a very old cancellation won't still have shelf stock.
  useEffect(() => {
    let aborted = false;
    api.get('/api/storefront/orders', { params: { status: 'CANCELLED', limit: 500 } })
      .then(r => {
        if (aborted) return;
        const ids = new Set<string>(((r.data?.orders) || []).map((o: any) => String(o.order_id)));
        setCancelledStorefrontIds(ids);
      })
      .catch(() => { /* non-fatal — degrade to releasing vanished bins as before */ })
      .finally(() => { if (!aborted) setCancelledStorefrontLoaded(true); });
    return () => { aborted = true; };
  }, []);

  // One-time, on first load:
  //  (a) BACKFILL bins for multi-item, NOT-yet-shipped orders on the shelf with no bin yet.
  //  (b) RECLAIM bins (free the space) for orders that shipped (and not in THIS session) OR
  //      vanished from the page (shipped/old orders drop out of the status=unshipped fetch) —
  //      their items physically left the shelf, so the bin returns to the pool. justShipped
  //      orders keep their bin through the current packing session. EXCEPTION: a vanished order
  //      that's a known CANCELLED storefront order is left alone — its item may still be on the
  //      shelf, so it's surfaced as an orphan bin (see orphanBins) for staff to remove manually.
  const backfilledRef = useRef(false);
  useEffect(() => {
    if (backfilledRef.current || loading || !slotData || allOrders.length === 0 || !cancelledStorefrontLoaded) return;
    backfilledRef.current = true;

    const orderByKey = new Map(allOrders.map(o => [`${o.platform}::${o.orderId}`, o]));
    const loadedPlatforms = new Set<string>(allOrders.map(o => o.platform));
    // How many items each order already has binned. An order can be PARTIALLY binned when a
    // later shipment's items are received after the first ones were already shelved.
    const binnedCountByOrder = new Map<string, number>();
    for (const it of (slotData.items || [])) {
      const k = `${it.platform}::${it.orderId}`;
      binnedCountByOrder.set(k, (binnedCountByOrder.get(k) || 0) + 1);
    }

    // Multi-item, not-yet-shipped orders that still have received items missing a bin →
    // backfill. Includes PARTIALLY-binned orders (fixes stragglers received in a later
    // shipment onto an order that already had bins). Server-side sync is idempotent + sticky,
    // so it only fills blanks; the < receivedCount test just avoids no-op calls.
    // 門市 parcels are excluded deliberately, and slot.service.js has NO 'counter' entry to
    // match: a received 門市 piece is stocked into noke_inventory in the same transaction
    // and physically leaves for the counter, so a bin number would point at an empty shelf.
    // (The TABLES map and the getSlottedRows UNION must always agree — an entry without an
    // arm would strand permanent phantom orphans — so this feature adds neither.)
    const toBackfill = allOrders
      .filter(o => o.platform !== 'counter'
        && !o.hasTrackingInfo && o.totalCount >= 2 && o.receivedCount > 0
        && (binnedCountByOrder.get(`${o.platform}::${o.orderId}`) || 0) < o.receivedCount)
      .map(o => ({ platform: o.platform, orderId: o.orderId, total: o.totalCount }));

    // Binned orders to release: shipped (and not just-shipped now), OR vanished from the page
    // (shipped/old — left the shelf). A vanished order that's a known CANCELLED storefront order
    // is NOT released — it's surfaced as an orphan bin for manual removal instead.
    const seen = new Set<string>();
    const toRelease: Array<{ platform: string; orderId: string }> = [];
    for (const it of (slotData.items || [])) {
      const k = `${it.platform}::${it.orderId}`;
      if (seen.has(k)) continue; seen.add(k);
      const o = orderByKey.get(k);
      // A vanished order is an ORPHAN (item may still be on the shelf) — do NOT auto-release it;
      // it's surfaced in orphanBins for manual 移除 instead. Storefront: order is cancelled
      // (server flag or known-cancelled id). Shopee: gone from the feed with no ship record.
      const vanishedOrphan = it.platform === 'shopee'
        ? it.shipped !== true
        : (it.cancelled === true || (it.platform === 'storefront' && cancelledStorefrontIds.has(it.orderId)));
      if (!o) {
        if (loadedPlatforms.has(it.platform) && !vanishedOrphan) toRelease.push({ platform: it.platform, orderId: it.orderId });
      } else if (o.hasTrackingInfo && !o.justShipped) {
        toRelease.push({ platform: it.platform, orderId: it.orderId });
      }
    }

    const calls: Promise<any>[] = [];
    if (toBackfill.length) calls.push(api.post('/api/receiving/slots/backfill', { orders: toBackfill }));
    if (toRelease.length) calls.push(api.post('/api/receiving/slots/release', { orders: toRelease }));
    if (calls.length === 0) return;
    Promise.all(calls).then(() => fetchSlots()).catch(() => { backfilledRef.current = false; });
  }, [loading, slotData, allOrders, fetchSlots, cancelledStorefrontLoaded, cancelledStorefrontIds]);

  // Orphan bin removal — an order vanished from the live fetch (e.g. a cancelled/
  // refunded Shopee order) but its item is still physically on the shelf. Staff pull
  // the item and click 移除; releasing frees every slot the order held.
  const handleReleaseOrphan = useCallback(async (platform: string, orderId: string) => {
    try {
      await api.post('/api/receiving/slots/release', { orders: [{ platform, orderId }] });
      await fetchSlots();
      enqueueSnackbar('已從貨架移除', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`移除失敗: ${err.message}`, { variant: 'error' });
    }
  }, [fetchSlots, enqueueSnackbar]);

  // Manual reset — staff moved the bag; drop + re-assign this order's bins.
  const handleResetSlot = useCallback(async (order: UnifiedOrder) => {
    try {
      await api.post('/api/receiving/slots/reset', {
        platform: order.platform, orderId: order.orderId, total: order.totalCount,
      });
      fetchSlots();
    } catch (err: any) {
      enqueueSnackbar(`重設貨架格失敗: ${err.message}`, { variant: 'error' });
    }
  }, [fetchSlots, enqueueSnackbar]);
  const [searchQuery, setSearchQuery] = useState('');
  // Category-first search: pick 上衣 / 外套 / 下身 / 配件… then keyword-search inside it,
  // so 「上衣」 stops dragging in every 落地褲 that shares a descriptor token.
  // Category is inferred from the product name (no category field exists on any
  // platform's line items) — see utils/productCategory.ts.
  const [categoryFilter, setCategoryFilter] = useState<CategoryKey | ''>('');
  const [colorFilter, setColorFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [shippingOrderId, setShippingOrderId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'unreceived' | 'ready' | 'shipped'>('all');
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest'>('oldest');
  // 未收到 grace window: a just-received item's 貨架格 bin is assigned server-side
  // ~1s after the click (batched write + socket refetch). In 未收到 mode the row
  // would normally vanish the instant it's checked — before the bin badge renders.
  // Keep such rows visible for GRACE_MS so staff can read the bin, then let them
  // drop off. Item id → visible. Timers held in a ref for cleanup / uncheck.
  const GRACE_MS = 10_000;
  const [graceReceivedIds, setGraceReceivedIds] = useState<Set<string>>(new Set());
  const graceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => () => {
    graceTimersRef.current.forEach(t => clearTimeout(t));
    graceTimersRef.current.clear();
  }, []);
  const noteItemReceived = useCallback((itemId: string, received: boolean) => {
    const timers = graceTimersRef.current;
    if (received) {
      if (timers.has(itemId)) clearTimeout(timers.get(itemId)!);
      setGraceReceivedIds(prev => { const n = new Set(prev); n.add(itemId); return n; });
      timers.set(itemId, setTimeout(() => {
        setGraceReceivedIds(prev => { const n = new Set(prev); n.delete(itemId); return n; });
        timers.delete(itemId);
      }, GRACE_MS));
    } else {
      if (timers.has(itemId)) { clearTimeout(timers.get(itemId)!); timers.delete(itemId); }
      setGraceReceivedIds(prev => {
        if (!prev.has(itemId)) return prev;
        const n = new Set(prev); n.delete(itemId); return n;
      });
    }
  }, []);
  // Auto-print 40x30mm item label via local print bridge when item marked received.
  // Persisted per-device in localStorage so each warehouse PC keeps its own setting.
  const [autoPrintLabel, setAutoPrintLabel] = useState<boolean>(() => {
    try { return localStorage.getItem('oms.autoPrintLabel') === '1'; } catch { return false; }
  });
  const [printBridgeOnline, setPrintBridgeOnline] = useState<boolean>(false);
  useEffect(() => {
    try { localStorage.setItem('oms.autoPrintLabel', autoPrintLabel ? '1' : '0'); } catch {}
  }, [autoPrintLabel]);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const ok = await isPrintBridgeAvailable(true);
      if (!cancelled) setPrintBridgeOnline(ok);
    };
    check();
    const t = setInterval(check, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const [tagFilter, setTagFilter] = useState<'all' | 'numbers' | 'no-numbers' | 'no-tag'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'shopee' | 'shopify' | 'storefront' | 'counter'>('all');
  // Big-order filter: only show orders with ≥5 items. Used by warehouse to
  // prioritize bagging items as they arrive (大訂單先包 workflow).
  const [bigOrderFilter, setBigOrderFilter] = useState(false);
  // HK-only filter: narrow to orders that ship via SF International (HK).
  // Independent toggle — works on top of source / status filters.
  const [hkOnlyFilter, setHkOnlyFilter] = useState(false);
  const [pinnedOnlyFilter, setPinnedOnlyFilter] = useState(false);
  // Lazy-fetched shipped storefront orders. Default fetch only hits
  // status=unshipped to keep the page snappy; this is loaded on demand when
  // the user clicks the 已出貨 status chip so they can search/inspect
  // already-shipped orders without leaving the receiving page.
  const [shippedStorefrontLoaded, setShippedStorefrontLoaded] = useState(false);
  const [shippedStorefrontLoading, setShippedStorefrontLoading] = useState(false);
  // 「搜尋全部時間」mode — when on, the search box also queries the storefront orders
  // DB directly (by order#/name/phone, ALL statuses, no recency limit), so orders older
  // than the on-page window (e.g. long-ago 已出貨 官網 orders) still surface. Storefront
  // only — it's the one platform with an unbounded, indexed server-side search.
  const [searchAllMode, setSearchAllMode] = useState(false);
  const [searchAllOrders, setSearchAllOrders] = useState<UnifiedOrder[]>([]);
  const [searchAllLoading, setSearchAllLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'orders' | 'products' | 'bins' | 'receive'>('orders');
  // 收貨 mode: after a variant tap, remember the credited line so 復原 / 換訂單 stay reachable.
  const [receiveConfirm, setReceiveConfirm] = useState<ReceiveConfirmation | null>(null);
  const [receiveSwitchOpen, setReceiveSwitchOpen] = useState(false);
  // Imperative search-box setter (nonce bump applies value): clear after each
  // receive, or preload the order id when jumping to the 訂單 view (查看訂單).
  const [searchSetSignal, setSearchSetSignal] = useState({ nonce: 0, value: '' });
  const pushSearchValue = useCallback((value: string) => {
    setSearchSetSignal(s => ({ nonce: s.nonce + 1, value }));
  }, []);
  // /receiving?order=ORD-123 (每日看板 到倉雷達 → 訂單): open the 訂單 view on that order's card.
  useEffect(() => {
    const order = new URLSearchParams(window.location.search).get('order');
    if (order) {
      setViewMode('orders');
      setStatusFilter('all'); // same as 查看訂單: a stale status filter must not hide the order
      pushSearchValue(order);
    }
  }, [pushSearchValue]);
  // 收貨 image gallery (tap the card photo to verify the garment).
  // 選色 plate — storefront flat lay + labelled colour markers, for "is this 灰色
  // or 淺灰?" moments. One dialog instance; every trigger just sets this.
  const [colorPlate, setColorPlate] = useState<{ sku: string; productName: string; color?: string } | null>(null);
  const handleOpenColorPlate = useCallback((item: { sku?: string; productName?: string; variantOrSpec?: string }) => {
    setColorPlate({
      sku: item.sku || '',
      productName: item.productName || '',
      color: parseVariant(item.variantOrSpec || '').color || undefined,
    });
  }, []);
  const [receiveGallery, setReceiveGallery] = useState<{ title: string; images: string[] } | null>(null);
  const [receiveGalleryIndex, setReceiveGalleryIndex] = useState(0);
  const [expandedGroupSpecs, setExpandedGroupSpecs] = useState<Set<string>>(new Set());
  const [linkBatchIndex, setLinkBatchIndex] = useState(0);
  // ── Sinsang 多帳號模式 ──────────────────────────────────────────────────
  // A Sinsang cart holds 99 distinct 款式 (colours/sizes of a 款 are free), so a
  // big 叫貨 cycle spills across several logins. Once a 款 lives in one cart, a
  // later order for it has to be a variant bump in THAT cart — so the server
  // keeps a sticky SKU → account map and we just render the partition.
  // Off by default: most cycles fit one cart and don't need the extra chrome.
  const [multiAccountMode, setMultiAccountMode] = useState(() => {
    try { return localStorage.getItem(MULTI_ACCOUNT_MODE_KEY) === '1'; } catch { return false; }
  });
  const [cartAccounts, setCartAccounts] = useState<Map<string, number>>(new Map());
  // Server-side fill per account. This is the count of ALL 款 the account owns,
  // not just the ones still pending on screen — a received 款 keeps its cart
  // slot until you actually check out, so capacity has to be read from the map.
  const [cartAccountFill, setCartAccountFill] = useState<
    { account: number; count: number; remaining: number }[]
  >([]);
  const [cartCapacity, setCartCapacity] = useState(99);
  const [cartAssigning, setCartAssigning] = useState(false);
  // Gate: assignment must not run before the stored map has arrived, or a slow
  // GET could land after an assign and blank out the just-placed 款.
  const [cartMapLoaded, setCartMapLoaded] = useState(false);
  // Gate: the first read of the server's cycle (mode + cutoff) has finished.
  const [cycleStateSynced, setCycleStateSynced] = useState(false);
  // Batch-open cursor per account — each cart is worked through independently.
  const [accountBatchIndex, setAccountBatchIndex] = useState<Record<number, number>>({});
  // How many links one press opens, per account. Sinsang is worked one login at
  // a time, so the comfortable batch differs by how full that cart already is.
  const [accountBatchSize, setAccountBatchSize] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem('sinsangBatchSize') || '{}'); } catch { return {}; }
  });
  // Expanded accounts, by key ('1', '2', … or 'none'). Empty = everything
  // collapsed, which is the useful default: a 400-款 cycle is unscrollable
  // otherwise, and you only ever work one cart at a time.
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [cartClearConfirm, setCartClearConfirm] = useState(false);
  // Auto-compute last ordering cutoff: Wed 20:00 or Sun 20:00 (whichever is most recent)
  const getLastOrderingCutoff = useCallback(() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 3=Wed
    const makeDate = (targetDay: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() - ((day - targetDay + 7) % 7));
      d.setHours(20, 0, 0, 0);
      if (d > now) d.setDate(d.getDate() - 7);
      return d;
    };
    const lastWed = makeDate(3);
    const lastSun = makeDate(0);
    return lastWed > lastSun ? lastWed : lastSun;
  }, []);
  // Format the cutoff as Taipei wall-clock "YYYY-MM-DDTHH:mm" for the datetime-local input.
  // NOTE: toISOString() returns UTC, which would shift the default 8h earlier on a Taipei machine.
  const defaultCutoffValue = useCallback(() => {
    const d = getLastOrderingCutoff();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [getLastOrderingCutoff]);
  // 篩選起始時間 defines the 叫貨 cycle, and in 多帳號模式 it decides which 款 get a
  // cart slot. It is persisted (and synced across tabs via the `storage` event)
  // so every tab and every return visit sees the SAME list — when each tab fell
  // back to its own default and was retyped by hand, the in-between values the
  // date input emits while typing (22 → "2" → 09-02) widened the list for a
  // moment and auto-assign permanently claimed slots for 67 款 outside the cycle
  // (2026-09-27). An empty string is a real value: 清除 means "no cutoff".
  const [groupingAfterDate, setGroupingAfterDate] = useState(() => {
    try {
      const stored = localStorage.getItem(GROUPING_AFTER_DATE_KEY);
      if (stored !== null) return stored;
    } catch { /* storage blocked — fall back to the default */ }
    // Format the cutoff as Taipei wall-clock "YYYY-MM-DDTHH:mm" for the datetime-local input.
    // NOTE: toISOString() returns UTC, which would shift the default 8h earlier on a Taipei machine.
    return defaultCutoffValue();
  });
  // What the input shows while it is being edited. The list (and therefore
  // auto-assign) only follows the COMMITTED value — on blur, Enter, or after the
  // field has been left alone for a moment — never a half-typed keystroke.
  const [groupingDateDraft, setGroupingDateDraft] = useState(groupingAfterDate);
  const groupingAfterDateRef = useRef(groupingAfterDate);
  const groupingDateDraftRef = useRef(groupingDateDraft);
  groupingDateDraftRef.current = groupingDateDraft;
  // The cycle (mode + cutoff) is server-owned so every device sees the same list
  // (/api/receiving/cycle-state); localStorage is only this browser's cache of it,
  // used for the first paint and to sync tabs instantly.
  //
  // Local changes queue in cyclePendingRef until a PUT confirms them. While
  // anything is queued or in flight, a poll must not apply the server's (older)
  // row: it re-sends the queue instead. Otherwise a GET racing the PUT, or the
  // first poll after a failed PUT, would quietly undo the change here — and a
  // failed save of "mode off" would leave every other device rebuilding the map.
  // The seq is bumped at both ends of each PUT so a GET started before or during
  // it is discarded.
  type CyclePatch = { multiAccountMode?: boolean; groupingAfterDate?: string };
  const cycleWriteSeqRef = useRef(0);
  const cyclePendingRef = useRef<CyclePatch>({});
  const cyclePutInFlightRef = useRef(0);
  const flushCycleState = useCallback(() => {
    const patch: CyclePatch = { ...cyclePendingRef.current };
    if (Object.keys(patch).length === 0) return;
    cycleWriteSeqRef.current += 1;
    cyclePutInFlightRef.current += 1;
    api.put('/api/receiving/cycle-state', { ...patch, userName: currentUser?.name })
      .then(() => {
        // Drop only what this PUT carried and nothing has changed since.
        const pending = cyclePendingRef.current;
        (Object.keys(patch) as (keyof CyclePatch)[]).forEach(k => {
          if (pending[k] === patch[k]) delete pending[k];
        });
      })
      .catch(err => {
        console.error('[Receiving] cycle-state save failed:', err);
        enqueueSnackbar('同步到其他裝置失敗，稍後自動重試', { variant: 'warning' });
      })
      .finally(() => {
        cyclePutInFlightRef.current -= 1;
        cycleWriteSeqRef.current += 1;
      });
  }, [currentUser, enqueueSnackbar]);
  const pushCycleState = useCallback((patch: CyclePatch) => {
    Object.assign(cyclePendingRef.current, patch);
    flushCycleState();
  }, [flushCycleState]);
  // source: 'user' = typed here (cache + push to server); 'server' = another
  // device changed it (cache only); 'storage' = another tab already cached it.
  const commitGroupingAfterDate = useCallback((value: string, source: 'user' | 'server' | 'storage' = 'user') => {
    // A background sync (this tab's poll, or another tab caching its poll) must
    // never clobber a date being typed right now.
    if (source !== 'user' && groupingDateDraftRef.current !== groupingAfterDateRef.current) return;
    setGroupingDateDraft(value);
    if (value === groupingAfterDateRef.current) return;
    groupingAfterDateRef.current = value;
    if (source !== 'storage') {
      try { localStorage.setItem(GROUPING_AFTER_DATE_KEY, value); } catch { /* private mode */ }
    }
    if (source === 'user') pushCycleState({ groupingAfterDate: value });
    setGroupingAfterDate(value);
    setLinkBatchIndex(0);
  }, [pushCycleState]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === GROUPING_AFTER_DATE_KEY && e.newValue !== null) commitGroupingAfterDate(e.newValue, 'storage');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [commitGroupingAfterDate]);
  useEffect(() => {
    if (groupingDateDraft === groupingAfterDate) return;
    const t = window.setTimeout(() => commitGroupingAfterDate(groupingDateDraft), DATE_COMMIT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [groupingDateDraft, groupingAfterDate, commitGroupingAfterDate]);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const ordersPerPage = 20;

  // Reship dialog state
  const [reshipDialogOpen, setReshipDialogOpen] = useState(false);
  const [reshipOrder, setReshipOrder] = useState<UnifiedOrder | null>(null);
  const [reshipPaymentType, setReshipPaymentType] = useState<'Y' | 'N'>('N');
  const [reshipCodAmount, setReshipCodAmount] = useState('');

  // Refund dialog state (storefront)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundOrder, setRefundOrder] = useState<UnifiedOrder | null>(null);
  const [refundMode, setRefundMode] = useState<'full' | 'partial'>('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundSelectedIds, setRefundSelectedIds] = useState<Set<string>>(new Set());
  const [refundProcessing, setRefundProcessing] = useState(false);
  type RefundHistoryRow = {
    id: number;
    payment_method: string;
    amount: number;
    cumulative_refunded: number | null;
    rtn_code: string | null;
    rtn_message: string | null;
    created_at: string;
    created_by: string | null;
  };
  const [refundHistory, setRefundHistory] = useState<RefundHistoryRow[]>([]);
  const [refundHistoryLoading, setRefundHistoryLoading] = useState(false);
  const alreadyRefunded = refundHistory.reduce((s, r) => s + (r.amount || 0), 0);

  // Admin 斷貨 follow-up dialog (triggered by the 斷貨 toggle on storefront items,
  // admin only). Two modes: sell out ONLY the 斷貨 colour/size, or delist the
  // whole product from 官網 / 蝦皮.
  const [delistDialogOpen, setDelistDialogOpen] = useState(false);
  const [delistItem, setDelistItem] = useState<{ sku: string; productName: string; variantOrSpec: string } | null>(null);
  const [delistMode, setDelistMode] = useState<'variant' | 'product'>('product');
  const [delistStorefront, setDelistStorefront] = useState(true);
  const [delistShopee, setDelistShopee] = useState(true);
  const [delistProcessing, setDelistProcessing] = useState(false);
  const [oosOptions, setOosOptions] = useState<VariantOptionsResp | null>(null);
  const [oosOptionsLoading, setOosOptionsLoading] = useState(false);
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([]);
  // Per-colour stop-sell only makes sense when a group has more than one value.
  const hasSplittableOptions = useMemo(
    () => (oosOptions?.storefront?.optionGroups || []).some(g => g.values.length > 1),
    [oosOptions]
  );
  // Clear variant filters whenever the search query changes
  useEffect(() => {
    setColorFilter('');
    setSizeFilter('');
  }, [searchQuery]);

  // Shipping modals
  const [shippingModalOrder, setShippingModalOrder] = useState<any>(null);
  // SF International (HK/MO …) modal — shown for international orders
  const [sfModalOrder, setSfModalOrder] = useState<UnifiedOrder | null>(null);
  // Parxl (SG) booking modal — separate carrier, separate dialog. Never both.
  const [parxlModalOrder, setParxlModalOrder] = useState<UnifiedOrder | null>(null);

  // ── Fetch all orders ──────────────────────────────────────────────────────

  const fetchAllOrders = useCallback(async () => {
    setLoading(true);
    // The main fetch replaces allOrders, which would wipe any shipped
    // storefront orders we lazy-loaded. Clear the loaded flag so the auto-
    // trigger refetches them if the user is still viewing 已出貨.
    setShippedStorefrontLoaded(false);
    try {
      // Fire all fetches in parallel
      const [
        shopeeRes, shopifyRes, sfRes,
        shopeeTrackRes, shopifyTrackRes, sfTrackRes, metadataRes, pinRes, counterRes
      ] = await Promise.allSettled([
        shopeeAuthenticated
          ? api.get('/api/shopee/orders').then(async (r) => {
              const orderList = r.data.response?.order_list || [];
              if (orderList.length === 0) return { details: [] };
              const sns = orderList.map((o: any) => o.order_sn);
              const detailsRes = await api.get(`/api/shopee/orders/details?orderSnList=${sns.join(',')}`);
              const details = detailsRes.data.response?.order_list || [];

              // Enrich PROCESSED/SHIPPED orders with tracking numbers from logistics API
              const needTracking = details.filter((o: any) =>
                ['PROCESSED', 'SHIPPED', 'READY_TO_SHIP'].includes(o.order_status) && !o.tracking_number
              );
              if (needTracking.length > 0) {
                const trackingResults = await Promise.allSettled(
                  needTracking.map((o: any) =>
                    api.get(`/api/shopee/shipping/tracking/${o.order_sn}`).catch(() => null)
                  )
                );
                trackingResults.forEach((res, idx) => {
                  if (res.status === 'fulfilled' && res.value?.data?.response?.tracking_number) {
                    needTracking[idx].tracking_number = res.value.data.response.tracking_number;
                  }
                });
              }

              return { details };
            })
          : Promise.resolve({ details: [] }),
        api.get('/api/shopify/orders', { params: { limit: 600, fulfillmentFilter: 'unfulfilled' } }),
        api.get('/api/storefront/orders', { params: { limit: 1000, status: 'unshipped' } }),
        api.get('/api/orders/tracking').catch(() => ({ data: [] })),
        api.get('/api/orders/shopify-tracking').catch(() => ({ data: [] })),
        api.get('/api/storefront/orders-tracking').catch(() => ({ data: [] })),
        api.get('/api/products/metadata').catch(() => ({ data: [] })),
        api.get('/api/orders/pins').catch(() => ({ data: [] })),
        // 門市 parcels. `.catch` to an empty feed on purpose: a database one deploy
        // behind answers { ready: false, orders: [] }, and a 500 here must not be able
        // to take down a page whose real job is customer orders.
        // limit 500 = the server's maximum. This route is the ONLY way a 門市 line can be
        // received (the counter's own 收到 is scoped away from batched rows), so a parcel
        // that falls off the end of this page has no receiver at all — hence the max, and
        // hence the warning below when the server still says it truncated.
        api.get('/api/receiving/counter-orders', { params: { open_only: true, limit: 500 } })
          .catch(() => ({ data: { ready: false, orders: [] } })),
      ]);

      // Build pin (需注意) lookup keyed by "{platform}:{orderId}"
      const pinList: any[] = pinRes.status === 'fulfilled' ? (pinRes.value as any).data || [] : [];
      const pinMap: Record<string, { note: string; pinnedBy?: string; pinnedAt?: string }> = {};
      pinList.forEach((p: any) => {
        pinMap[`${p.platform}:${p.orderId}`] = {
          note: p.note || '',
          pinnedBy: p.pinnedBy,
          pinnedAt: p.pinnedAt,
        };
      });

      const shopeeDetails = shopeeRes.status === 'fulfilled' ? (shopeeRes.value as any).details || [] : [];
      const shopifyOrders = shopifyRes.status === 'fulfilled' ? (shopifyRes.value as any).data?.orders || [] : [];
      const sfOrders = sfRes.status === 'fulfilled' ? (sfRes.value as any).data?.orders || [] : [];
      const shopeeTracking = shopeeTrackRes.status === 'fulfilled' ? (shopeeTrackRes.value as any).data || [] : [];
      const shopifyTracking = shopifyTrackRes.status === 'fulfilled' ? (shopifyTrackRes.value as any).data || [] : [];
      const sfTracking = sfTrackRes.status === 'fulfilled' ? (sfTrackRes.value as any).data || [] : [];

      // Build product metadata lookup { productKey → { tag, reference_image } }
      const metadataRaw: any[] = metadataRes.status === 'fulfilled'
        ? (metadataRes.value as any)?.data || [] : [];
      const metadataMap: Record<string, any> = {};
      metadataRaw.forEach((m: any) => {
        const key = m.product_key || m.productKey;
        if (key) metadataMap[key] = m;
      });
      // Build SKU→tag index so all variants of the same SKU share the tag
      const skuTagMap: Record<string, string> = {};
      metadataRaw.forEach((m: any) => {
        const key = m.product_key || m.productKey || '';
        const sku = key.split('|')[0];
        if (sku && m.tag && !skuTagMap[sku]) skuTagMap[sku] = m.tag;
      });
      skuTagMapRef.current = { ...skuTagMapRef.current, ...skuTagMap };

      // Debug: log failures only
      if (shopeeRes.status === 'rejected') console.error('[Receiving] Shopee FAILED:', (shopeeRes as any).reason);
      if (shopifyRes.status === 'rejected') console.error('[Receiving] Shopify FAILED:', (shopifyRes as any).reason);
      if (sfRes.status === 'rejected') console.error('[Receiving] Storefront FAILED:', (sfRes as any).reason);

      // Debug: log raw tracking fields from first order of each platform
      if (shopifyOrders.length > 0) {
        const so = shopifyOrders[0];
        console.log('[Receiving] Shopify sample raw fields:', {
          tracking_number: so.tracking_number, payment_no: so.payment_no,
          cvs_validation_no: so.cvs_validation_no, ecpay_logistics_id: so.ecpay_logistics_id,
          delivery_method: so.delivery_method, store_id: so.store_id,
        });
      }
      if (sfOrders.length > 0) {
        const sf = sfOrders[0];
        console.log('[Receiving] Storefront sample raw fields:', {
          tracking_number: sf.tracking_number, cvs_payment_no: sf.cvs_payment_no,
          ecpay_logistics_id: sf.ecpay_logistics_id, hsinchu_tracking_number: sf.hsinchu_tracking_number,
        });
      }
      if (shopeeDetails.length > 0) {
        const sp = shopeeDetails[0];
        console.log('[Receiving] Shopee sample raw fields:', {
          tracking_number: sp.tracking_number, order_status: sp.order_status,
          package_list: sp.package_list?.map((p: any) => ({ tracking_number: p.tracking_number, logistics_status: p.logistics_status })),
        });
      }

      // Fetch Shopee product details for images (image_url_list)
      const shopeeItemDetails: Record<string, any> = {};
      if (shopeeDetails.length > 0) {
        const allItemIds = new Set<string>();
        shopeeDetails.forEach((order: any) => {
          (order.item_list || []).forEach((item: any) => {
            if (item.item_id) allItemIds.add(item.item_id.toString());
          });
        });
        if (allItemIds.size > 0) {
          const itemIdList = Array.from(allItemIds);
          const batchSize = 50;
          try {
            const batchPromises = [];
            for (let i = 0; i < itemIdList.length; i += batchSize) {
              batchPromises.push(
                api.get('/api/shopee/products/details', {
                  params: { itemIds: itemIdList.slice(i, i + batchSize).join(',') }
                }).catch(() => null)
              );
            }
            const results = await Promise.all(batchPromises);
            for (const res of results) {
              if (res?.data?.response?.item_list) {
                for (const item of res.data.response.item_list) {
                  if (item.item_id) shopeeItemDetails[item.item_id] = item;
                }
              }
            }
          } catch (e) {
            console.error('[Receiving] Shopee product details fetch failed:', e);
          }
        }
      }

      const shopeeNorm = normalizeShopeeOrders(shopeeDetails, shopeeTracking, metadataMap, shopeeItemDetails, skuTagMap);
      const shopifyNorm = normalizeShopifyOrders(shopifyOrders, shopifyTracking, metadataMap, skuTagMap);
      const sfNorm = normalizeStorefrontOrders(sfOrders, sfTracking, metadataMap, skuTagMap);

      // 門市 parcels. An un-migrated database answers { ready: false, orders: [] }, which
      // produces zero cards — the page then behaves exactly as it did before 門市 existed.
      const counterPayload: any = counterRes.status === 'fulfilled' ? (counterRes.value as any).data || {} : {};
      const counterNorm = buildCounterOrders(counterPayload.orders || [], skuTagMapRef.current);
      // Both of these are states nobody can see from the cards themselves, and both mean
      // stock can go unreceived — so they are said out loud rather than logged.
      if (counterPayload.truncated) {
        enqueueSnackbar('門市訂單太多，這一頁沒有全部載入 — 最舊的幾張沒顯示，請通知工程', { variant: 'error' });
      }
      if (counterPayload.ready && counterPayload.receipts_ready === false) {
        enqueueSnackbar('門市到貨欄位尚未建立：卡片看得到，但還不能按「收到並入庫」', { variant: 'warning' });
      }

      const normalized = [...shopeeNorm, ...shopifyNorm, ...sfNorm, ...counterNorm].map(o => {
        const key = `${o.platform}:${o.orderId}`;
        const pin = pinMap[key];
        if (pin) o = { ...o, pinned: true, pinNote: pin.note, pinnedBy: pin.pinnedBy, pinnedAt: pin.pinnedAt };
        return o;
      });

      // FIFO sort — earliest first
      normalized.sort((a, b) => a.orderDate.getTime() - b.orderDate.getTime());

      // Render immediately, then enrich in background so a slow enrichment
      // endpoint never blocks the receiving list from showing tracking info.
      setAllOrders(normalized);

      // 現貨 auto-receive: MdoC / Doing What are always in local stock — nothing to physically
      // receive. On load, persist any such item that isn't already received in the DB
      // (receivedAt unset) so it shows checked everywhere and never lingers as "pending" in the
      // Day-7 status notifications. Best-effort; fired once per item per session.
      (async () => {
        const userId = currentUser?.id?.toString();
        const userName = currentUser?.name;
        const posts: Promise<any>[] = [];
        for (const o of normalized) {
          // 門市 has no tracking table and its receipt moves real stock, so it is never
          // auto-received: the trailing `else` below would otherwise post a counter line
          // to /api/storefront/orders-tracking under order id 「77」.
          if (o.platform === 'counter') continue;
          for (const it of o.items) {
            if (!isAutoReceiveItem(it.productName, !!it.originalItem?.is_gift) || it.receivedAt) continue;
            const dedupKey = `${o.platform}:${o.orderId}:${it.id}`;
            if (autoReceivePersistedRef.current.has(dedupKey)) continue;
            autoReceivePersistedRef.current.add(dedupKey);
            const base = {
              received: true,
              missingCount: it.missingCount || 0,
              note: it.note || '',
              tag: it.tag || '',
              userId,
              userName,
              orderTotal: o.totalCount,
            };
            if (o.platform === 'shopee') {
              posts.push(api.post('/api/orders/tracking', {
                orderSn: o.orderId, productName: it.productName,
                itemSpec: it.variantOrSpec || '', sku: it.sku || '', ...base,
              }));
            } else if (o.platform === 'shopify') {
              posts.push(api.post('/api/orders/shopify-tracking', {
                orderId: o.orderId, productName: it.productName,
                variantTitle: it.variantOrSpec || 'Default', ...base,
              }));
            } else {
              posts.push(api.post('/api/storefront/orders-tracking', {
                orderId: o.orderId, productName: it.productName,
                variantTitle: it.variantOrSpec || 'Default', sku: it.sku || '',
                outOfStock: false, referenceImage: '', ...base,
              }));
            }
          }
        }
        if (posts.length) {
          await Promise.allSettled(posts);
          console.log(`[Receiving] 現貨 auto-receive: persisted ${posts.length} MdoC item(s)`);
        }
      })();

      // Background: enrich storefront orders with 補寄 history. Backend keys
      // reships by the ORIGINAL base order id (e.g. "ORD-10077"), which is
      // exactly what `order.orderId` holds — no stripping needed.
      (async () => {
        try {
          const reshipRes = await api.get('/api/storefront/reships').catch(() => null);
          const reshipMap: Record<string, any[]> = reshipRes?.data?.reships || {};
          if (Object.keys(reshipMap).length === 0) return;
          setAllOrders(prev => prev.map(o => {
            if (o.platform !== 'storefront') return o;
            const hit = reshipMap[String(o.orderId)];
            return hit ? { ...o, reships: hit } : o;
          }));
        } catch (e) {
          console.warn('[Receiving] Reship enrichment failed:', e);
        }
      })();

      // Background: enrich exchange orders (new_order_id stored as
      // "{origOrder}-換貨") with return-shipment status. Covers Shopify exchange
      // orders AND storefront "-換貨" replacement orders (written to the
      // storefront `orders` table with the suffixed order_id).
      (async () => {
        try {
          const exchangeCandidateIds = normalized
            .filter(o => o.platform === 'shopify'
              || (o.platform === 'storefront' && /-換貨$/.test(String(o.orderId))))
            .map(o => String(o.orderId));
          if (exchangeCandidateIds.length === 0) return;
          const exchRes = await api.post('/api/exchange-return/admin/by-new-orders', {
            newOrderIds: exchangeCandidateIds,
          }).catch((err) => {
            console.warn('[Receiving] by-new-orders failed:', err?.response?.status, err?.response?.data);
            return null;
          });
          const requestMap: Record<string, any> = exchRes?.data?.requests || {};
          console.log(`[Receiving] Exchange enrichment: ${exchangeCandidateIds.length} candidate orders, ${Object.keys(requestMap).length} matched`);
          if (Object.keys(requestMap).length === 0) return;
          setAllOrders(prev => prev.map(o => {
            // The backend map is keyed by BOTH the suffixed and bare order id;
            // on storefront only the "-換貨" replacement order may take the
            // banner — never the original order.
            if (o.platform === 'storefront' && !/-換貨$/.test(String(o.orderId))) return o;
            const key = String(o.orderId).replace(/^#/, '');
            const hit = requestMap[key];
            return hit ? { ...o, exchangeRequest: hit } : o;
          }));
        } catch (e) {
          console.warn('[Receiving] Exchange request enrichment failed:', e);
        }
      })();
    } catch (err: any) {
      console.error('Failed to fetch orders:', err);
      enqueueSnackbar('載入訂單失敗', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [shopeeAuthenticated, enqueueSnackbar]);

  // ── Lazy-fetch shipped storefront orders ──────────────────────────────────
  //
  // The default order fetch hits status=unshipped to keep the receiving page
  // snappy. When Magnus clicks the 已出貨 chip he wants to be able to find
  // already-shipped 官網 orders too (e.g. to reprint a label or check what
  // shipped recently). This lazy-fetches once per session — toggling the chip
  // off/on doesn't refetch.
  const fetchShippedStorefrontOrders = useCallback(async () => {
    if (shippedStorefrontLoaded || shippedStorefrontLoading) return;
    setShippedStorefrontLoading(true);
    try {
      const [shippedRes, trackingRes, metadataRes] = await Promise.allSettled([
        api.get('/api/storefront/orders', { params: { limit: 500, status: 'SHIPPED' } }),
        api.get('/api/storefront/orders-tracking').catch(() => ({ data: [] })),
        api.get('/api/products/metadata').catch(() => ({ data: [] })),
      ]);
      const shippedOrders = shippedRes.status === 'fulfilled' ? (shippedRes.value as any).data?.orders || [] : [];
      const tracking = trackingRes.status === 'fulfilled' ? (trackingRes.value as any).data || [] : [];
      const metadataRaw: any[] = metadataRes.status === 'fulfilled' ? (metadataRes.value as any)?.data || [] : [];
      const metadataMap: Record<string, any> = {};
      const skuTagMap: Record<string, string> = {};
      metadataRaw.forEach((m: any) => {
        const key = m.product_key || m.productKey;
        if (key) metadataMap[key] = m;
        const sku = (key || '').split('|')[0];
        if (sku && m.tag && !skuTagMap[sku]) skuTagMap[sku] = m.tag;
      });

      const normalized = normalizeStorefrontOrders(shippedOrders, tracking, metadataMap, skuTagMap, true);

      setAllOrders(prev => {
        // Merge: keep existing entries, append shipped that aren't already in the list
        const existingIds = new Set(prev.map(o => o.id));
        const additions = normalized.filter(o => !existingIds.has(o.id));
        const merged = [...prev, ...additions];
        merged.sort((a, b) => a.orderDate.getTime() - b.orderDate.getTime());
        return merged;
      });
      setShippedStorefrontLoaded(true);
      enqueueSnackbar(`已載入 ${normalized.length} 筆已出貨官網訂單`, { variant: 'info', autoHideDuration: 3000 });
    } catch (err: any) {
      console.error('[Receiving] shipped storefront fetch failed:', err);
      enqueueSnackbar('載入已出貨官網訂單失敗', { variant: 'error' });
    } finally {
      setShippedStorefrontLoading(false);
    }
  }, [shippedStorefrontLoaded, shippedStorefrontLoading, enqueueSnackbar]);

  useEffect(() => { fetchAllOrders(); }, [fetchAllOrders]);

  // Auto-trigger when user switches to 已出貨 — saves an extra click.
  useEffect(() => {
    if (statusFilter === 'shipped' && !shippedStorefrontLoaded && !shippedStorefrontLoading) {
      fetchShippedStorefrontOrders();
    }
  }, [statusFilter, shippedStorefrontLoaded, shippedStorefrontLoading, fetchShippedStorefrontOrders]);

  // 「搜尋全部時間」— when the mode is on and a query is typed, hit the storefront orders
  // DB directly (search=q, status=all, no recency limit) so old/shipped 官網 orders surface.
  // Debounced; a targeted indexed search returning ≤200 rows is fast (faster than the bulk
  // shipped load). Results are merged into the filter base in filteredOrders.
  // A 寄件編號 / 追蹤號碼 query fires the same server search WITHOUT the toggle: the
  // parcel whose label is in hand has almost always shipped already, so it sits
  // outside the page's live (unshipped) window and a local-only search finds nothing.
  useEffect(() => {
    const q = searchQuery.trim();
    // 收貨 mode only ever credits unshipped lines, so a code lookup there would be a
    // wasted round trip (a scanned tag can look like a code).
    const serverSearch = searchAllMode || (viewMode !== 'receive' && looksLikeShipmentCode(q));
    if (!serverSearch) { setSearchAllOrders([]); setSearchAllLoading(false); return; }
    if (q.length < 2) { setSearchAllOrders([]); setSearchAllLoading(false); return; }
    let cancelled = false;
    setSearchAllLoading(true);
    const timer = setTimeout(async () => {
      try {
        const [ordersRes, trackingRes, metadataRes] = await Promise.allSettled([
          api.get('/api/storefront/orders', { params: { search: q, status: 'all', limit: 200 } }),
          api.get('/api/storefront/orders-tracking').catch(() => ({ data: [] })),
          api.get('/api/products/metadata').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const orders = ordersRes.status === 'fulfilled' ? (ordersRes.value as any).data?.orders || [] : [];
        const tracking = trackingRes.status === 'fulfilled' ? (trackingRes.value as any).data || [] : [];
        const metadataRaw: any[] = metadataRes.status === 'fulfilled' ? (metadataRes.value as any)?.data || [] : [];
        const metadataMap: Record<string, any> = {};
        const skuTagMap: Record<string, string> = {};
        metadataRaw.forEach((m: any) => {
          const key = m.product_key || m.productKey;
          if (key) metadataMap[key] = m;
          const sku = (key || '').split('|')[0];
          if (sku && m.tag && !skuTagMap[sku]) skuTagMap[sku] = m.tag;
        });
        const normalized = normalizeStorefrontOrders(orders, tracking, metadataMap, skuTagMap, true, searchAllMode);
        if (!cancelled) setSearchAllOrders(normalized);
      } catch {
        if (!cancelled) setSearchAllOrders([]);
      } finally {
        if (!cancelled) setSearchAllLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchAllMode, searchQuery, viewMode]);

  // ── Auto-focus search ─────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => searchRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, [loading]);

  // ── Socket.io ─────────��───────────────────────��───────────────────────────

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const socket = io(apiUrl, { transports: ['websocket', 'polling'], reconnectionAttempts: 5 });

    socket.on('connect', () => {
      socket.emit('join-shop', 'shopee');
      socket.emit('join-shop', 'shopify');
      socket.emit('join-shop', 'storefront');
    });

    socket.on('tracking-updated', (data: any) => {
      if (data.socketId === socket.id) return; // skip own echo
      // Apply remote change locally — never refetch
      setAllOrders(prev => prev.map(order => {
        if (order.orderId !== (data.orderId || data.order_sn)) return order;
        const updatedItems = order.items.map(item => {
          const matchProduct = item.productName === (data.productName || data.product_name);
          const matchVariant = (item.variantOrSpec || '') === (data.itemSpec || data.variantTitle || '');
          // Disambiguate same name+variant line items by SKU. When the emit carries
          // a sku, require an exact match so only the toggled item flips; legacy
          // emits (no sku) fall back to name+variant matching.
          const dataSku = data.sku;
          const matchSku = (dataSku === undefined || dataSku === null || dataSku === '')
            ? true
            : (item.sku || '') === dataSku;
          if (!matchProduct || !matchVariant || !matchSku) return item;
          return { ...item, ...data.changes };
        });
        const receivedCount = updatedItems.filter(i => i.received).length;
        return {
          ...order, items: updatedItems, receivedCount,
          allReceived: updatedItems.length > 0 && updatedItems.every(i => i.received || i.outOfStock),
          // Un-ticking 缺貨 restores a 斷貨通知 deduction server-side.
          ...(data.orderTotalAmount != null ? { totalAmount: Number(data.orderTotalAmount) } : {}),
        };
      }));
    });

    socket.on('order-shipped', (data: any) => {
      // Update shipped order locally — don't remove, user may need to print label
      const shippedId = data.orderId || data.order_sn;
      if (shippedId) {
        setAllOrders(prev => prev.map(o => {
          if (o.orderId !== shippedId.toString()) return o;
          return {
            ...o,
            status: data.status || 'PROCESSED',
            hasTrackingInfo: !!(data.trackingNumber || o.trackingNumber),
            justShipped: true,
            trackingNumber: data.trackingNumber || o.trackingNumber,
          };
        }));
      }
    });

    // 需注意 pin sync — another station pinned/unpinned/edited-note an order.
    socket.on('order-pin-updated', (data: any) => {
      const { platform, orderId, pinned, note, pinnedAt, pinnedBy } = data || {};
      if (!platform || !orderId) return;
      setAllOrders(prev => prev.map(o => {
        if (o.platform !== platform || String(o.orderId) !== String(orderId)) return o;
        return {
          ...o,
          pinned: !!pinned,
          pinNote: pinned ? (note || '') : '',
          pinnedBy: pinned ? pinnedBy : undefined,
          pinnedAt: pinned ? pinnedAt : undefined,
        };
      }));
    });

    // 貨架格 — a station assigned/released bins; refetch (debounced) so badges sync.
    socket.on('slots-updated', () => { scheduleSlotRefetch(); });

    socketRef.current = socket;
    return () => { socket.disconnect(); };
  }, []);

  // ── Search / Filter ────────────���──────────────────────────────────────────

  // Build per-order 貨架格 info (bin set, stale flag, per-item bin map) keyed by order.id.
  // Stable reference while slotData/allOrders are unchanged, so memoized cards don't churn.
  const slotInfoById = useMemo(() => {
    const map = new Map<string, OrderSlotInfo>();
    if (!slotData?.items?.length) return map;
    const staleDays = slotData.staleDays || 14;
    const staleMs = staleDays * 86400000;
    const now = Date.now();
    const byOrder = new Map<string, { slots: Set<number>; oldest: number | null; items: Map<string, number> }>();
    for (const it of slotData.items) {
      const k = `${it.platform}::${it.orderId}`;
      let e = byOrder.get(k);
      if (!e) { e = { slots: new Set(), oldest: null, items: new Map() }; byOrder.set(k, e); }
      e.slots.add(it.slotNumber);
      const ra = it.receivedAt ? new Date(it.receivedAt).getTime() : null;
      if (ra && (e.oldest == null || ra < e.oldest)) e.oldest = ra;
      e.items.set(`${(it.productName || '').trim()}|||${(it.variantSpec || '').trim()}`, it.slotNumber);
    }
    for (const o of allOrders) {
      const e = byOrder.get(`${o.platform}::${o.orderId}`);
      if (!e) continue;
      map.set(o.id, {
        slots: Array.from(e.slots).sort((a, b) => a - b),
        stale: e.oldest != null && (now - e.oldest) > staleMs,
        staleDays,
        items: e.items,
      });
    }
    return map;
  }, [allOrders, slotData]);

  // Bin-centric grouping for the 依貨架格 view: bin number → the items that belong in it
  // (with their order), so staff can walk the shelf and fill each numbered bin.
  const binGroups = useMemo(() => {
    const map = new Map<number, Array<{ order: UnifiedOrder; item: UnifiedOrderItem }>>();
    for (const o of allOrders) {
      const info = slotInfoById.get(o.id);
      if (!info) continue;
      for (const item of o.items) {
        const key = `${(item.productName || '').trim()}|||${(item.variantOrSpec || '').trim()}`;
        const bin = info.items.get(key)
          ?? (!item.variantOrSpec ? info.items.get(`${(item.productName || '').trim()}|||Default`) : undefined);
        if (bin == null) continue;
        if (!map.has(bin)) map.set(bin, []);
        map.get(bin)!.push({ order: o, item });
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [allOrders, slotInfoById]);

  // Received-but-unbinned: a received, in-stock line of an unshipped MULTI-item order that has
  // no bin. Mostly accessories (bags, belts, jewelry…) — slot.service EXCLUDE_NAME_RE keeps
  // them off the clothing shelf, so they sit elsewhere and get forgotten when the order ships
  // (Magnus 2026-10-20). MdoC / Doing What are local stock (auto-received), never flagged.
  // Single-item orders ship on receipt and 門市 parcels never bin, so both are skipped.
  const unbinnedItems = useMemo(() => {
    const out: Array<{ order: UnifiedOrder; item: UnifiedOrderItem }> = [];
    for (const o of allOrders) {
      if (o.platform === 'counter' || o.hasTrackingInfo || o.totalCount < 2) continue;
      if ((o.status || '').toUpperCase().includes('CANCEL')) continue;
      const binMap = slotInfoById.get(o.id)?.items;
      for (const item of o.items) {
        if (!item.received || item.outOfStock || isAutoReceiveItem(item.productName, !!item.originalItem?.is_gift)) continue;
        const name = (item.productName || '').trim();
        const bin = binMap?.get(`${name}|||${(item.variantOrSpec || '').trim()}`)
          ?? (!item.variantOrSpec ? binMap?.get(`${name}|||Default`) : undefined);
        if (bin == null) out.push({ order: o, item });
      }
    }
    return out;
  }, [allOrders, slotInfoById]);

  // Orphan bins: a binned slot whose order was CANCELLED while the item may still be on the
  // shelf. We flag ONLY with positive proof of cancellation — never mere absence from the
  // fetch, because shipped/old orders also drop out (the page loads status=unshipped) and
  // must NOT be flagged. Two proofs: (a) the order is still on the page with status CANCELLED
  // (the in-page 取消 button, before the next refetch drops it); (b) it vanished AND its id is
  // in the known-cancelled storefront set. Everything else that vanished = shipped/old → not
  // an orphan (it's released by the reclaim pass instead).
  const orphanBins = useMemo(() => {
    type OrphanItem = { platform: string; orderId: string; productName: string; variantSpec: string; imageUrl: string };
    const map = new Map<number, OrphanItem[]>();
    if (!slotData?.items?.length || allOrders.length === 0) return [] as [number, OrphanItem[]][];
    const orderByKey = new Map(allOrders.map(o => [`${o.platform}::${o.orderId}`, o]));
    for (const it of slotData.items) {
      const o = orderByKey.get(`${it.platform}::${it.orderId}`);
      let isOrphan = false;
      if (it.platform === 'shopee') {
        // Shopee status is never persisted (live feed = READY_TO_SHIP/PROCESSED only), so we can't
        // read a CANCELLED flag. Instead: a binned Shopee order that has LEFT the live feed (!o)
        // without a durable ship record (it.shipped) is one that vanished for a non-ship reason
        // (cancelled / refunded / order changed) → its item may still be on the shelf. Shipped
        // orders carry it.shipped=true (recorded at ship time) and fall off naturally.
        isOrphan = !o && it.shipped !== true;
      } else {
        // Storefront / Shopify: it.cancelled is the authoritative server join on orders.status
        // (CANCELLED / PAYMENT_CANCELLED / PAYMENT_FAILED) — reliable whether or not the order is
        // still in the live fetch. The two legacy client proofs stay as a fast-path fallback for
        // the brief window before getSlottedRows refetches.
        isOrphan = it.cancelled === true
          || (o != null && (o.status || '').toUpperCase() === 'CANCELLED')
          || (o == null && it.platform === 'storefront' && cancelledStorefrontIds.has(it.orderId));
      }
      if (!isOrphan) continue;
      if (!map.has(it.slotNumber)) map.set(it.slotNumber, []);
      map.get(it.slotNumber)!.push({ platform: it.platform, orderId: it.orderId, productName: it.productName, variantSpec: it.variantSpec, imageUrl: it.imageUrl || '' });
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [allOrders, slotData, cancelledStorefrontIds]);

  // Returns the visible orders PLUS the per-category item counts used by the chip row.
  // Counts are tallied BEFORE the category filter is applied, so picking a category
  // doesn't make the other chips disappear (same rule as the colour/size chips).
  const orderSearchResult = useMemo((): {
    list: (UnifiedOrder & { matchedItems: UnifiedOrderItem[] })[];
    counts: Map<CategoryKey, number>;
  } => {
    const q = searchQuery.toLowerCase().trim();
    const digitsQ = normalizePhoneDigits(q);
    const codeQ = normalizeShipCode(q);
    // 寄件編號 hit: the order is the one on the label in hand, whatever its status.
    const shipHit = (o: UnifiedOrder) => codeQ.length >= 4 && shipmentCodeMatches(o, codeQ);
    const colorQ = colorFilter.trim();
    const sizeQ = sizeFilter.trim().toUpperCase();
    const counts = new Map<CategoryKey, number>();
    const tally = (items: UnifiedOrderItem[]) => {
      for (const i of items) {
        const c = classifyProductName(i.productName);
        counts.set(c, (counts.get(c) || 0) + 1);
      }
    };
    const categoryMatches = (item: UnifiedOrderItem): boolean =>
      !categoryFilter || classifyProductName(item.productName) === categoryFilter;
    const variantMatches = (item: UnifiedOrderItem): boolean => {
      if (!colorQ && !sizeQ) return true;
      const { color, size } = parseVariant(item.variantOrSpec || '');
      if (colorQ && color !== colorQ) return false;
      if (sizeQ && size !== sizeQ) return false;
      return true;
    };

    // Step 1: Status filter
    // In 「搜尋全部時間」mode, fold the DB-searched storefront orders (old/shipped, beyond
    // the on-page window) into the base, deduped against what's already loaded live.
    let base = allOrders;
    if (searchAllOrders.length) {
      const existing = new Set(allOrders.map(o => o.id));
      base = [...allOrders, ...searchAllOrders.filter(o => !existing.has(o.id))];
    }
    if (sourceFilter !== 'all') base = base.filter(o => o.platform === sourceFilter);
    if (bigOrderFilter) base = base.filter(o => o.receivedCount >= 5);
    if (hkOnlyFilter) base = base.filter(o => isIntlOrder(o));
    if (pinnedOnlyFilter) base = base.filter(o => o.pinned);
    const isCancelledSearchResult = (o: UnifiedOrder) =>
      searchAllMode && q.length >= 2 && o.platform === 'storefront' && (o.status || '').toUpperCase() === 'CANCELLED';
    if (statusFilter === 'unreceived') {
      // Show unreceived + fully-received orders that haven't shipped yet.
      // Once tracking exists (出貨完成 / waybill assigned), drop from 未收到 —
      // EXCEPT orders that still have an item not checked received (a label may
      // be printed before every item is physically confirmed), OR orders just
      // shipped in this session (justShipped), so the user can confirm the
      // tracking number appeared before they vanish. Search still narrows below.
      // A 寄件編號 search is always about one specific parcel — it must not fall
      // through the status filter just because that parcel already shipped.
      base = base.filter(o => isCancelledSearchResult(o) || !o.hasTrackingInfo || o.justShipped || o.items.some(i => !i.received) || shipHit(o));
    } else if (statusFilter === 'ready') {
      base = base.filter(o => isCancelledSearchResult(o) || (isReadyToShipOrder(o) || o.justShipped) || shipHit(o));
    } else if (statusFilter === 'shipped') {
      base = base.filter(o => isCancelledSearchResult(o) || o.hasTrackingInfo);
    }

    // A 門市 parcel's terminal state is 「全部收到並入庫」 — there is no waybill to wait for,
    // so nothing downstream would ever move it off the queue. Once every line is stocked
    // (or the parcel is cancelled) it leaves here, which is also what the server's
    // open_only feed does on the next refresh; this makes it happen on the tick instead
    // of only on reload. Without it the card would sit in 可以出貨 for ever.
    base = base.filter(o => !isCounterComplete(o));

    // Step 1b: Tag filter helper
    const hasNumbersOnly = (tag: string) => {
      if (!tag || !tag.trim()) return false;
      return /\d/.test(tag) && /^[\d\s\-_.,]*$/.test(tag);
    };
    const tagItemFilter = (i: UnifiedOrderItem): boolean => {
      if (tagFilter === 'all') return true;
      if (tagFilter === 'numbers') return hasNumbersOnly(i.tag || '');
      if (tagFilter === 'no-numbers') return !!(i.tag && i.tag.trim() && !hasNumbersOnly(i.tag));
      return !i.tag || !i.tag.trim(); // no-tag
    };
    if (tagFilter !== 'all') {
      base = base.filter(o => o.items.some(tagItemFilter));
    }

    // Step 2: For "unreceived" filter, only show unreceived items
    const filterReceivedItems = statusFilter === 'unreceived';

    // Step 3: Search filter
    if (!q) {
      const mapped = base.map(o => {
        let items = o.items.filter(tagItemFilter).filter(variantMatches);
        // When all received, show all items (order is shippable). Grace-window items
        // stay briefly so their freshly-assigned bin badge is readable.
        if (filterReceivedItems && !o.allReceived) items = items.filter(i => !i.received || graceReceivedIds.has(i.id));
        tally(items);
        return { ...o, matchedItems: items.filter(categoryMatches) };
      }).filter(o => o.matchedItems.length > 0);
      mapped.sort((a, b) => sortOrder === 'oldest'
        ? a.orderDate.getTime() - b.orderDate.getTime()
        : b.orderDate.getTime() - a.orderDate.getTime()
      );
      return { list: mapped, counts };
    }

    const results: (UnifiedOrder & { matchedItems: UnifiedOrderItem[] })[] = [];

    for (const order of base) {
      const orderIdMatch = orderIdentityMatches(order, q, digitsQ) || shipHit(order);

      let matched: UnifiedOrderItem[];
      if (orderIdMatch) {
        matched = order.items;
      } else {
        matched = order.items.filter(item =>
          item.sku.toLowerCase().includes(q) ||
          item.tag.toLowerCase().includes(q) ||
          item.productName.toLowerCase().includes(q) ||
          (item.specOverride || item.variantOrSpec).toLowerCase().includes(q) ||
          item.note.toLowerCase().includes(q)
        );
      }

      // Apply tag filter
      if (tagFilter !== 'all') matched = matched.filter(tagItemFilter);
      // Apply variant (color / size) filter
      if (colorQ || sizeQ) matched = matched.filter(variantMatches);
      // When "unreceived" filter active, hide already-received items — but NOT if all received (shippable)
      // or still inside their bin-badge grace window.
      if (filterReceivedItems && !order.allReceived && !isCancelledSearchResult(order)) matched = matched.filter(i => !i.received || graceReceivedIds.has(i.id));

      // Tally before narrowing to the picked category, then narrow.
      tally(matched);
      matched = matched.filter(categoryMatches);

      // When all items received, expand to show ALL items (so user can ship)
      if (order.allReceived && matched.length === 0) {
        // Search matched this order before but all matched items are now received
        // Still show the order with all items so user can ship
        const anySearchMatch = orderIdMatch || order.items.some(item =>
          item.sku.toLowerCase().includes(q) ||
          item.tag.toLowerCase().includes(q) ||
          item.productName.toLowerCase().includes(q) ||
          (item.specOverride || item.variantOrSpec).toLowerCase().includes(q) ||
          item.note.toLowerCase().includes(q)
        );
        // With a category picked, a fully-received order only re-surfaces if it
        // actually holds something in that category.
        if (anySearchMatch && order.items.some(categoryMatches)) {
          results.push({ ...order, matchedItems: order.items });
          continue;
        }
      }

      if (matched.length > 0) {
        // If all received, show all items for shipping context
        results.push({ ...order, matchedItems: order.allReceived ? order.items : matched });
      }
    }

    // Sort
    results.sort((a, b) => sortOrder === 'oldest'
      ? a.orderDate.getTime() - b.orderDate.getTime()
      : b.orderDate.getTime() - a.orderDate.getTime()
    );

    return { list: results, counts };
  }, [allOrders, searchAllMode, searchAllOrders, searchQuery, statusFilter, sortOrder, tagFilter, sourceFilter, categoryFilter, colorFilter, sizeFilter, bigOrderFilter, hkOnlyFilter, pinnedOnlyFilter, graceReceivedIds]);

  const filteredOrders = orderSearchResult.list;

  // Reset page when search/filter changes
  useEffect(() => { setPage(1); }, [searchQuery, statusFilter, sortOrder, tagFilter, sourceFilter, categoryFilter, colorFilter, sizeFilter, bigOrderFilter, hkOnlyFilter, pinnedOnlyFilter]);

  // Available colors/sizes among matched items (for the chip filter row).
  // Computed from the search-filtered set IGNORING the active color/size filter,
  // so chips don't disappear after you pick one.
  const variantOptions = useMemo(() => {
    const colors = new Map<string, number>();
    const sizes = new Map<string, number>();
    const q = searchQuery.toLowerCase().trim();
    if (!q) return { colors: [] as string[], sizes: [] as string[] };
    filteredOrders.forEach(o => {
      o.items.forEach(it => {
        // Only consider items that would have matched search (matchedItems is post-color/size).
        // Re-derive search match cheaply:
        // Narrow (no phone) on purpose — that is what this chip row has always matched on.
        const orderHit = orderTextMatches(o, q);
        const itemHit = orderHit || it.sku.toLowerCase().includes(q) ||
          it.tag.toLowerCase().includes(q) || it.productName.toLowerCase().includes(q) ||
          (it.specOverride || it.variantOrSpec).toLowerCase().includes(q) || it.note.toLowerCase().includes(q);
        if (!itemHit) return;
        const { color, size } = parseVariant(it.specOverride || it.variantOrSpec || '');
        if (color) colors.set(color, (colors.get(color) || 0) + it.quantity);
        if (size) sizes.set(size, (sizes.get(size) || 0) + it.quantity);
      });
    });
    return {
      colors: Array.from(colors.keys()).sort((a, b) => (colors.get(b)! - colors.get(a)!)),
      sizes: Array.from(sizes.keys()).sort((a, b) => {
        // Sort sizes by canonical order, then by count
        const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', 'F', 'FREE'];
        const ai = order.indexOf(a), bi = order.indexOf(b);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return (sizes.get(b)! - sizes.get(a)!);
      }),
    };
  }, [filteredOrders, searchQuery]);

  // Paginated slice
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * ordersPerPage;
    return filteredOrders.slice(start, start + ordersPerPage);
  }, [filteredOrders, page, ordersPerPage]);

  // Summary stats
  const stats = useMemo(() => {
    const matchedItems = filteredOrders.reduce((acc, o) => acc + o.matchedItems.length, 0);
    const receivedItems = filteredOrders.reduce((acc, o) => acc + o.receivedCount, 0);
    // 可以出貨 counts PARCELS THAT SHIP. A 門市 parcel never does — it is unpacked onto the
    // counter's rail — so counting it here would tell 出貨人員 there is a bag to post.
    // Only 門市 is excluded: the customer arithmetic is left exactly as it was, so a queue
    // with no 門市 orders shows the same number it showed before this feature existed.
    const readyToShip = filteredOrders.filter(o => o.platform !== 'counter' && o.allReceived).length;
    return { orders: filteredOrders.length, matchedItems, receivedItems, readyToShip };
  }, [filteredOrders]);

  // ── 叫貨 mode: group unreceived items by product ────────────────────────

  // Sourcing links: allowlisted users only (see config/permissions.ts).
  const canSeeLinks = canViewProductLinks(currentUser?.id);

  // The grouping itself lives in components/receiving/demandGrouping.ts — pure and
  // unit-tested, because 「don't merge」 (Magnus 2026-09-15) has to be provable: a 門市
  // line and a customer line for the same SKU+colour+size must stay two rows with two
  // totals, and the cart packer must never be handed the merged one.
  const groupedProductsResult = useMemo(() => {
    if (viewMode !== 'products') return { list: [] as DemandGroup[], counts: new Map<CategoryKey, number>() };
    // Pure wall-clock string comparison — no Date objects, no TZ interpretation.
    // Both sides are normalized to `YYYY-MM-DD HH:mm` which sorts chronologically as a string.
    // This filters by exactly what the user sees in `orderDateDisplay`, ignoring browser/DB TZ.
    const afterStr = groupingAfterDate ? groupingAfterDate.replace('T', ' ').slice(0, 16) : '';
    return buildDemandGroups(allOrders, {
      sourceFilter, afterStr, categoryFilter, classify: classifyProductName,
    });
  }, [viewMode, allOrders, sourceFilter, categoryFilter, groupingAfterDate]);

  const groupedProducts = groupedProductsResult.list;

  // ── 收貨 mode: disambiguation grid ────────────────────────────────────────
  // Group unreceived lines across ALL unshipped orders by STYLE (sku||productName),
  // with variants as sub-groups, matching the same fields as the order search
  // (tag / sku / name / variant / note / order id). The human matches the style from
  // the photo, taps the variant button; FIFO (oldest order) picks the credited line.
  // A category alone is enough to render (browse the 上衣 pile, then narrow by
  // keyword) — the search box is no longer the only way in.
  const receiveResult = useMemo((): { groups: ReceiveStyleGroup[]; counts: Map<CategoryKey, number> } => {
    const counts = new Map<CategoryKey, number>();
    if (viewMode !== 'receive') return { groups: [], counts };
    const q = searchQuery.toLowerCase().trim();
    // With neither a keyword nor a category the grid stays empty — but the counts
    // still get tallied below, otherwise the chip row would have nothing to show
    // and there'd be no way to pick a category in the first place.
    const browsing = !q && !categoryFilter;
    const digitsQ = normalizePhoneDigits(q);
    const groups = new Map<string, ReceiveStyleGroup>();
    for (const order of allOrders) {
      // Only unshipped orders can still be credited a receive.
      if (order.hasTrackingInfo) continue;
      // 門市 lines are NOT credited here. This grid's tap writes a platform tracking row
      // and moves no stock; a 門市 line needs the opposite (stock into noke_inventory as
      // 未上架, with an explicit quantity because the arrival may be short). It is ticked
      // on its own card — see handleCounterReceive. Letting it appear here would post a
      // counter parcel to a customer tracking table under order id 「77」.
      if (order.platform === 'counter') continue;
      const orderHit = !q || orderIdentityMatches(order, q, digitsQ);
      for (const item of order.items) {
        if (item.received || item.outOfStock) continue;
        const itemHit = orderHit ||
          item.sku.toLowerCase().includes(q) ||
          item.tag.toLowerCase().includes(q) ||
          item.productName.toLowerCase().includes(q) ||
          (item.specOverride || item.variantOrSpec).toLowerCase().includes(q) ||
          item.note.toLowerCase().includes(q);
        if (!itemHit) continue;
        // Tally the keyword hits per category first, so the chip row shows where
        // the matches actually live; then keep only the picked category.
        const cat = classifyProductName(item.productName);
        counts.set(cat, (counts.get(cat) || 0) + 1);
        if (browsing || (categoryFilter && cat !== categoryFilter)) continue;
        const key = item.sku || item.productName;
        if (!key) continue;
        let g = groups.get(key);
        if (!g) {
          g = { key, sku: item.sku, productName: item.productName, imageUrl: item.imageUrl, tag: item.tag, totalCount: 0, variants: [] };
          groups.set(key, g);
        }
        if (!g.imageUrl && item.imageUrl) g.imageUrl = item.imageUrl;
        if (!g.tag && item.tag) g.tag = item.tag;
        // Normalize variant: "海軍藍,M" and "海軍藍 / M" → "海軍藍 / M" (same as 叫貨 grouping)
        const rawVariant = item.specOverride || item.variantOrSpec || '-'; // Shopee override = the size that arrives from Korea
        const variant = rawVariant.replace(/\s*[,，]\s*/g, ' / ').replace(/\s*\/\s*/g, ' / ').trim() || '-';
        let vg = g.variants.find(v => v.variant === variant);
        if (!vg) {
          const { color, size } = parseVariant(variant);
          vg = { variant, color, size, lineCount: 0, candidates: [] };
          g.variants.push(vg);
        }
        vg.candidates.push({ order, item });
        vg.lineCount += 1;
        g.totalCount += 1;
      }
    }
    const result = Array.from(groups.values());
    result.forEach(g => {
      // FIFO: oldest order first within each variant (this is the credit order).
      g.variants.forEach(v => v.candidates.sort((a, b) => a.order.orderDate.getTime() - b.order.orderDate.getTime()));
      // Buttons: colors in first-encountered (stable) order, size ascending within color,
      // "(n)" paren suffix as tie-breaker (parseVariant strips it from the size token).
      const colorIndex = new Map<string, number>();
      g.variants.forEach(v => { if (!colorIndex.has(v.color)) colorIndex.set(v.color, colorIndex.size); });
      g.variants.sort((a, b) =>
        (colorIndex.get(a.color)! - colorIndex.get(b.color)!) ||
        (receiveSizeRank(a.size || a.variant) - receiveSizeRank(b.size || b.variant)) ||
        (receiveParenSuffix(a.variant) - receiveParenSuffix(b.variant)) ||
        a.variant.localeCompare(b.variant, 'zh-TW'));
    });
    // Cards: highest total need first, then name.
    result.sort((a, b) => b.totalCount - a.totalCount || a.productName.localeCompare(b.productName, 'zh-TW'));
    return { groups: result, counts };
  }, [viewMode, allOrders, searchQuery, categoryFilter]);

  const receiveGroups = receiveResult.groups;

  // Chips for the active view, in CATEGORY_DEFS order, hiding categories with no
  // items in the current data. 其他 only appears when something actually lands there.
  const categoryOptions = useMemo(() => {
    const counts = viewMode === 'receive' ? receiveResult.counts
      : viewMode === 'products' ? groupedProductsResult.counts
      : orderSearchResult.counts;
    const keys: CategoryKey[] = [...CATEGORY_DEFS.map(d => d.key), 'other'];
    return keys
      .map(key => ({ key, label: CATEGORY_LABELS[key], count: counts.get(key) || 0 }))
      .filter(o => o.count > 0 || o.key === categoryFilter);
  }, [viewMode, receiveResult, groupedProductsResult, orderSearchResult, categoryFilter]);

  // What gets rendered in 叫貨 mode — all grouped products.
  const visibleGroups = groupedProducts;

  // Product links from localStorage (populated by Excel upload on Shopify page)
  // Product links — parse from localStorage on demand (not cached in useMemo,
  // because localStorage may be populated after component mounts via Excel upload)
  const productLinksRef = useRef<Map<string, { link?: string; allLinks?: string[] }> | null>(null);
  const [linksVersion, setLinksVersion] = useState(0);

  // Listen for localStorage changes (Excel upload on Settings page)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'productLinksData') {
        productLinksRef.current = null; // Invalidate cache
        setLinksVersion(v => v + 1);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // On mount, sync product links from the Google Sheet (via backend), then
  // refresh the in-memory map. The `storage` event above only fires for other
  // tabs, so we invalidate explicitly here for this tab.
  useEffect(() => {
    let cancelled = false;
    productLinksService.fetchProductLinks()
      .then(() => {
        if (cancelled) return;
        productLinksRef.current = null; // Invalidate cache
        setLinksVersion(v => v + 1);
      })
      .catch(() => { /* handled in service; keep any cached links */ });
    return () => { cancelled = true; };
  }, []);

  const getProductLinksMap = useCallback(() => {
    if (productLinksRef.current) return productLinksRef.current;
    const map = new Map<string, { link?: string; allLinks?: string[] }>();
    try {
      const stored = localStorage.getItem('productLinksData');
      if (!stored) return map;
      const products: any[] = JSON.parse(stored);
      const decode = (s: string) => {
        if (!s) return '';
        const ta = document.createElement('textarea');
        ta.innerHTML = s;
        return ta.value;
      };
      products.forEach(p => {
        if (!p.id) return;
        const allLinks = p.allLinks?.map((l: string) => decode(l)).filter(Boolean) || [];
        const link = decode(p.link || '') || (allLinks.length > 0 ? allLinks[0] : '');
        map.set(String(p.id), { link: link || undefined, allLinks: allLinks.length > 0 ? allLinks : undefined });
      });
      console.log(`[Receiving] Product links loaded: ${map.size} SKUs`);
    } catch (e) { console.error('[Receiving] Failed to parse productLinksData:', e); }
    productLinksRef.current = map;
    return map;
  }, [linksVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProductLink = useCallback((sku: string): { link?: string; allLinks?: string[] } => {
    if (!sku) return {};
    const map = getProductLinksMap();
    const skuStr = String(sku).trim();
    let result = map.get(skuStr);
    if (result?.link || result?.allLinks) return result;
    // Try base SKU (strip variant suffix like "3067-BK-M" → "3067")
    const baseSku = skuStr.split('-')[0].split('_')[0];
    if (baseSku !== skuStr) {
      result = map.get(baseSku);
      if (result?.link || result?.allLinks) return result;
    }
    return {};
  }, [getProductLinksMap]);

  // ── Sinsang 多帳號模式: automatic account partition ──────────────────────
  //
  // Only a 款 with a sourcing link can occupy a Sinsang cart slot, so the
  // partition is computed over those alone — a link-less product must not burn
  // capacity in a cart it will never be added to.
  //
  // ORDER IS LOAD-BEARING: the server packs 帳號1..N in the order this array
  // arrives. visibleGroups is sorted by totalQty desc (see groupedProducts), so
  // 帳號1 gets the highest-quantity 款 and the 1-2 件 tail lands in the last
  // cart. Don't sort this list — the sort at the source is the priority.
  //
  // 門市 demand is packed too — it is the same 款 bought on the same Sinsang page
  // (Magnus 2026-10-21). A SKU with both a customer and a 門市 card takes one slot.
  const cartEligibleSkus = useMemo(() => {
    if (!multiAccountMode || viewMode !== 'products' || !canSeeLinks) return [];
    return packCartEligibleSkus(visibleGroups as DemandGroup[], sku => {
      const info = getProductLink(sku);
      return Boolean(info.allLinks?.length || info.link);
    });
  }, [multiAccountMode, viewMode, canSeeLinks, visibleGroups, getProductLink]);

  // Stable identity for the eligible set, so the assign effect fires on real
  // membership changes rather than on every re-render of the memo.
  const cartEligibleKey = useMemo(
    () => cartEligibleSkus.slice().sort().join(','),
    [cartEligibleSkus]
  );

  // Set by handleToggleMultiAccount when the user CHECKS the box, so the load
  // effect knows this is a brand-new 叫貨 cycle and must start from an empty
  // map. A plain page reload with the mode already on must NOT wipe (the map is
  // the only record of which cart holds which 款 mid-cycle) — hence a ref set by
  // the click, not a branch on `multiAccountMode` itself.
  const freshCycleRef = useRef(false);

  // Every response that carries the map goes through here. The sequence number
  // makes the newest request win: a slow background refresh must never land on
  // top of a fresher assign result and roll the fill counts back.
  const cartMarksSeqRef = useRef(0);
  const multiAccountModeRef = useRef(multiAccountMode);
  multiAccountModeRef.current = multiAccountMode;
  const cartAssigningRef = useRef(false);
  const applyCartMarks = useCallback((data: any) => {
    setCartCapacity(data?.capacity || 99);
    setCartAccountFill(data?.accounts || []);
    setCartAccounts(new Map<string, number>(
      (data?.marks || []).map((m: any) => [String(m.sku), Number(m.account)])
    ));
  }, []);

  // Tabs of this page in the same browser tell each other when the map changed,
  // so a second tab never shows 帳號3 at 51/99 after the first has filled it.
  const cartChannelRef = useRef<BroadcastChannel | null>(null);
  const notifyCartMarksChanged = useCallback(() => {
    try { cartChannelRef.current?.postMessage('changed'); } catch { /* channel closed */ }
  }, []);

  // Re-read the server map (the only source of truth) without assigning.
  const refreshCartMarks = useCallback(() => {
    if (!multiAccountModeRef.current || cartAssigningRef.current) return;
    const seq = ++cartMarksSeqRef.current;
    api.get('/api/receiving/cart-marks')
      .then(res => {
        if (seq !== cartMarksSeqRef.current || !multiAccountModeRef.current) return;
        applyCartMarks(res.data);
      })
      .catch(err => console.error('[Receiving] cart-marks refresh failed:', err));
  }, [applyCartMarks]);

  // Option-label pairings already confirmed in the Sinsang autofill extension,
  // for every 款 on screen — passed along in each opened link's fragment so a
  // known SKU fills without asking. A load failure only means the extension
  // asks again; opening links never waits on it.
  const [sinsangOptionMap, setSinsangOptionMap] = useState<SinsangOptionMap>({});
  const sinsangMapSkus = useMemo(
    () => Array.from(new Set(visibleGroups.map(g => g.sku).filter(Boolean))).sort().join(','),
    [visibleGroups],
  );
  useEffect(() => {
    if (!canSeeLinks || !sinsangMapSkus) return;
    let cancelled = false;
    api.get('/api/receiving/sinsang-option-map', { params: { skus: sinsangMapSkus } })
      .then(res => { if (!cancelled) setSinsangOptionMap(res.data?.map || {}); })
      .catch(err => console.warn('[Receiving] sinsang option map load failed:', err));
    return () => { cancelled = true; };
  }, [canSeeLinks, sinsangMapSkus]);

  // Pairings confirmed on a Sinsang tab come back through this page: the
  // extension's content script shares our window, posts them here, and we save
  // them with THIS tab's login (the extension never holds a token). Each batch
  // is acked by id so the extension can drop it from its retry queue.
  useEffect(() => {
    if (!canSeeLinks) return;
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window || e.data?.type !== 'metanoia:sinsang-option-map:save') return;
      const batches: { id: string; mappings: { sku: string; omsVariant: string; sinsangOption: string }[] }[] =
        Array.isArray(e.data.batches) ? e.data.batches : [];
      batches.forEach(b => {
        if (!b?.id || !Array.isArray(b.mappings) || b.mappings.length === 0) return;
        api.post('/api/receiving/sinsang-option-map', { mappings: b.mappings, userName: currentUser?.name })
          .then(() => {
            window.postMessage({ type: 'metanoia:sinsang-option-map:saved', id: b.id }, window.location.origin);
            setSinsangOptionMap(prev => {
              const next = { ...prev };
              b.mappings.forEach(m => { next[m.sku] = { ...(next[m.sku] || {}), [m.omsVariant]: m.sinsangOption }; });
              return next;
            });
          })
          .catch(err => console.warn('[Receiving] sinsang option map save failed:', err));
      });
    };
    window.addEventListener('message', onMessage);
    // Tell the extension a listener exists, so it flushes anything queued.
    window.postMessage({ type: 'metanoia:sinsang-option-map:ready' }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [canSeeLinks, currentUser?.name]);

  // 自動叫貨: the autofill extension (v0.2+) says hello when installed. Then a
  // press hands it every remaining link; it keeps 15 Sinsang tabs working,
  // closes each once its add-to-cart landed, and leaves tabs that need a
  // pairing ✓ or failed open. Progress comes back as status messages.
  type SinsangBatchStatus = { total: number; done: number; open: number; queued: number; needsYou: number; failed: number; running: boolean;
    blocked?: 'held' | 'chrome_tabs' | null; maxChromeTabs?: number };
  const [sinsangBatchReady, setSinsangBatchReady] = useState(false);
  const [sinsangBatch, setSinsangBatch] = useState<SinsangBatchStatus | null>(null);
  useEffect(() => {
    if (!canSeeLinks) return;
    const onMessage = (e: MessageEvent) => {
      if (e.source !== window) return;
      if (e.data?.type === 'metanoia:sinsang-batch:hello') setSinsangBatchReady(true);
      if (e.data?.type === 'metanoia:sinsang-batch:status') {
        setSinsangBatchReady(true);
        setSinsangBatch(e.data.status || null);
      }
    };
    window.addEventListener('message', onMessage);
    window.postMessage({ type: 'metanoia:sinsang-batch:ping' }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, [canSeeLinks]);
  const startSinsangBatch = useCallback((urls: string[]) => {
    if (urls.length === 0) return;
    window.postMessage({ type: 'metanoia:sinsang-batch:start', urls, window: 15 }, window.location.origin);
  }, []);
  const stopSinsangBatch = useCallback(() => {
    window.postMessage({ type: 'metanoia:sinsang-batch:stop' }, window.location.origin);
  }, []);

  // Load the existing map when the mode turns on; drop it when it turns off.
  // Turning it on from the checkbox wipes the server map FIRST: disabling is
  // supposed to reset everything, but that clear is a network call that can fail
  // (or be undone by another tab still in the mode re-assigning the backlog), so
  // enabling re-does it. Otherwise last cycle's 帳號1 comes back already 99/99
  // full and this cycle's 款 start at 帳號2.
  useEffect(() => {
    if (!multiAccountMode) {
      cartMarksSeqRef.current += 1; // drop any in-flight response
      setCartAccounts(new Map());
      setCartAccountFill([]);
      setCartMapLoaded(false);
      return;
    }
    setCartMapLoaded(false);
    const fresh = freshCycleRef.current;
    freshCycleRef.current = false;
    const seq = ++cartMarksSeqRef.current;
    (fresh ? api.delete('/api/receiving/cart-marks').then(() => notifyCartMarksChanged()).catch(err => {
      console.error('[Receiving] cart-marks reset failed:', err);
    }) : Promise.resolve())
      .then(() => api.get('/api/receiving/cart-marks'))
      .then(res => {
        if (seq !== cartMarksSeqRef.current) return;
        applyCartMarks(res.data);
        setCartMapLoaded(true);
      })
      .catch(err => {
        console.error('[Receiving] cart-marks load failed:', err);
        if (seq === cartMarksSeqRef.current) setCartMapLoaded(true); // don't wedge assignment on a blip
      });
  }, [multiAccountMode, applyCartMarks, notifyCartMarksChanged]);

  // Keep this tab's copy of the map current: another tab's broadcast, coming
  // back to the tab, and a slow poll while visible (for other devices).
  useEffect(() => {
    if (!multiAccountMode) return;
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CART_MARKS_CHANNEL);
      channel.onmessage = () => refreshCartMarks();
      cartChannelRef.current = channel;
    }
    const onVisible = () => { if (document.visibilityState !== 'hidden') refreshCartMarks(); };
    const timer = window.setInterval(onVisible, CART_MARKS_POLL_MS);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      if (channel) channel.close();
      if (cartChannelRef.current === channel) cartChannelRef.current = null;
    };
  }, [multiAccountMode, refreshCartMarks]);

  // Auto-assign. Every pending 款 without an account is placed server-side into
  // the lowest-numbered cart with room, opening a new one when all are full —
  // so toggling on partitions the whole backlog at once, and tomorrow's new
  // arrivals slot into the first cart that still has space. The ref guards
  // against re-firing on a set the server has already answered for (otherwise a
  // SKU the server declines to place would spin this effect forever).
  //
  // SETTLE FIRST: the eligible set has to hold still for ASSIGN_SETTLE_MS before
  // anything is sent. A slot, once claimed, stays claimed for the whole cycle,
  // so a list that exists for a moment (a date mid-edit, a filter being flipped)
  // must never reach the server.
  const assignAttemptRef = useRef('');
  useEffect(() => {
    if (!multiAccountMode || cartAssigning || !cartMapLoaded || !cycleStateSynced) return;
    if (cartEligibleSkus.length === 0) return;
    if (cartEligibleSkus.every(s => cartAccounts.has(s))) return;
    if (assignAttemptRef.current === cartEligibleKey) return;
    const timer = window.setTimeout(() => {
      assignAttemptRef.current = cartEligibleKey;
      cartAssigningRef.current = true;
      setCartAssigning(true);
      const seq = ++cartMarksSeqRef.current;
      api.post('/api/receiving/cart-marks/assign', {
        skus: cartEligibleSkus,
        userName: currentUser?.name,
      })
        .then(res => {
          if (seq === cartMarksSeqRef.current && multiAccountModeRef.current) applyCartMarks(res.data);
          const added = res.data?.assignedCount || 0;
          if (added > 0) {
            notifyCartMarksChanged();
            enqueueSnackbar(`已分配 ${added} 個新款式到購物車帳號`, { variant: 'success' });
          }
        })
        .catch(err => {
          console.error('[Receiving] cart-marks assign failed:', err);
          enqueueSnackbar('分配購物車帳號失敗', { variant: 'error' });
        })
        .finally(() => {
          cartAssigningRef.current = false;
          setCartAssigning(false);
        });
    }, ASSIGN_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [multiAccountMode, cartMapLoaded, cycleStateSynced, cartEligibleKey, cartEligibleSkus, cartAccounts, cartAssigning, currentUser, enqueueSnackbar, applyCartMarks, notifyCartMarksChanged]);

  // Per-帳號 UI state that belongs to one cycle only.
  const resetCycleUi = useCallback(() => {
    assignAttemptRef.current = '';
    setAccountBatchIndex({});
    setAccountBatchSize({});
    try { localStorage.removeItem('sinsangBatchSize'); } catch { /* private mode */ }
    setExpandedAccounts(new Set());
    setCartAccounts(new Map());
    setCartAccountFill([]);
  }, []);

  // Either direction of the toggle ends a cycle, so BOTH wipe every scrap of
  // per-帳號 state — the map, the fill counts, the batch cursors, and the
  // per-account batch size (which is persisted in localStorage and used to
  // survive a reset, so 帳號1 came back carrying last cycle's settings).
  const handleToggleMultiAccount = useCallback((on: boolean) => {
    try { localStorage.setItem(MULTI_ACCOUNT_MODE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
    pushCycleState({ multiAccountMode: on });
    freshCycleRef.current = on;
    resetCycleUi();
    setMultiAccountMode(on);
  }, [resetCycleUi, pushCycleState]);

  // Follow the mode when another tab or device flips it. Never a fresh cycle
  // here — the place that was clicked does the wipe; this one only mirrors it.
  // Without this, a tab left in the mode kept re-assigning the backlog around
  // the other tab's clear.
  const mirrorMultiAccountMode = useCallback((on: boolean) => {
    if (on === multiAccountModeRef.current) return;
    freshCycleRef.current = false;
    resetCycleUi();
    setMultiAccountMode(on);
  }, [resetCycleUi]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== MULTI_ACCOUNT_MODE_KEY || e.newValue === null) return;
      mirrorMultiAccountMode(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [mirrorMultiAccountMode]);

  // Other devices: read the server's cycle on load, on focus, and on a slow poll.
  // Auto-assign waits for the first read (cycleStateSynced) — this browser's
  // cached cutoff may be stale, and a slot claimed against the wrong list stays
  // claimed for the whole cycle.
  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      // Local changes not yet confirmed by the server win: retry them, don't read.
      if (cyclePutInFlightRef.current > 0 || Object.keys(cyclePendingRef.current).length > 0) {
        if (cyclePutInFlightRef.current === 0) flushCycleState();
        setCycleStateSynced(true);
        return;
      }
      const seqAtStart = cycleWriteSeqRef.current;
      api.get('/api/receiving/cycle-state')
        .then(res => {
          if (cancelled || seqAtStart !== cycleWriteSeqRef.current) return;
          const { multiAccountMode: serverMode, groupingAfterDate: serverDate } = res.data || {};
          if (typeof serverDate === 'string') {
            commitGroupingAfterDate(serverDate, 'server');
          } else {
            // Never set on the server yet: seed it from this browser's explicit
            // choice — but only one inside the current cycle. A cutoff typed
            // weeks ago (or '' = 清除) would widen every device's list and let
            // auto-assign claim slots for 款 outside the cycle.
            let stored: string | null = null;
            try { stored = localStorage.getItem(GROUPING_AFTER_DATE_KEY); } catch { /* blocked */ }
            if (stored && stored >= defaultCutoffValue()) pushCycleState({ groupingAfterDate: stored });
          }
          if (typeof serverMode === 'boolean') {
            try { localStorage.setItem(MULTI_ACCOUNT_MODE_KEY, serverMode ? '1' : '0'); } catch { /* private mode */ }
            mirrorMultiAccountMode(serverMode);
          } else {
            let stored: string | null = null;
            try { stored = localStorage.getItem(MULTI_ACCOUNT_MODE_KEY); } catch { /* blocked */ }
            if (stored !== null) pushCycleState({ multiAccountMode: stored === '1' });
          }
        })
        .catch(err => console.error('[Receiving] cycle-state load failed:', err))
        .finally(() => { if (!cancelled) setCycleStateSynced(true); });
    };
    sync();
    const onVisible = () => { if (document.visibilityState !== 'hidden') sync(); };
    const timer = window.setInterval(onVisible, CART_MARKS_POLL_MS);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [commitGroupingAfterDate, mirrorMultiAccountMode, pushCycleState, flushCycleState, defaultCutoffValue]);

  // Toggling off clears the map, which is the one action that can drop you back
  // into "which cart holds this 款?" — hence the confirm.
  //
  // ORDER MATTERS: flip the mode off BEFORE the DELETE. While the mode is still
  // on, a background refresh of the 叫貨 list changes cartEligibleKey and re-arms
  // the auto-assign effect, which would re-populate the map around the very
  // request that is clearing it. If the DELETE does fail, re-enabling wipes
  // again (see freshCycleRef), so the reset is not left half-done.
  const handleClearCartAccounts = useCallback(async () => {
    setCartClearConfirm(false);
    handleToggleMultiAccount(false);
    try {
      await api.delete('/api/receiving/cart-marks');
      notifyCartMarksChanged();
      enqueueSnackbar('已清除購物車帳號分配', { variant: 'success' });
    } catch (err) {
      console.error('[Receiving] cart-marks clear failed:', err);
      enqueueSnackbar('清除失敗，下次開啟多帳號模式時會再清一次', { variant: 'error' });
    }
  }, [handleToggleMultiAccount, enqueueSnackbar, notifyCartMarksChanged]);

  // The 叫貨 list, flattened. Mode off → cards only (unchanged behaviour).
  // Mode on → 帳號1 header, its 款, 帳號2 header, its 款, … then anything that
  // couldn't be placed (no sourcing link) under a trailing 未分配 header.
  const productRows = useMemo((): ProductRow[] => {
    const groups = visibleGroups as ProductGroup[];
    // Without link permission there are no carts to partition into — such a user
    // would otherwise see every product stranded under 未分配.
    if (!multiAccountMode || !canSeeLinks) return groups.map(group => ({ kind: 'card', group }));

    const byAccount = new Map<number, ProductGroup[]>();
    const unassigned: ProductGroup[] = [];
    groups.forEach(group => {
      const account = group.sku ? cartAccounts.get(group.sku) : undefined;
      if (account == null) { unassigned.push(group); return; }
      if (!byAccount.has(account)) byAccount.set(account, []);
      byAccount.get(account)!.push(group);
    });

    // A collapsed account contributes its header only — the cards are dropped
    // before render rather than hidden with CSS, so 400 product cards aren't
    // sitting in the tree costing layout on every keystroke.
    const rows: ProductRow[] = [];
    Array.from(byAccount.keys()).sort((a, b) => a - b).forEach(account => {
      const inAccount = byAccount.get(account)!;
      rows.push({ kind: 'header', account, groups: inAccount });
      if (expandedAccounts.has(String(account))) {
        inAccount.forEach(group => rows.push({ kind: 'card', group }));
      }
    });
    if (unassigned.length > 0) {
      rows.push({ kind: 'header', account: null, groups: unassigned });
      if (expandedAccounts.has('none')) {
        unassigned.forEach(group => rows.push({ kind: 'card', group }));
      }
    }
    return rows;
  }, [multiAccountMode, canSeeLinks, visibleGroups, cartAccounts, expandedAccounts]);

  // 款 still pending per account, for the overview strip. Distinct from the
  // server's fill count, which includes 款 already received but still occupying
  // a cart slot until checkout.
  const pendingByAccount = useMemo(() => {
    const counts = new Map<string, number>();
    if (!multiAccountMode) return counts;
    (visibleGroups as ProductGroup[]).forEach(g => {
      const account = g.sku ? cartAccounts.get(g.sku) : undefined;
      const key = account == null ? 'none' : String(account);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [multiAccountMode, visibleGroups, cartAccounts]);

  const toggleAccountExpanded = useCallback((key: string) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const setBatchSizeFor = useCallback((key: string, value: number) => {
    setAccountBatchSize(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem('sinsangBatchSize', JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // ── Received handler ───────────────────────────────────────────────────

  const handleToggleReceived = useCallback(async (
    order: UnifiedOrder, item: UnifiedOrderItem, received: boolean
  ) => {
    // Keep this row on screen briefly in 未收到 mode so its bin badge is readable
    // (uncheck cancels the grace immediately).
    noteItemReceived(item.id, received);
    // Optimistic update
    setAllOrders(prev => prev.map(o => {
      if (o.id !== order.id) return o;
      const updatedItems = o.items.map(i =>
        i.id === item.id
          ? {
              ...i, received,
              receivedBy: received ? currentUser?.name : undefined,
              receivedAt: received ? new Date().toISOString() : undefined,
            }
          : i
      );
      const receivedCount = updatedItems.filter(i => i.received).length;
      const tracksOos = o.platform === 'storefront' || o.platform === 'shopee';
      return {
        ...o,
        items: updatedItems,
        receivedCount,
        allReceived: updatedItems.length > 0 && (tracksOos
          ? updatedItems.every(i => i.received || i.outOfStock)
          : receivedCount === updatedItems.length),
      };
    }));

    try {
      const userId = currentUser?.id?.toString();
      const userName = currentUser?.name;

      if (order.platform === 'shopee') {
        await api.post('/api/orders/tracking', {
          orderSn: order.orderId,
          productName: item.productName,
          itemSpec: item.variantOrSpec || '',
          sku: item.sku || '',
          received,
          missingCount: item.missingCount,
          note: item.note,
          tag: item.tag,
          userId,
          userName,
          socketId: socketRef.current?.id,
          orderTotal: order.totalCount,
        });
      } else if (order.platform === 'shopify') {
        await api.post('/api/orders/shopify-tracking', {
          orderId: order.orderId,
          productName: item.productName,
          variantTitle: item.variantOrSpec || 'Default',
          received,
          missingCount: item.missingCount,
          note: item.note,
          tag: item.tag,
          userId,
          userName,
          socketId: socketRef.current?.id,
          orderTotal: order.totalCount,
        });
      } else {
        await api.post('/api/storefront/orders-tracking', {
          orderId: order.orderId,
          productName: item.productName,
          variantTitle: item.variantOrSpec || 'Default',
          sku: item.sku || '',
          received,
          outOfStock: item.outOfStock || false,
          missingCount: item.missingCount,
          note: item.note,
          tag: item.tag,
          referenceImage: '',
          userId,
          userName,
          socketId: socketRef.current?.id,
          orderTotal: order.totalCount,
        });
      }

      // Fallback bin refresh: the 放N號 badge normally arrives via the
      // 'slots-updated' socket emit → refetch, but a dropped socket means it
      // never shows until reload (observed 2026-07-24, order 26071823CTJEQQ).
      // Poll after the server's 500ms batch window, and once more in case the
      // first lands before slot assignment finishes under load.
      if (received) {
        setTimeout(() => { fetchSlots(); }, 1500);
        setTimeout(() => { fetchSlots(); }, 4000);
      }

      // Track employee action (feeds EmployeePerformancePage)
      if (received) {
        const trackingKey = `${item.id}_${new Date().toISOString().split('T')[0]}`;
        if (!window.__trackedItems) window.__trackedItems = new Set();
        if (!window.__trackedItems.has(trackingKey)) {
          window.__trackedItems.add(trackingKey);
          import('../services/employeeTrackingService').then(module => {
            module.default.trackItemReceived(order.orderId, item.id, item.quantity || 1, {
              productName: item.productName,
              itemSpec: item.variantOrSpec,
              sku: item.sku,
              platform: order.platform,
              user_id: userId,
              user_name: userName,
            });
          }).catch(console.error);
        }
      }

      // Auto-print item label via local bridge (non-blocking — failures don't
      // affect the receiving flow, just surface a toast)
      if (received && autoPrintLabel) {
        const { color, size } = parseVariant(item.specOverride || item.variantOrSpec || '');
        const copies = Math.max(1, (item.quantity || 1) - (item.missingCount || 0));
        printItemLabels(
          {
            sku: item.sku || '',
            color,
            size,
            product_type: deriveProductType(item.productName),
            order_id: order.orderNumber || order.orderId,
          },
          copies,
        ).catch(err => {
          enqueueSnackbar(`標籤列印失敗：${err.message}`, { variant: 'warning' });
          setPrintBridgeOnline(false);
        });
      }
    } catch (err: any) {
      enqueueSnackbar(`更新失敗: ${err.message}`, { variant: 'error' });
      // Revert grace + optimistic update locally
      noteItemReceived(item.id, !received);
      setAllOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        const revertedItems = o.items.map(i =>
          i.id === item.id ? { ...i, received: !received } : i
        );
        const receivedCount = revertedItems.filter(i => i.received).length;
        const tracksOos = o.platform === 'storefront' || o.platform === 'shopee';
        return {
          ...o, items: revertedItems, receivedCount,
          allReceived: revertedItems.length > 0 && (tracksOos
            ? revertedItems.every(i => i.received || i.outOfStock)
            : receivedCount === revertedItems.length),
        };
      }));
    }
  }, [currentUser, enqueueSnackbar, autoPrintLabel, noteItemReceived, fetchSlots]);

  // ── 門市 receipt ───────────────────────────────────────────────────────────
  //
  // Not handleToggleReceived. That one writes a tracking row and a bin and moves NO
  // stock; this one puts the pieces into the counter's inventory as 未上架 so Adam scans
  // them onto the rail with 掃碼上架. Routing 門市 through the customer path would leave
  // the counter with a parcel it cannot see and a planner that re-orders it.
  //
  // `qty` is explicit and may be short — the arrival is allocated by earliest ordered_at
  // (Magnus 2026-09-18), so an older customer order can legitimately take pieces this
  // line ordered. There is no auto-tick: an under-receive is always a typed number.
  const handleCounterReceive = useCallback(async (
    order: UnifiedOrder, item: UnifiedOrderItem, qty: number, location: 'office' | 'store',
  ) => {
    const line = item.counterLine;
    if (!line) return;
    try {
      const data = await submitCounterReceipt(line.lineId, qty, location);
      const got = Number(data.line.received_qty) || 0;
      const cancelled = Number(data.line.cancelled_qty) || 0;
      const remaining = data.remaining_qty ?? Math.max(0, item.quantity - got - cancelled);
      setAllOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        const items = o.items.map(i => i.id !== item.id ? i : {
          ...i, received: remaining === 0, missingCount: 0,
          receivedBy: currentUser?.name, receivedAt: data.line.received_at || undefined,
          counterLine: { ...line, receivedQty: got, remainingQty: remaining, cancelledQty: cancelled },
        });
        const receivedCount = items.filter(i => i.received).length;
        return { ...o, items, receivedCount, allReceived: items.every(i => i.received),
          counterOrder: o.counterOrder && { ...o.counterOrder,
            pieceReceived: items.reduce((n, i) => n + (i.counterLine?.receivedQty || 0), 0),
            pieceOpen: items.reduce((n, i) => n + (i.counterLine?.remainingQty ?? (i.received ? 0 : i.quantity)), 0) },
        };
      }));
      notifyOrderChanged();
      enqueueSnackbar(`收貨已記錄，累計 ${got} 件；${remaining > 0 ? `還有 ${remaining} 件待收。` : '已收齊。'}${data.requested_location === 'office' ? '下一步：送往 NOKE。' : '下一步：門市上架。'}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`收貨尚未確認：${err.response?.data?.error || err.message}`, { variant: 'error' });
      throw err;
    }
  }, [currentUser, enqueueSnackbar]);

  // Refresh only the read-only counter feed. The full-page loader also auto-receives unrelated
  // customer stock, so it must never be used as a workflow poll or transition callback.
  const refreshCounterFeed = useCallback(async () => {
    try {
      const response = await api.get('/api/receiving/counter-orders', { params: { open_only: true, limit: 500 } });
      const data = response.data;
      const rows = Array.isArray(data) ? data : data.orders;
      if (!Array.isArray(rows)) return;
      setAllOrders(prev => [...prev.filter(o => o.platform !== 'counter'), ...buildCounterOrders(rows, skuTagMapRef.current)]);
    } catch { /* Keep the last confirmed feed; the workflow panel reports its own read failures. */ }
  }, []);
  // 移除 one 門市 line: cancel only its still-awaited remainder; received pieces stay in stock.
  // The feed is re-read afterwards rather than patched, so the card shows the server's numbers.
  const handleCounterCancelLine = useCallback(async (_order: UnifiedOrder, item: UnifiedOrderItem) => {
    const line = item.counterLine;
    const remaining = line?.remainingQty ?? 0;
    if (!line || remaining <= 0) return;
    await submitCounterCancelLine(line.lineId, remaining);
    await refreshCounterFeed();
    notifyOrderChanged();
    enqueueSnackbar(`已移除 ${item.productName}（取消待收 ${remaining} 件）`, { variant: 'success' });
  }, [refreshCounterFeed, enqueueSnackbar]);

  // 取消門市訂單. Nothing received yet → the whole-parcel cancel (one ledger entry, 開了單 → 取消).
  // Some pieces already here → that endpoint refuses by design, so each open line's remainder
  // is cancelled instead; the received pieces stay in stock either way.
  const handleCounterCancelOrder = useCallback(async (order: UnifiedOrder) => {
    const counter = order.counterOrder;
    if (!counter) return;
    try {
      if (counter.pieceReceived === 0) {
        await submitCounterCancelOrder(counter.id);
      } else {
        for (const item of order.items) {
          const line = item.counterLine;
          const remaining = line?.remainingQty ?? 0;
          if (line && remaining > 0) await submitCounterCancelLine(line.lineId, remaining);
        }
      }
    } finally {
      // Refresh even on a partial failure so the card shows which lines were really cancelled.
      await refreshCounterFeed();
      notifyOrderChanged();
    }
    enqueueSnackbar(`已取消${order.orderNumber}${counter.pieceReceived ? `；已收到的 ${counter.pieceReceived} 件保留` : ''}`, { variant: 'success' });
  }, [refreshCounterFeed, enqueueSnackbar]);
  useEffect(() => {
    const visibleRefresh = () => { if (document.visibilityState !== 'hidden') refreshCounterFeed(); };
    const timer = window.setInterval(visibleRefresh, 30000);
    window.addEventListener(ORDER_CHANGED, visibleRefresh);
    window.addEventListener('focus', visibleRefresh);
    return () => { window.clearInterval(timer); window.removeEventListener(ORDER_CHANGED, visibleRefresh); window.removeEventListener('focus', visibleRefresh); };
  }, [refreshCounterFeed]);

  // Where each open 門市 line stands in the queue for its colour/size. Nothing is
  // reserved here — 出貨人員 still tick what they physically hold — but the card can say
  // 「還有 3 件更早的訂單在等」 so a short arrival goes to the right place without anyone
  // having to compare dates across four cards.
  //
  // Computed from `allOrders`, which already holds every unshipped 蝦皮 / Shopify / 官網
  // order plus every open 門市 parcel. No query: the database has been timing out, and a
  // server-side version could not have afforded to look at all four sources anyway.
  const counterQueue = useMemo(() => {
    const byVariant = new Map<string, DemandLine[]>();
    const keyOf = (sku: string, variant: string) => `${(sku || '').trim()}§${normalizeVariant(variant)}`;
    for (const o of allOrders) {
      for (const it of o.items) {
        if (it.received || it.outOfStock) continue;
        if (!it.sku) continue;
        const k = keyOf(it.sku, it.specOverride || it.variantOrSpec);
        if (!byVariant.has(k)) byVariant.set(k, []);
        byVariant.get(k)!.push({
          key: it.id,
          source: o.platform === 'counter' ? 'counter' : 'customer',
          orderedAt: (it.counterLine?.orderedAt) || o.orderDate.getTime(),
          qty: it.counterLine?.remainingQty ?? it.quantity,
          label: o.orderNumber,
        });
      }
    }
    // available = 0 ranks the whole queue without pretending to know what arrived.
    const out = new Map<string, { rank: number; aheadPieces: number; aheadLabel: string; queueSize: number }>();
    byVariant.forEach((demands) => {
      const ranked = allocateByOrderTime(demands, 0);
      ranked.forEach((a, i) => {
        const ahead = ranked.slice(0, i);
        out.set(a.key, {
          rank: a.rank,
          aheadPieces: ahead.reduce((n, x) => n + x.qty, 0),
          aheadLabel: ahead.length ? String(ahead[0].label || '') : '',
          queueSize: ranked.length,
        });
      });
    });
    return out;
  }, [allOrders]);

  // ── 收貨 mode handlers ────────────────────────────────────────────────────
  // Ensure the just-credited order has a 貨架格 bin so staff know where to put the
  // garment. Only multi-item, unshipped orders that are NOT completed by this
  // receive qualify (a complete order goes straight to packing).
  // POST /slots/backfill is the same idempotent + sticky endpoint the first-load
  // backfill effect uses; refetch afterwards so the strip shows the assigned bin.
  const ensureBinForReceive = useCallback(async (order: UnifiedOrder, justReceivedItemId: string) => {
    if (order.hasTrackingInfo || order.totalCount < 2) return;
    const tracksOos = order.platform === 'storefront' || order.platform === 'shopee';
    const nowComplete = order.items.every(i =>
      i.id === justReceivedItemId || i.received || (tracksOos && !!i.outOfStock));
    if (nowComplete) return; // 可出貨 — ships immediately, no bin needed
    try {
      await api.post('/api/receiving/slots/backfill', {
        orders: [{ platform: order.platform, orderId: order.orderId, total: order.totalCount }],
      });
      fetchSlots();
    } catch { /* non-fatal — the socket slots-updated refetch will still catch up */ }
  }, [fetchSlots]);

  // Tap a variant button → credit the OLDEST matching unshipped order via the
  // existing receive write path, snapshot the confirmation, clear + refocus search.
  const handleReceiveVariantTap = useCallback(async (group: ReceiveStyleGroup, vg: ReceiveVariantGroup) => {
    const pick = vg.candidates[0]; // FIFO — oldest order first
    if (!pick) return;
    setReceiveConfirm({
      productName: group.productName,
      variant: vg.variant,
      order: pick.order,
      item: pick.item,
      candidates: vg.candidates,
    });
    setReceiveSwitchOpen(false);
    pushSearchValue('');
    requestAnimationFrame(() => searchRef.current?.focus());
    // Await the tracking POST so the server sees the received item before backfill.
    await handleToggleReceived(pick.order, pick.item, true);
    ensureBinForReceive(pick.order, pick.item.id);
  }, [handleToggleReceived, ensureBinForReceive, pushSearchValue]);

  // 復原 — un-receive the line we just credited.
  const handleReceiveUndo = useCallback(() => {
    if (receiveConfirm) handleToggleReceived(receiveConfirm.order, receiveConfirm.item, false);
    setReceiveConfirm(null);
    setReceiveSwitchOpen(false);
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [handleToggleReceived, receiveConfirm]);

  // 換訂單 — move the credit from the auto-picked line to a chosen one.
  const handleReceiveSwitchOrder = useCallback(async (cand: ReceiveCandidate) => {
    if (!receiveConfirm) return;
    const prev = receiveConfirm;
    setReceiveConfirm({ ...prev, order: cand.order, item: cand.item });
    setReceiveSwitchOpen(false);
    requestAnimationFrame(() => searchRef.current?.focus());
    if (prev.item.id !== cand.item.id) {
      await handleToggleReceived(prev.order, prev.item, false);
      await handleToggleReceived(cand.order, cand.item, true);
      ensureBinForReceive(cand.order, cand.item.id);
    }
  }, [handleToggleReceived, ensureBinForReceive, receiveConfirm]);

  // 查看訂單 — jump to the 訂單 view with the completed order preloaded in search.
  const handleReceiveViewOrder = useCallback(() => {
    if (!receiveConfirm) return;
    const orderNo = receiveConfirm.order.orderNumber || receiveConfirm.order.orderId;
    setViewMode('orders');
    setStatusFilter('all'); // guarantee the order isn't hidden by a stale status filter
    pushSearchValue(orderNo);
  }, [receiveConfirm, pushSearchValue]);

  // Tap the card photo → open the product image gallery (verify the garment).
  const handleReceiveOpenGallery = useCallback((group: ReceiveStyleGroup) => {
    // Scan candidate lines for the richest image list (all_images / product_images).
    let images: string[] = [];
    for (const v of group.variants) {
      for (const c of v.candidates) {
        const imgs = extractItemImages(c.item);
        if (imgs.length > images.length) images = imgs;
        if (images.length > 1) break;
      }
      if (images.length > 1) break;
    }
    if (images.length === 0 && group.imageUrl) images = [group.imageUrl];
    if (images.length === 0) return;
    setReceiveGalleryIndex(0);
    setReceiveGallery({ title: `${group.productName}${group.sku ? ` · ${group.sku}` : ''}`, images });
  }, []);

  // Auto-focus the search box when entering 收貨 mode (staff scan straight in).
  useEffect(() => {
    if (viewMode !== 'receive') return;
    const t = setTimeout(() => searchRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [viewMode]);

  // ── Pin (需注意) toggle / note edit ─────────────────────────────────────────
  // pinned=false unpins; pinned=true upserts the pin with the given note:
  // optimistic update + one POST + live socket sync.
  const handleTogglePin = useCallback(async (order: UnifiedOrder, pinned: boolean, note?: string) => {
    const ts = new Date().toISOString();
    const noteVal = (note ?? order.pinNote ?? '').trim();
    setAllOrders(prev => prev.map(o => o.id === order.id
      ? {
          ...o,
          pinned,
          pinNote: pinned ? noteVal : '',
          pinnedBy: pinned ? currentUser?.name : undefined,
          pinnedAt: pinned ? (o.pinnedAt || ts) : undefined,
        }
      : o
    ));
    try {
      await api.post('/api/orders/pins', {
        platform: order.platform,
        orderId: order.orderId,
        pinned,
        note: noteVal,
        userName: currentUser?.name,
      });
    } catch (err: any) {
      enqueueSnackbar(`更新失敗: ${err.message}`, { variant: 'error' });
      // Revert
      setAllOrders(prev => prev.map(o => o.id === order.id
        ? { ...o, pinned: order.pinned, pinNote: order.pinNote, pinnedBy: order.pinnedBy, pinnedAt: order.pinnedAt }
        : o
      ));
    }
  }, [currentUser, enqueueSnackbar]);

  // ── Field update handler (tag, note) ───────────────────────────────────────

  const handleUpdateField = useCallback(async (
    order: UnifiedOrder, item: UnifiedOrderItem, field: string, value: any
  ) => {
    // Optimistic update
    setAllOrders(prev => prev.map(o => {
      if (o.id !== order.id) return o;
      return {
        ...o,
        items: o.items.map(i => i.id === item.id ? { ...i, [field]: value } : i),
      };
    }));

    try {
      const userId = currentUser?.id?.toString();
      const userName = currentUser?.name;

      if (field === 'referenceImage') {
        // Reference image — already saved by the lightbox button, just need optimistic update (done above)
        return;
      } else if (field === 'tag') {
        // Storefront: propagate the tag to sibling SIZE/COLOR variants of the SAME
        // product (Metanoia uses one SKU across a product's variants, so they share
        // the store tag). Scope by SKU — NOT productName — so two different products
        // (different SKU) that happen to share a display name don't bleed tags into
        // each other. Fall back to productName only when an item has no SKU.
        const isSibling = (i: UnifiedOrderItem) =>
          (i.sku && item.sku) ? i.sku === item.sku : i.productName === item.productName;
        // Keep the live index in step so the 門市 feed's next refresh (30s, focus, or a
        // receipt) rebuilds the card with the tag that was just typed, not the snapshot.
        if (item.sku && item.sku.trim()) skuTagMapRef.current[item.sku.trim()] = value;
        // 門市 lines take the same path as 官網: one SKU covers every colour/size.
        if (order.platform === 'storefront' || order.platform === 'counter') {
          setAllOrders(prev => prev.map(o => {
            if (o.id !== order.id) return o;
            return {
              ...o,
              items: o.items.map(i => isSibling(i) ? { ...i, tag: value } : i),
            };
          }));
          const siblings = order.items.filter(isSibling);
          const saves: Promise<any>[] = [];
          const seen = new Set<string>();
          siblings.forEach(sib => {
            const productKey = getProductKey(sib.sku || '', sib.productName, sib.variantOrSpec || '');
            const skuKey = getLegacyProductKey(sib.sku || '');
            if (productKey && !seen.has(productKey)) {
              seen.add(productKey);
              saves.push(api.post('/api/products/metadata', { productKey, tag: value }));
            }
            if (skuKey && !seen.has(skuKey)) {
              seen.add(skuKey);
              saves.push(api.post('/api/products/metadata', { productKey: skuKey, tag: value }));
            }
          });
          await Promise.all(saves);
        } else {
          const productKey = getProductKey(item.sku || '', item.productName, item.variantOrSpec || '');
          const skuKey = getLegacyProductKey(item.sku || '');
          const saves = [api.post('/api/products/metadata', { productKey, tag: value })];
          if (skuKey && skuKey !== productKey) {
            saves.push(api.post('/api/products/metadata', { productKey: skuKey, tag: value }));
          }
          await Promise.all(saves);
        }
      } else {
        // Notes go to tracking
        if (order.platform === 'shopee') {
          await api.post('/api/orders/tracking', {
            orderSn: order.orderId, productName: item.productName,
            itemSpec: item.variantOrSpec || '', sku: item.sku || '',
            received: item.received,
            missingCount: item.missingCount, note: field === 'note' ? value : item.note,
            tag: item.tag, userId, userName, socketId: socketRef.current?.id,
          });
        } else if (order.platform === 'shopify') {
          await api.post('/api/orders/shopify-tracking', {
            orderId: order.orderId, productName: item.productName,
            variantTitle: item.variantOrSpec || 'Default', received: item.received,
            missingCount: item.missingCount, note: field === 'note' ? value : item.note,
            tag: item.tag, userId, userName, socketId: socketRef.current?.id,
          });
        } else {
          await api.post('/api/storefront/orders-tracking', {
            orderId: order.orderId, productName: item.productName,
            variantTitle: item.variantOrSpec || 'Default', sku: item.sku || '',
            received: item.received,
            outOfStock: item.outOfStock || false, missingCount: item.missingCount,
            note: field === 'note' ? value : item.note, tag: item.tag,
            referenceImage: '', userId, userName, socketId: socketRef.current?.id,
          });
        }
      }
    } catch (err: any) {
      enqueueSnackbar(`更新失敗: ${err.message}`, { variant: 'error' });
    }
  }, [currentUser, enqueueSnackbar]);

  // ── Shopee ship + print ───────────────────────────────────────────────────

  const handleShipShopee = useCallback(async (order: UnifiedOrder) => {
    setShippingOrderId(order.id);
    try {
      enqueueSnackbar('正在處理出貨...', { variant: 'info', autoHideDuration: 2000 });
      const userId = currentUser?.id?.toString();
      const userName = currentUser?.name;

      await api.post(`/api/shopee/shipping/ship/${order.orderId}`, { userId, userName });

      let trackingNumber: string | null = null;
      for (let i = 0; i < 3; i++) {
        try {
          const res = await api.get(`/api/shopee/shipping/tracking/${order.orderId}`);
          trackingNumber = res.data.response?.tracking_number || null;
          if (trackingNumber) break;
          await new Promise(r => setTimeout(r, 1000));
        } catch { /* retry */ }
      }

      enqueueSnackbar(
        trackingNumber ? `已出貨！追蹤號碼：${trackingNumber}` : '已出貨！追蹤號碼稍後顯示',
        { variant: 'success', autoHideDuration: 6000 }
      );

      // Mark as shipped locally
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
        ...o,
        status: 'PROCESSED',
        hasTrackingInfo: true,
        justShipped: true,
        trackingNumber: trackingNumber || o.trackingNumber,
      }));

      // Track employee action (feeds EmployeePerformancePage)
      const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
      import('../services/employeeTrackingService').then(module => {
        module.default.trackOrderShipped(order.orderId, itemCount, {
          platform: 'shopee',
          trackingNumber,
          user_id: userId,
          user_name: userName,
        });
      }).catch(console.error);

      // Auto-print shipping document
      try {
        await handlePrintShopee(order);
      } catch { /* print failed, user can retry manually */ }
    } catch (err: any) {
      enqueueSnackbar(`出貨失敗：${err.response?.data?.message || err.message}`, { variant: 'error' });
    } finally {
      setShippingOrderId(null);
    }
  }, [currentUser, enqueueSnackbar]);

  const handlePrintShopee = useCallback(async (order: UnifiedOrder) => {
    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info', autoHideDuration: 2000 });

      let trackingNumber: string | null = null;
      for (let i = 0; i < 3; i++) {
        try {
          const res = await api.get(`/api/shopee/shipping/tracking/${order.orderId}`);
          trackingNumber = res.data.response?.tracking_number || null;
          if (trackingNumber) break;
          await new Promise(r => setTimeout(r, 2000));
        } catch { /* retry */ }
      }
      if (!trackingNumber) throw new Error('追蹤號碼尚未準備好');

      await api.post(`/api/shopee/shipping/document/create/${order.orderId}`);
      await new Promise(r => setTimeout(r, 2000));

      const dlRes = await api.post(`/api/shopee/shipping/document/download/${order.orderId}`);
      if (!dlRes?.data?.response) throw new Error('無效的單據資料');

      const { type, content } = dlRes.data.response;
      if (type === 'pdf') {
        const bytes = atob(content);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else if (type === 'html') {
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
      enqueueSnackbar('出貨單已開啟', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.message || '列印出貨單失敗', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Hsinchu label helpers ──────────────────────────────────────────────────

  const hexToBase64 = (hexString: string): string => {
    let binaryString = '';
    for (let i = 0; i < hexString.length; i += 2) {
      binaryString += String.fromCharCode(parseInt(hexString.substr(i, 2), 16));
    }
    return btoa(binaryString);
  };

  const displayShippingLabel = useCallback((hexImage: string, labelData: { trackingNumber?: string; recipientName?: string; recipientAddress?: string; weight?: number } = {}) => {
    if (!hexImage) return;
    const base64Image = hexToBase64(hexImage);
    const newWindow = window.open('', '_blank');
    if (!newWindow) { alert('Please allow popups to view the shipping label'); return; }

    newWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>宅配出貨單 - ${labelData.trackingNumber || '追蹤號碼'}</title>
      <style>
        body { margin:0; padding:20px; font-family:Arial,sans-serif; background-color:#f5f5f5; display:flex; flex-direction:column; align-items:center; }
        .header { text-align:center; margin-bottom:20px; background:white; padding:15px; border-radius:8px; box-shadow:0 2px 4px rgba(0,0,0,0.1); width:100%; max-width:800px; }
        .header h1 { color:#2c3e50; margin:0 0 10px 0; }
        .tracking-info { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:10px; margin-top:15px; text-align:left; }
        .info-item { background:#f8f9fa; padding:8px 12px; border-radius:4px; border-left:3px solid #3498db; }
        .info-label { font-weight:bold; color:#2c3e50; font-size:0.9em; }
        .info-value { color:#34495e; margin-top:2px; }
        .label-container { background:white; padding:20px; border-radius:8px; box-shadow:0 2px 4px rgba(0,0,0,0.1); max-width:100%; overflow:auto; }
        .shipping-label { max-width:100%; height:auto; border:1px solid #ddd; border-radius:4px; }
        .actions { margin-top:20px; text-align:center; }
        .btn { background:#3498db; color:white; border:none; padding:10px 20px; border-radius:4px; cursor:pointer; font-size:14px; margin:0 5px; }
        .btn:hover { background:#2980b9; }
        .btn-print { background:#27ae60; } .btn-print:hover { background:#229954; }
        .btn-download { background:#e74c3c; } .btn-download:hover { background:#c0392b; }
        @media print { body{background:white;padding:0;} .header,.actions{display:none;} .label-container{box-shadow:none;padding:0;margin:0;} }
      </style></head><body>
      <div class="header">
        <h1>宅配出貨單</h1><p>新竹物流 Hsinchu Logistics</p>
        ${labelData.trackingNumber ? `<div class="tracking-info">
          <div class="info-item"><div class="info-label">追蹤號碼</div><div class="info-value">${labelData.trackingNumber}</div></div>
          ${labelData.recipientName ? `<div class="info-item"><div class="info-label">收件人</div><div class="info-value">${labelData.recipientName}</div></div>` : ''}
          ${labelData.recipientAddress ? `<div class="info-item"><div class="info-label">收件地址</div><div class="info-value">${labelData.recipientAddress}</div></div>` : ''}
          ${labelData.weight ? `<div class="info-item"><div class="info-label">重量</div><div class="info-value">${labelData.weight} kg</div></div>` : ''}
        </div>` : ''}
      </div>
      <div class="label-container"><img src="data:image/png;base64,${base64Image}" alt="Shipping Label" class="shipping-label"></div>
      <div class="actions">
        <button class="btn btn-print" onclick="window.print()">列印</button>
        <button class="btn btn-download" onclick="var a=document.createElement('a');a.href='data:image/png;base64,${base64Image}';a.download='宅配出貨單_${labelData.trackingNumber || Date.now()}.png';a.click();">下載</button>
        <button class="btn" onclick="window.close()">關閉</button>
      </div></body></html>`);
    newWindow.document.close();
  }, []);

  // ── Direct Hsinchu shipping (no modal) ───────────────────────────────────

  const handleHsinchuShipping = useCallback(async (order: UnifiedOrder) => {
    try {
      enqueueSnackbar('正在建立宅配出貨單...', { variant: 'info' });

      const orig = order.original;
      let addr = order.shippingAddress || '';
      if (!addr && orig.shipping_address) {
        if (typeof orig.shipping_address === 'object') {
          const a = orig.shipping_address;
          addr = [a.address1, a.address2, a.city, a.province, a.zip].filter(Boolean).join(', ');
        } else {
          addr = orig.shipping_address;
        }
      }

      if (!addr) {
        enqueueSnackbar('缺少收件人地址', { variant: 'error' });
        return;
      }

      const cleanAddress = addr.replace(/, Taiwan,?/gi, '').replace(/,\s*$/, '').trim();
      const formatPhone = (p: string) => {
        let c = p.replace(/\D/g, '');
        if (c.startsWith('886')) c = '0' + c.substring(3);
        if (!c.startsWith('0') && c.length === 9) c = '0' + c;
        return c;
      };

      const response = await api.post('/api/hsinchu/create-order', {
        orderId: order.orderNumber,
        recipientName: order.customerName || '顧客',
        recipientPhone: formatPhone(order.customerPhone || ''),
        recipientAddress: cleanAddress,
        weight: 1,
        itemCount: order.items?.length || 1,
        notes: '',
        declaredValue: 0,
        productType: '001',
      });

      if (response.data.success && response.data.data) {
        const result = response.data.data;

        // Update order status to SHIPPED
        if (order.platform === 'storefront') {
          try { await api.patch(`/api/storefront/orders/${order.orderId}/status`, { status: 'SHIPPED', trackingNumber: result.trackingNumber }); } catch {}
        }

        setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
          ...o, status: 'SHIPPED', hasTrackingInfo: true, justShipped: true,
          trackingNumber: result.trackingNumber,
        }));

        // Track employee action (feeds EmployeePerformancePage)
        const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
        import('../services/employeeTrackingService').then(module => {
          module.default.trackOrderShipped(order.orderId, itemCount, {
            platform: order.platform,
            carrier: 'hsinchu',
            trackingNumber: result.trackingNumber,
            user_id: currentUser?.id?.toString(),
            user_name: currentUser?.name,
          });
        }).catch(console.error);

        enqueueSnackbar(`宅配出貨成功！追蹤號碼: ${result.trackingNumber}`, { variant: 'success', autoHideDuration: 6000 });

        // Display label in new tab
        if (result.labelImage) {
          displayShippingLabel(result.labelImage, {
            trackingNumber: result.trackingNumber,
            recipientName: order.customerName,
            recipientAddress: cleanAddress,
            weight: result.weight,
          });
        }

        // Auto-fulfill Shopify order
        if (order.platform === 'shopify' && result.trackingNumber) {
          try {
            enqueueSnackbar('正在標記 Shopify 訂單為已出貨...', { variant: 'info' });
            await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, {
              trackingNumber: result.trackingNumber,
              trackingCompany: 'Hsinchu Logistics',
            });
            setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'fulfilled' } : o));
            enqueueSnackbar('Shopify 訂單已標記為已出貨', { variant: 'success' });
          } catch (err: any) {
            console.error('Auto-fulfill after Hsinchu failed:', err);
            enqueueSnackbar('宅配出貨成功，但 Shopify 出貨標記失敗，請手動操作', { variant: 'warning' });
          }
        }
      } else {
        enqueueSnackbar(`建立宅配失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      console.error('Hsinchu shipping error:', err);
      enqueueSnackbar(`宅配出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [currentUser, enqueueSnackbar, displayShippingLabel]);

  // ── Storefront 宅配 = 順豐 TW domestic (replaced 新竹物流 2026-08) ──────────
  //
  // Creates the SF waybill server-side (persists sf_waybill_no / sf_label_url on
  // the order, so 列印 works after a refresh), then PATCHes status → SHIPPED via
  // the same endpoint the Hsinchu flow used, keeping the shipped email + sockets
  // on one path. SF returns a PDF URL, not a PNG — opened directly, no
  // displayShippingLabel() viewer.

  const handleSfDomesticShipping = useCallback(async (order: UnifiedOrder) => {
    try {
      enqueueSnackbar('正在建立順豐宅配單...', { variant: 'info' });

      const orig = order.original;
      let addr = order.shippingAddress || '';
      if (!addr && orig.shipping_address) {
        if (typeof orig.shipping_address === 'object') {
          const a = orig.shipping_address;
          addr = [a.address1, a.address2, a.city, a.province, a.zip].filter(Boolean).join(', ');
        } else {
          addr = orig.shipping_address;
        }
      }

      if (!addr) {
        enqueueSnackbar('缺少收件人地址', { variant: 'error' });
        return;
      }

      // SF parses 縣市/區 out of the address itself — strip the ", Taiwan" tail
      // and the comma separators the storefront adds, which break the 縣市 match.
      const cleanAddress = addr
        .replace(/, Taiwan,?/gi, '')
        .replace(/,\s*/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const formatPhone = (p: string) => {
        let c = p.replace(/\D/g, '');
        if (c.startsWith('886')) c = '0' + c.substring(3);
        if (!c.startsWith('0') && c.length === 9) c = '0' + c;
        return c;
      };

      const response = await api.post(`/api/storefront/orders/${order.orderId}/sf-domestic/create`, {
        recipientName: order.customerName || '顧客',
        recipientPhone: formatPhone(order.customerPhone || ''),
        recipientAddress: cleanAddress,
        recipientEmail: order.customerEmail || '',
        // No weight — the backend declares SF's requested 5kg for TW parcels.
      });

      const result = response.data;
      if (!result?.success || !result.sfWaybillNo) {
        enqueueSnackbar(`建立順豐單失敗: ${result?.error || '未知錯誤'}`, { variant: 'error' });
        return;
      }

      try {
        await api.patch(`/api/storefront/orders/${order.orderId}/status`, { status: 'SHIPPED' });
      } catch { /* waybill already exists; status can be fixed from the card */ }

      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
        ...o, status: 'SHIPPED', hasTrackingInfo: true, justShipped: true,
        trackingNumber: result.sfWaybillNo,
        original: { ...o.original, sf_waybill_no: result.sfWaybillNo, sf_label_url: result.labelUrl },
      }));

      const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
      import('../services/employeeTrackingService').then(module => {
        module.default.trackOrderShipped(order.orderId, itemCount, {
          platform: order.platform,
          carrier: 'sf_domestic',
          trackingNumber: result.sfWaybillNo,
          user_id: currentUser?.id?.toString(),
          user_name: currentUser?.name,
        });
      }).catch(console.error);

      enqueueSnackbar(`順豐出貨成功！單號: ${result.sfWaybillNo}`, { variant: 'success', autoHideDuration: 6000 });

      // SF has not yet given us a 台灣宅配 product code — INT0014 is an export
      // product, so the label carries a customs invoice and may bill at the
      // international rate. Warn until SF_TW_PRODUCT_CODE is set.
      if (result.usingInternationalProductCode) {
        enqueueSnackbar('⚠️ 仍使用國際產品代碼 INT0014，運費可能以國際出口費率計價', {
          variant: 'warning', autoHideDuration: 10000,
        });
      }

      if (result.labelUrl) {
        const win = window.open(result.labelUrl, '_blank', 'noopener,noreferrer');
        if (!win) enqueueSnackbar('請允許彈出視窗以開啟順豐面單', { variant: 'warning' });
      } else {
        enqueueSnackbar('順豐單已建立，但未回傳面單連結', { variant: 'warning' });
      }
    } catch (err: any) {
      console.error('SF domestic shipping error:', err);
      enqueueSnackbar(`順豐出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [currentUser, enqueueSnackbar]);

  // ── Ship handler (dispatches by platform) ─────────────────────────────────

  const handleShip = useCallback((order: UnifiedOrder) => {
    console.log('[handleShip] called:', order.orderId, order.platform, order.deliveryMethod);
    if (!confirmShipUnreturnedExchange(order)) return;
    if (order.platform === 'shopee') {
      handleShipShopee(order);
      return;
    }

    // International orders ship via their region's carrier, NOT Hsinchu Taiwan.
    // The shared helper covers normalized deliveryMethod, raw shipping_country,
    // and legacy intl_hk rows.
    if (isIntlOrder(order)) {
      // Each intl region routes to its own carrier's dialog: HK/MO → SF (順豐),
      // SG → Parxl. The backend enforces the same split, so opening the wrong
      // one would just 400.
      const region = intlRegionOf(order);
      if (region?.carrier === 'parxl') {
        setParxlModalOrder(order);
        return;
      }
      if (!isSfIntlOrder(order)) {
        enqueueSnackbar(`${region?.nameZh || '此'}訂單不適用順豐出貨`, { variant: 'warning' });
        return;
      }
      setSfModalOrder(order);
      return;
    }

    // Shopify / Storefront — determine CVS vs home delivery
    const orig = order.original;
    const deliveryMethod = order.deliveryMethod;

    if (deliveryMethod === 'home_delivery' || deliveryMethod === 'shipping') {
      // Storefront 宅配 ships via 順豐 TW domestic (replaced 新竹物流 2026-08).
      // Shopify orders stay on Hsinchu — that lane is being decommissioned and
      // has no storefront order row to persist an SF waybill against.
      if (order.platform === 'storefront') {
        handleSfDomesticShipping(order);
      } else {
        handleHsinchuShipping(order);
      }
    } else {
      // ShippingPreparationModal (CVS pickup)
      // 斷貨 (out-of-stock) handling — storefront only. Exclude OOS items from the
      // collected goods amount so the warehouse never charges the customer for an
      // item they aren't getting. Anchored to the reconstructed paidTotal (購物金 already off), so
      // it's correct (idempotent) whether or not 斷貨通知 already reduced
      // total_amount in the DB.
      const priceCtx = buildStorefrontPriceCtx(order);
      const oosItems = order.items.filter(i => i.outOfStock);
      const hasOOS = oosItems.length > 0;
      const oosDeduction = oosItems.reduce((s, i) => s + (priceCtx.itemLineTotals.get(i.id) || 0), 0);
      const inStockGoods = Math.max(0, priceCtx.paidTotal - oosDeduction);
      // The amount fed to ECPay / shown as 代收金額. Falls back to existing behavior
      // for non-OOS orders (and non-storefront platforms, which have no OOS flag).
      const goodsAmount = hasOOS ? inStockGoods : (order.totalAmount || orig.total_amount || orig.total_price);

      // Pass raw item data so modal can read price/discount
      const modalItems = order.items.map(i => {
        const raw = i.originalItem || {};
        return {
          id: raw.id || raw.shopify_line_item_id || i.id,
          productName: i.productName,
          title: i.productName,
          variantTitle: i.variantOrSpec,
          variant_title: i.variantOrSpec,
          quantity: i.quantity,
          price: raw.is_gift ? 0 : (raw.price || raw.discount_price || 0),
          total_discount: raw.is_gift ? 0 : (raw.total_discount || 0),
          is_gift: raw.is_gift || false,
          sku: i.sku,
          image_url: i.imageUrl,
          // 斷貨 flag + this item's true paid share, so the modal can strike it
          // through and show the exact deduction.
          outOfStock: i.outOfStock || false,
          paidLineTotal: priceCtx.itemLineTotals.get(i.id) || 0,
        };
      });
      setShippingModalOrder({
        orderId: order.orderNumber,  // Use orderNumber (includes mdoc- prefix) for ECPay
        orderNumber: order.orderNumber,
        customer: order.customerName,
        customerPhone: order.customerPhone,
        customerEmail: order.customerEmail || '',
        store_id: order.storeId || orig.store_id,
        store_name: order.storeName || orig.store_name,
        store_address: orig.store_address,
        logistics_sub_type: order.logisticsSubType || orig.logistics_sub_type || orig.elo_logistics_sub_type,
        total_amount: goodsAmount,
        total_price: orig.total_price || order.totalAmount,
        discountAmount: orig.discount_amount || orig.total_discounts || 0,
        payment_method: order.paymentMethod || orig.payment_method,
        // 斷貨 summary for the modal's deduction breakdown
        hasOOS,
        oosDeduction,
        oosCount: oosItems.length,
        originalGoods: priceCtx.originalTotal,
        storeCreditUsed: priceCtx.storeCreditUsed,
        oosKeepsTier: priceCtx.keepsTier,
        items: modalItems,
        original: {
          ...orig,
          // Ensure fields exist that ShippingPreparationModal reads.
          // current_total_price drives the Goods Amount field — use the
          // OOS-adjusted amount so it never charges for 斷貨 items.
          current_total_price: String(goodsAmount || 0),
          financial_status: orig.financial_status || (
            order.platform === 'shopify' ? order.status :
            !(order.paymentMethod || '').toLowerCase().includes('cod') ? 'paid' : 'pending'
          ),
          // Shopify-format customer/address for modal compatibility
          customer: orig.customer || { email: order.customerEmail || orig.customer_email || '' },
          shipping_address: orig.shipping_address || {
            first_name: order.customerName || orig.customer_name || '',
            last_name: '',
            phone: order.customerPhone || orig.customer_phone || '',
          },
        },
        platform: order.platform,
      });
    }
  }, [handleShipShopee, handleHsinchuShipping, handleSfDomesticShipping]);

  const handlePrintLabel = useCallback(async (order: UnifiedOrder) => {
    if (order.platform === 'shopee') {
      handlePrintShopee(order);
      return;
    }

    // SG (Parxl): the label is PDF bytes behind adminAuth, not a hosted URL like
    // SF's — fetch it as a blob with the auth header, then open the object URL.
    if (isIntlOrder(order) && intlRegionOf(order)?.carrier === 'parxl') {
      if (!order.original?.parxl_tracking_no) {
        enqueueSnackbar('尚未建立 Parxl 單，請先點「新加坡出貨」', { variant: 'info' });
        setParxlModalOrder(order);
        return;
      }
      try {
        const resp = await api.get(
          `/api/storefront/orders/${encodeURIComponent(order.orderId)}/parxl/label`,
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) enqueueSnackbar('請允許彈出視窗以開啟 Parxl 面單', { variant: 'warning' });
        else enqueueSnackbar('Parxl 面單已開啟', { variant: 'success' });
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err: any) {
        enqueueSnackbar(`載入 Parxl 面單失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      }
      return;
    }
    if (isIntlOrder(order)) {
      const labelUrl = order.original?.sf_label_url;
      if (labelUrl) {
        window.open(labelUrl, '_blank', 'noopener,noreferrer');
        enqueueSnackbar('順豐出貨單已開啟', { variant: 'success' });
      } else {
        enqueueSnackbar('尚未建立順豐單，請先點「建立順豐單」', { variant: 'info' });
        setSfModalOrder(order);
      }
      return;
    }

    const isHomeDelivery = order.deliveryMethod === 'home_delivery' || order.deliveryMethod === 'shipping';

    // Storefront 宅配 = 順豐: the PDF label URL is stored on the order row.
    if (isHomeDelivery && order.platform === 'storefront') {
      const cached = order.original?.sf_label_url;
      if (cached) {
        window.open(cached, '_blank', 'noopener,noreferrer');
        enqueueSnackbar('順豐面單已開啟', { variant: 'success' });
        return;
      }
      try {
        const response = await api.get(`/api/storefront/orders/${order.orderId}/sf/print`);
        if (response.data?.labelUrl) {
          window.open(response.data.labelUrl, '_blank', 'noopener,noreferrer');
          enqueueSnackbar('順豐面單已開啟', { variant: 'success' });
        } else {
          enqueueSnackbar('尚未建立順豐單，請先點「順豐宅配」', { variant: 'warning' });
        }
      } catch (err: any) {
        enqueueSnackbar(`載入順豐面單失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      }
      return;
    }

    if (isHomeDelivery) {
      // Shopify home delivery: fetch saved Hsinchu label from DB (no API re-call)
      try {
        enqueueSnackbar('正在載入宅配出貨單...', { variant: 'info' });
        const response = await api.get(`/api/hsinchu/label-by-order/${order.orderNumber}`);
        if (response.data.success && response.data.data?.labelImage) {
          displayShippingLabel(response.data.data.labelImage, {
            trackingNumber: response.data.data.trackingNumber,
            recipientName: response.data.data.labelData?.recipientName || order.customerName,
            recipientAddress: response.data.data.labelData?.recipientAddress || order.shippingAddress,
            weight: response.data.data.labelData?.weight,
          });
          enqueueSnackbar('出貨單已開啟', { variant: 'success' });
        } else {
          enqueueSnackbar('找不到已儲存的出貨單，請重新建立', { variant: 'warning' });
        }
      } catch (err: any) {
        enqueueSnackbar(`載入出貨單失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      }
      return;
    }

    // CVS: print ECPay label directly
    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info' });
      const logisticsType = order.logisticsSubType;
      const printId = order.orderNumber;
      const platform = order.platform === 'storefront' ? 'storefront' : 'shopify';
      const response = await api.post(`/api/ecpay/logistics/print/${printId}`, {
        platform,
        logistics_sub_type: logisticsType,
      });
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
        enqueueSnackbar('出貨單已開啟', { variant: 'success' });
      }
    } catch (err: any) {
      enqueueSnackbar(`列印失敗: ${err.message}`, { variant: 'error' });
    }
  }, [handlePrintShopee, enqueueSnackbar, displayShippingLabel]);

  // ── Storefront: Quick ship (CVS) ────────────────────────────────────────

  const handleQuickShip = useCallback(async (order: UnifiedOrder) => {
    if (!confirmShipUnreturnedExchange(order)) return;
    const orig = order.original;
    try {
      enqueueSnackbar('正在快速建立物流單...', { variant: 'info' });
      if (!order.storeId) { enqueueSnackbar('訂單缺少超商門市資訊', { variant: 'error' }); return; }

      const logisticsType = order.logisticsSubType ||
        (order.storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(order.storeId) ? 'FAMIC2C' : 'UNIMARTC2C');
      const totalAmount = Math.floor(order.totalAmount || 0) || 100;
      const formatPhone = (p: string) => { let c = p.replace(/\D/g, ''); if (c.startsWith('886')) c = '0' + c.substring(3); if (!c.startsWith('0') && c.length === 9) c = '0' + c; return c; };
      const customerName = (order.customerName || '顧客').substring(0, 10);
      const customerPhone = formatPhone(order.customerPhone || '');
      if (!customerPhone || customerPhone.length !== 10) { enqueueSnackbar('客戶電話號碼無效', { variant: 'error' }); return; }

      const itemsDesc = order.items.slice(0, 3).map(i => i.productName.split(' ')[0]).join(', ') || 'Goods';
      const goodsName = itemsDesc.length > 25 ? itemsDesc.substring(0, 22) + '...' : itemsDesc;
      // Determine if already paid: Shopify uses financial_status ('paid'), Storefront uses paymentMethod.
      // Storefront: only `store_cod` requires collection; manual_resend / credit_card / line_pay are all prepaid.
      const isPrepaid = order.platform === 'shopify'
        ? ['paid', 'partially_refunded', 'refunded'].includes(order.status)
        : !(order.paymentMethod || '').toLowerCase().includes('cod');
      const source = order.platform === 'storefront' ? 'storefront_quick' : 'shopify';

      const response = await api.post('/api/ecpay/logistics/create-for-shopify', {
        orderId: String(order.orderNumber), storeId: order.storeId, customerName, customerPhone,
        customerEmail: order.customerEmail || '', logisticsSubType: logisticsType,
        goodsAmount: totalAmount, storeName: order.storeName || '',
        isCollection: isPrepaid ? 'N' : 'Y', goodsName, source,
      });

      if (response.data?.success) {
        const tn = response.data.trackingNumber || response.data.paymentNo || '';
        if (order.platform === 'storefront') {
          try { await api.patch(`/api/storefront/orders/${order.orderId}/status`, { status: 'SHIPPED', trackingNumber: tn }); } catch {}
        }
        setAllOrders(prev => prev.map(o => o.id === order.id ? {
          ...o, status: 'SHIPPED', hasTrackingInfo: true, justShipped: true,
          trackingNumber: response.data.trackingNumber, paymentNo: response.data.paymentNo,
          cvsValidationNo: response.data.validationNo,
        } : o));
        enqueueSnackbar(`快速出貨成功！追蹤號碼: ${tn}`, { variant: 'success', autoHideDuration: 6000 });

        // Auto-fulfill Shopify order (mark as fulfilled in Shopify)
        if (order.platform === 'shopify') {
          try {
            enqueueSnackbar('正在標記訂單為已出貨...', { variant: 'info' });
            await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, {
              trackingNumber: tn,
              trackingCompany: logisticsType === 'FAMIC2C' ? 'Family Mart' : '7-ELEVEN',
            });
            setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'fulfilled' } : o));
            enqueueSnackbar('Shopify 訂單已標記為已出貨', { variant: 'success' });
          } catch (fulfillErr: any) {
            console.error('Auto-fulfill failed:', fulfillErr);
            enqueueSnackbar('物流單已建立，但 Shopify 出貨標記失敗，請手動操作', { variant: 'warning' });
          }
        }

        // Auto-print shipping label (use orderNumber for mdoc compatibility)
        try {
          const printId = order.orderNumber;
          const platform = order.platform === 'storefront' ? 'storefront' : 'shopify';
          const printRes = await api.post(`/api/ecpay/logistics/print/${printId}`, {
            platform, logistics_sub_type: logisticsType,
          });
          if (printRes?.data) {
            const blob = new Blob([printRes.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 100);
          }
        } catch { enqueueSnackbar('出貨成功但無法自動列印，請手動列印', { variant: 'warning' }); }
      } else {
        enqueueSnackbar(`建立物流單失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(`快速出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Shopify: Fulfill order ─────────────────────────────────────────────

  const handleFulfill = useCallback(async (order: UnifiedOrder) => {
    if (!confirmShipUnreturnedExchange(order)) return;
    try {
      // Exchange orders (start with 9, 8 digits) — hide instead of Shopify fulfill
      const isExchange = /^9\d{7}$/.test(String(order.orderId));
      if (isExchange) {
        enqueueSnackbar('換貨訂單 — 本地標記為已出貨', { variant: 'info' });
        const hideRes = await api.put(`/api/shopify/orders/${order.orderId}/hidden`, { hidden: true });
        if (hideRes.data.success) {
          setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, status: 'fulfilled', hasTrackingInfo: true, justShipped: true }));
          enqueueSnackbar('換貨訂單已標記為已出貨', { variant: 'success' });
        }
        return;
      }

      enqueueSnackbar('正在處理出貨...', { variant: 'info', autoHideDuration: 2000 });

      // Determine tracking number and company (same logic as ShopifyOrdersPage)
      let trackingNumber = order.trackingNumber || '';
      let trackingCompany = 'ECPay Logistics';

      if (trackingNumber) {
        trackingCompany = 'ECPay Logistics';
      } else if (order.paymentNo) {
        const logisticsType = order.logisticsSubType ||
          (order.storeId && (order.storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(order.storeId))
            ? 'FAMIC2C' : 'UNIMARTC2C');
        if (logisticsType === 'FAMIC2C') {
          trackingNumber = order.paymentNo;
          trackingCompany = 'Family Mart';
        } else {
          trackingNumber = order.cvsValidationNo
            ? `${order.paymentNo}${order.cvsValidationNo}` : order.paymentNo;
          trackingCompany = '7-ELEVEN';
        }
      }

      // Confirm if no tracking
      if (!trackingNumber) {
        if (!window.confirm('此訂單沒有追蹤號碼，確定要標記已出貨嗎？')) return;
      }

      // Use orderNumber (includes mdoc- prefix) — backend strips it
      await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, { trackingNumber, trackingCompany });
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, status: 'fulfilled', hasTrackingInfo: true, justShipped: true }));
      enqueueSnackbar('已標記為出貨', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Storefront: 斷貨 follow-up (admin) ─────────────────────────────────
  // Opens right after an item is ticked 斷貨: offer to sell out just that colour
  // (the common case — the other colours are still coming) or delist the whole
  // product. Variant options are read live so the checkboxes match the listing.
  const openOutOfStockFollowUp = useCallback(async (item: UnifiedOrderItem) => {
    setDelistItem({ sku: item.sku, productName: item.productName, variantOrSpec: item.variantOrSpec || '' });
    setDelistMode('product');
    setDelistStorefront(true);
    setDelistShopee(true);
    setOosOptions(null);
    setSelectedOptionValues([]);
    setDelistDialogOpen(true);
    setOosOptionsLoading(true);
    try {
      const res = await api.get('/api/storefront/admin/variant-options', { params: { sku: item.sku } });
      const data: VariantOptionsResp = res.data;
      setOosOptions(data);
      const groups = data.storefront?.optionGroups || [];
      // Pre-tick only the FIRST option group (colour) — "象牙白 / M" must not
      // silently sell out size M in every other colour too.
      const spec = (item.variantOrSpec || '').trim();
      const preselected = (groups[0]?.values || [])
        .filter(v => !v.disabled && spec.length > 0 && spec.includes(v.value))
        .map(v => v.value);
      setSelectedOptionValues(preselected);
      // Per-colour only makes sense when the product has more than one value.
      const canSplit = groups.some(g => g.values.length > 1);
      setDelistMode(canSplit ? 'variant' : 'product');
    } catch (err: any) {
      console.warn('[斷貨] variant-options fetch failed:', err.message);
    } finally {
      setOosOptionsLoading(false);
    }
  }, []);

  // ── Storefront: Out-of-stock toggle ────────────────────────────────────

  const handleToggleOutOfStock = useCallback(async (order: UnifiedOrder, item: UnifiedOrderItem, outOfStock: boolean) => {
    // Optimistic update — recalculate allReceived since 斷貨 items bypass 已收到
    setAllOrders(prev => prev.map(o => {
      if (o.id !== order.id) return o;
      const updatedItems = o.items.map(i => i.id === item.id ? { ...i, outOfStock } : i);
      return {
        ...o,
        items: updatedItems,
        allReceived: updatedItems.length > 0 && updatedItems.every(i => i.received || i.outOfStock),
      };
    }));
    try {
      console.log('[outOfStock] saving:', order.orderId, item.productName, item.variantOrSpec, 'outOfStock:', outOfStock);
      if (order.platform === 'shopee') {
        // Internal flag only — Shopee money/cancellation stays in Seller Center.
        await api.post('/api/orders/tracking/line-flags', {
          orderSn: order.orderId, productName: item.productName,
          itemSpec: item.variantOrSpec || '', sku: item.sku || '',
          outOfStock, orderTotal: order.totalCount,
          userName: currentUser?.name, socketId: socketRef.current?.id,
        });
        fetchSlots();
        if (outOfStock && currentUser?.id === 1 && item.sku) {
          openOutOfStockFollowUp(item);
        }
        return;
      }
      const res = await api.post('/api/storefront/orders-tracking', {
        orderId: order.orderId, productName: item.productName,
        variantTitle: item.variantOrSpec || 'Default', sku: item.sku || '',
        received: item.received,
        outOfStock, missingCount: item.missingCount, note: item.note, tag: item.tag,
        referenceImage: '', userId: currentUser?.id?.toString(), userName: currentUser?.name,
        socketId: socketRef.current?.id,
      });
      // Un-ticking 缺貨 after 斷貨通知: the server put the deduction back on the
      // order total — mirror it so 超商出貨 collects the full amount.
      if (res.data?.totalAmount != null) {
        const restoredTotal = Number(res.data.totalAmount);
        setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, totalAmount: restoredTotal } : o));
        enqueueSnackbar(
          `已取消斷貨，訂單金額恢復為 $${restoredTotal}（+${res.data.oosRestored}）— 記得通知客戶商品會出貨`,
          { variant: 'warning', autoHideDuration: 8000 },
        );
      }
      if (outOfStock && currentUser?.id === 1 && item.sku) {
        openOutOfStockFollowUp(item);
      }
    } catch (err: any) {
      enqueueSnackbar(`缺貨更新失敗: ${err.message}`, { variant: 'error' });
      // Revert
      setAllOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        const revertedItems = o.items.map(i => i.id === item.id ? { ...i, outOfStock: !outOfStock } : i);
        return {
          ...o,
          items: revertedItems,
          allReceived: revertedItems.length > 0 && revertedItems.every(i => i.received || i.outOfStock),
        };
      }));
    }
  }, [currentUser, enqueueSnackbar, openOutOfStockFollowUp, fetchSlots]);

  // ── Storefront: Out-of-stock notify ────────────────────────────────────

  const handleOutOfStockNotify = useCallback(async (order: UnifiedOrder) => {
    if (!window.confirm(
      `確定要寄送斷貨通知給 ${order.customerName}？\n\n` +
      `系統會：\n` +
      `  1. 寄 Email 通知客戶哪些商品斷貨\n` +
      `  2. 把訂單金額在資料庫減去斷貨商品的金額\n\n` +
      `⚠ 這不會自動退款，仍須按「退刷」才會把錢退給客戶。`
    )) return;
    try {
      const res = await api.post(`/api/storefront/orders/${order.orderId}/out-of-stock-notify`);
      if (res.data.success) {
        setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, totalAmount: res.data.remainingTotal } : o));
        enqueueSnackbar('斷貨通知已寄出，訂單金額已更新', { variant: 'success' });
      } else {
        enqueueSnackbar(`寄送失敗: ${res.data.error}`, { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(`寄送失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Admin: delist product from storefront / Shopee after 斷貨 ───────────

  const handleDelistSubmit = useCallback(async () => {
    if (!delistItem) return;
    if (!delistStorefront && !delistShopee) {
      setDelistDialogOpen(false);
      setDelistItem(null);
      return;
    }
    setDelistProcessing(true);
    try {
      const res = await api.post('/api/storefront/admin/delist-product', {
        sku: delistItem.sku,
        targets: { storefront: delistStorefront, shopee: delistShopee },
      });
      const r = res.data?.result || {};
      const parts: string[] = [];
      if (delistStorefront) {
        parts.push(r.storefront?.ok ? '官網已下架' : `官網失敗: ${r.storefront?.message || '未知'}`);
      }
      if (delistShopee) {
        parts.push(r.shopee?.ok ? '蝦皮已下架' : `蝦皮失敗: ${r.shopee?.message || '未知'}`);
      }
      const hasFailure = (delistStorefront && !r.storefront?.ok) || (delistShopee && !r.shopee?.ok);
      enqueueSnackbar(parts.join('；'), { variant: hasFailure ? 'warning' : 'success' });
      setDelistDialogOpen(false);
      setDelistItem(null);
    } catch (err: any) {
      enqueueSnackbar(`下架失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    } finally {
      setDelistProcessing(false);
    }
  }, [delistItem, delistStorefront, delistShopee, enqueueSnackbar]);

  // ── Admin: sell out ONE colour/size after 斷貨 ─────────────────────────

  const handleDisableVariantSubmit = useCallback(async () => {
    if (!delistItem || selectedOptionValues.length === 0) return;
    if (!delistStorefront && !delistShopee) return;
    setDelistProcessing(true);
    try {
      const res = await api.post('/api/storefront/admin/disable-variant-options', {
        sku: delistItem.sku,
        values: selectedOptionValues,
        disabled: true,
        targets: { storefront: delistStorefront, shopee: delistShopee },
        reason: `斷貨 — ${delistItem.productName}`,
      });
      const r = res.data?.result || {};
      const label = selectedOptionValues.join('、');
      const parts: string[] = [];
      if (delistStorefront) {
        parts.push(r.storefront?.ok
          ? `官網已停售 ${label}（${r.storefront.variantsUpdated} 個規格）`
          : `官網失敗: ${r.storefront?.message || '未知'}`);
      }
      if (delistShopee) {
        parts.push(r.shopee?.ok
          ? `蝦皮已設為售完（${r.shopee.modelsUpdated} 個規格）`
          : `蝦皮失敗: ${r.shopee?.message || '未知'}`);
      }
      const hasFailure = (delistStorefront && !r.storefront?.ok) || (delistShopee && !r.shopee?.ok);
      enqueueSnackbar(parts.join('；'), { variant: hasFailure ? 'warning' : 'success' });
      if (!hasFailure) { setDelistDialogOpen(false); setDelistItem(null); }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      enqueueSnackbar(`停售失敗: ${msg}`, { variant: 'error' });
    } finally {
      setDelistProcessing(false);
    }
  }, [delistItem, selectedOptionValues, delistStorefront, delistShopee, enqueueSnackbar]);

  // ── Storefront: Refund ─────────────────────────────────────────────────

  // Proportional per-item pricing — shared with the 超商出貨 modal.
  // See buildStorefrontPriceCtx at module scope for the full formula.
  const buildRefundCtx = buildStorefrontPriceCtx;

  const handleRefund = useCallback(async (order: UnifiedOrder) => {
    const ctx = buildRefundCtx(order);
    const hasOOS = order.items.some(i => i.outOfStock);
    const oosItems = order.items.filter(i => i.outOfStock);
    const oosSum = oosItems.reduce((s, i) => s + (ctx.itemLineTotals.get(i.id) || 0), 0);

    setRefundOrder(order);
    setRefundHistory([]);
    setRefundHistoryLoading(true);
    setRefundDialogOpen(true);

    let priorRefunded = 0;
    try {
      const res = await api.get(`/api/storefront/orders/${order.orderId}/refunds`);
      const rows: RefundHistoryRow[] = res.data?.refunds || [];
      setRefundHistory(rows);
      priorRefunded = rows.reduce((s, r) => s + (r.amount || 0), 0);
    } catch (err: any) {
      console.warn('[Refund] history fetch failed:', err.message);
    } finally {
      setRefundHistoryLoading(false);
    }

    const remaining = Math.max(0, ctx.paidTotal - priorRefunded);
    if (hasOOS) {
      setRefundMode('partial');
      setRefundSelectedIds(new Set(oosItems.map(i => i.id)));
      setRefundAmount(String(Math.min(oosSum, remaining)));
    } else {
      setRefundMode('full');
      setRefundSelectedIds(new Set());
      setRefundAmount(String(remaining));
    }
  }, []);

  const toggleRefundItem = useCallback((itemId: string) => {
    if (!refundOrder) return;
    const ctx = buildRefundCtx(refundOrder);
    const remaining = Math.max(0, ctx.paidTotal - alreadyRefunded);
    setRefundSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      const sum = Array.from(next).reduce((s, id) => s + (ctx.itemLineTotals.get(id) || 0), 0);
      setRefundAmount(String(Math.min(sum, remaining)));
      return next;
    });
  }, [refundOrder, alreadyRefunded]);

  const handleRefundModeChange = useCallback((mode: 'full' | 'partial') => {
    if (!refundOrder) return;
    const ctx = buildRefundCtx(refundOrder);
    const remaining = Math.max(0, ctx.paidTotal - alreadyRefunded);
    setRefundMode(mode);
    if (mode === 'full') {
      setRefundAmount(String(remaining));
    } else {
      const sum = Array.from(refundSelectedIds).reduce((s, id) => s + (ctx.itemLineTotals.get(id) || 0), 0);
      setRefundAmount(String(Math.min(sum, remaining)));
    }
  }, [refundOrder, refundSelectedIds, alreadyRefunded]);

  const handleRefundSubmit = useCallback(async () => {
    const order = refundOrder;
    if (!order) return;
    const ctx = buildRefundCtx(order);
    const remaining = Math.max(0, ctx.paidTotal - alreadyRefunded);
    const numAmount = parseInt(refundAmount || '0', 10);
    if (!numAmount || numAmount <= 0 || numAmount > remaining) {
      enqueueSnackbar(`金額需介於 $1 - $${remaining}（已退 $${alreadyRefunded}）`, { variant: 'warning' });
      return;
    }
    setRefundProcessing(true);
    try {
      const refundUrl = order.paymentMethod === 'line_pay'
        ? `/api/linepay/refund/${order.orderId}` : `/api/ecpg/payment/refund/${order.orderId}`;
      const res = await api.post(refundUrl, { refundAmount: numAmount });
      if (res.data.success) {
        const newPaymentStatus = res.data.isFullRefund ? 'REFUNDED' : 'PARTIALLY_REFUNDED';
        setAllOrders(prev => prev.map(o => o.id === order.id ? {
          ...o, paymentStatus: newPaymentStatus,
        } : o));
        enqueueSnackbar(`${res.data.isFullRefund ? '全額' : '部分'}退刷成功 - $${numAmount}`, { variant: 'success' });
        setRefundDialogOpen(false);
        setRefundOrder(null);
        setRefundHistory([]);
      } else {
        enqueueSnackbar(`退刷失敗: ${res.data.error || res.data.rtnMsg || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(`退刷失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    } finally {
      setRefundProcessing(false);
    }
  }, [refundOrder, refundAmount, enqueueSnackbar]);

  // ── Storefront: Cancel order ───────────────────────────────────────────

  const handleCancelOrder = useCallback(async (order: UnifiedOrder) => {
    if (!window.confirm(`確定要取消訂單 ${order.orderId}？`)) return;
    try {
      await api.post(`/api/storefront/orders/${order.orderId}/cancel`);
      setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'CANCELLED' } : o));
      enqueueSnackbar(`${order.orderId} 已取消`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`取消失敗: ${err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Storefront: Revert shipped ─────────────────────────────────────────

  const handleRevertShipped = useCallback(async (order: UnifiedOrder) => {
    if (!window.confirm(`確定要將 ${order.orderId} 從「已出貨」退回「未出貨」？`)) return;
    try {
      const targetStatus = order.paymentStatus === 'SUCCESS' ? 'PAID' : 'PENDING';
      await api.patch(`/api/storefront/orders/${order.orderId}/status`, { status: targetStatus });
      setAllOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: targetStatus } : o));
      enqueueSnackbar(`${order.orderId} 已退回未出貨`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`退回失敗: ${err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Storefront: Reship (補寄) ──────────────────────────────────────────

  // Open reship dialog
  const handleReship = useCallback((order: UnifiedOrder) => {
    setReshipOrder(order);
    setReshipPaymentType('N');
    setReshipCodAmount(String(Math.floor(order.totalAmount || 0)));
    setReshipDialogOpen(true);
  }, []);

  // Detect SF-carried intl orders so the reship dialog can branch behavior.
  // Parxl regions (SG) have no /sf/reship lane — they fall through to the plain
  // 補寄 branch instead of promising a 順豐補寄單 the backend would reject.
  const reshipIsHK = !!(reshipOrder && isSfIntlOrder(reshipOrder));
  const reshipRegion = reshipOrder ? intlRegionOf(reshipOrder) : null;
  // 順豐台灣宅配 補寄 — storefront TW home delivery that already carries a waybill.
  // Same reasoning as the HK branch: SF keys parcels on customerOrderNo, so the
  // second box needs METANOIA-TW-{order}-R{n} or it rides the first box's 單號.
  const reshipIsSfTw = !!(
    reshipOrder &&
    reshipOrder.platform === 'storefront' &&
    !isIntlOrder(reshipOrder) &&
    (reshipOrder.deliveryMethod === 'home_delivery' || reshipOrder.deliveryMethod === 'shipping') &&
    reshipOrder.original?.sf_waybill_no
  );
  const reshipSfTwNextNo =
    (reshipOrder?.reships?.filter(r => r.carrier === 'sf_tw' || r.carrier === 'sf').length || 0) + 1;

  // Execute reship
  const handleReshipSubmit = useCallback(async () => {
    const order = reshipOrder;
    if (!order) return;

    // SF-carried intl only — an SG (Parxl) order has no SF waybill to re-issue,
    // and no `sf` address tiers to build a payload from. The narrowing below is
    // what keeps regionFirst/phoneAreaCode statically safe.
    const intlRegion = intlRegionOf(order);
    const region = intlRegion && intlRegion.carrier === 'sf' ? intlRegion : null;

    // ── Intl (HK/MO) / SF International reship ───────────────────────────
    if (region) {
      try {
        enqueueSnackbar('正在建立順豐補寄單...', { variant: 'info' });

        // Reuse the receiver info from the original SF waybill on the order.
        // We only override the customerOrderNo server-side (via -R{n} suffix);
        // address/contact stay the same as the original ship.
        const orig = order.original || {};
        const areaCode = region.phoneAreaCode;
        const stripArea = (p: string) =>
          (p || '').replace(/\D/g, '').replace(new RegExp(`^${areaCode}`), '').slice(-15);
        const response = await api.post(
          `/api/storefront/orders/${encodeURIComponent(order.orderId)}/sf/reship`,
          {
            // Pass receiver so the server can keep using the original intl contact.
            // If left blank the SF service falls back to order row fields.
            receiver: {
              contact: order.customerName || orig.customer_name || '',
              phoneNo: stripArea(order.customerPhone || orig.customer_phone || ''),
              telNo: stripArea(order.customerPhone || orig.customer_phone || ''),
              phoneAreaCode: areaCode,
              telAreaCode: areaCode,
              email: order.customerEmail || orig.customer_email || '',
              address: (typeof orig.shipping_address === 'string' ? orig.shipping_address : order.shippingAddress) || region.regionFirst,
              regionFirst: region.regionFirst,
            },
          }
        );

        if (response.data?.success) {
          const { sfWaybillNo, labelUrl, reshipNo } = response.data;
          enqueueSnackbar(`補寄成功！順豐單號 ${sfWaybillNo}（第 ${reshipNo} 次補寄）`, {
            variant: 'success',
            autoHideDuration: 8000,
          });

          // Auto-open the new shipping label in a new tab — Magnus's main ask.
          if (labelUrl) {
            window.open(labelUrl, '_blank', 'noopener,noreferrer');
          } else {
            enqueueSnackbar('補寄成功但順豐尚未回傳出貨單連結', { variant: 'warning' });
          }

          // Track employee action
          const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
          import('../services/employeeTrackingService').then(module => {
            module.default.trackOrderShipped(`${order.orderId}-R${reshipNo}`, itemCount, {
              platform: order.platform,
              reship: true,
              originalOrderId: order.orderId,
              trackingNumber: sfWaybillNo,
              carrier: 'sf_intl',
              user_id: currentUser?.id?.toString(),
              user_name: currentUser?.name,
            });
          }).catch(console.error);

          // Optimistically append to the order's reship history so the card
          // shows the new entry without a refetch.
          setAllOrders(prev => prev.map(o => {
            if (o.id !== order.id) return o;
            const next = [...(o.reships || []), {
              reshipOrderId: `${order.orderId}-R${reshipNo}`,
              trackingNumber: sfWaybillNo,
              labelUrl,
              createdAt: new Date().toISOString(),
              carrier: 'sf' as const,
            }];
            return { ...o, reships: next };
          }));
        } else {
          enqueueSnackbar(`補寄失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
        }
      } catch (err: any) {
        enqueueSnackbar(`補寄失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      } finally {
        setReshipDialogOpen(false);
        setReshipOrder(null);
      }
      return;
    }

    // ── TW 宅配 / 順豐台灣 domestic reship ────────────────────────────────
    // Storefront home-delivery orders already shipped on 順豐. The backend mints
    // METANOIA-TW-{orderId}-R{n}; without that suffix SF returns the ORIGINAL
    // waybill number and two parcels travel on one 單號.
    if (
      order.platform === 'storefront' &&
      !intlRegion &&
      (order.deliveryMethod === 'home_delivery' || order.deliveryMethod === 'shipping') &&
      order.original?.sf_waybill_no
    ) {
      try {
        enqueueSnackbar('正在建立順豐宅配補寄單...', { variant: 'info' });

        // No payload: the server reads name/phone/address off the order row, so
        // an address corrected via the card's edit (which PATCHes the DB) is
        // already the one SF gets.
        const response = await api.post(
          `/api/storefront/orders/${encodeURIComponent(order.orderId)}/sf-domestic/reship`,
          {}
        );

        if (response.data?.success) {
          const { sfWaybillNo, labelUrl, reshipNo } = response.data;
          enqueueSnackbar(`補寄成功！順豐單號 ${sfWaybillNo}（第 ${reshipNo} 次補寄）`, {
            variant: 'success',
            autoHideDuration: 8000,
          });

          if (labelUrl) {
            const win = window.open(labelUrl, '_blank', 'noopener,noreferrer');
            if (!win) enqueueSnackbar('請允許彈出視窗以開啟順豐面單', { variant: 'warning' });
          } else {
            enqueueSnackbar('補寄成功但順豐尚未回傳出貨單連結', { variant: 'warning' });
          }

          const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
          import('../services/employeeTrackingService').then(module => {
            module.default.trackOrderShipped(`${order.orderId}-R${reshipNo}`, itemCount, {
              platform: order.platform,
              reship: true,
              originalOrderId: order.orderId,
              trackingNumber: sfWaybillNo,
              carrier: 'sf_tw',
              user_id: currentUser?.id?.toString(),
              user_name: currentUser?.name,
            });
          }).catch(console.error);

          // Append to the card's 補寄紀錄 without a refetch. The order row's
          // canonical sf_waybill_no / trackingNumber stay on the FIRST parcel.
          setAllOrders(prev => prev.map(o => {
            if (o.id !== order.id) return o;
            const next = [...(o.reships || []), {
              reshipOrderId: `${order.orderId}-R${reshipNo}`,
              trackingNumber: sfWaybillNo,
              labelUrl,
              createdAt: new Date().toISOString(),
              carrier: 'sf_tw' as const,
            }];
            return { ...o, reships: next };
          }));
        } else {
          enqueueSnackbar(`補寄失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
        }
      } catch (err: any) {
        enqueueSnackbar(`補寄失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      } finally {
        setReshipDialogOpen(false);
        setReshipOrder(null);
      }
      return;
    }

    // ── TW / ECPay CVS reship (existing flow) ────────────────────────────
    try {
      enqueueSnackbar('正在建立補寄物流單...', { variant: 'info' });
      if (!order.storeId) { enqueueSnackbar('訂單缺少超商門市資訊', { variant: 'error' }); return; }

      const logisticsType = order.logisticsSubType ||
        (order.storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(order.storeId) ? 'FAMIC2C' : 'UNIMARTC2C');
      const totalAmount = Math.floor(order.totalAmount || 0) || 100;
      const formatPhone = (p: string) => { let c = p.replace(/\D/g, ''); if (c.startsWith('886')) c = '0' + c.substring(3); if (!c.startsWith('0') && c.length === 9) c = '0' + c; return c; };
      const customerPhone = formatPhone(order.customerPhone || '');
      const itemsDesc = order.items.slice(0, 3).map(i => i.productName.split(' ')[0]).join(', ') || 'Goods';
      const goodsName = itemsDesc.length > 25 ? itemsDesc.substring(0, 22) + '...' : itemsDesc;

      // Generate suffixed orderId
      const parts = order.orderId.split('-');
      const baseId = parts.length >= 3 && /^\d{1,2}$/.test(parts[parts.length - 1])
        ? parts.slice(0, -1).join('-') : order.orderId;
      let suffix = 1;
      const existingIds = new Set(allOrders.map(o => o.orderId));
      while (existingIds.has(`${baseId}-${suffix}`)) suffix++;
      const reshipOrderId = `${baseId}-${suffix}`;

      const response = await api.post('/api/ecpay/logistics/create-for-shopify', {
        orderId: reshipOrderId, storeId: order.storeId,
        customerName: (order.customerName || '顧客').substring(0, 10),
        customerPhone, customerEmail: order.customerEmail || '',
        logisticsSubType: logisticsType,
        goodsAmount: reshipPaymentType === 'Y' ? (parseInt(reshipCodAmount, 10) || totalAmount) : totalAmount,
        storeName: order.storeName || '', isCollection: reshipPaymentType,
        goodsName: `[補寄] ${goodsName}`, source: 'storefront_reship',
      });

      if (response.data?.success) {
        const tn = response.data.trackingNumber || response.data.paymentNo || '';
        enqueueSnackbar(`補寄成功！寄件編號: ${reshipOrderId}，追蹤號碼: ${tn}`, { variant: 'success', autoHideDuration: 8000 });

        // Track employee action (feeds EmployeePerformancePage)
        const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
        import('../services/employeeTrackingService').then(module => {
          module.default.trackOrderShipped(reshipOrderId, itemCount, {
            platform: order.platform,
            reship: true,
            originalOrderId: order.orderId,
            trackingNumber: tn,
            logisticsSubType: logisticsType,
            user_id: currentUser?.id?.toString(),
            user_name: currentUser?.name,
          });
        }).catch(console.error);

        // Auto-print
        try {
          const printRes = await api.post(`/api/ecpay/logistics/print/${reshipOrderId}`, { platform: 'storefront', logistics_sub_type: logisticsType });
          if (printRes?.data) {
            const blob = new Blob([printRes.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 100);
          }
        } catch { enqueueSnackbar('補寄成功但無法自動列印', { variant: 'warning' }); }
      } else {
        enqueueSnackbar(`補寄失敗: ${response.data?.error?.message || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(`補寄失敗: ${err.response?.data?.error?.message || err.message}`, { variant: 'error' });
    } finally {
      setReshipDialogOpen(false);
      setReshipOrder(null);
    }
  }, [reshipOrder, reshipPaymentType, reshipCodAmount, allOrders, currentUser, enqueueSnackbar]);

  // ── Variant update (Shopify / Storefront; Shopee = OMS-only override) ────

  const handleVariantUpdate = useCallback(async (order: UnifiedOrder, item: UnifiedOrderItem, newVariant: string) => {
    const trimmed = newVariant.trim();

    if (order.platform === 'shopee') {
      // Shopee won't let a seller change the buyer's model, so store an override beside the
      // original. Typing the original back (or clearing the field) removes the override.
      const next = (!trimmed || trimmed === '—' || trimmed === item.variantOrSpec) ? null : trimmed;
      if (next === (item.specOverride || null)) return;
      const setOverride = (v: string | null) => setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
        ...o, items: o.items.map(i => i.id === item.id ? { ...i, specOverride: v } : i),
      }));
      setOverride(next);
      try {
        await api.post('/api/orders/tracking/line-flags', {
          orderSn: order.orderId, productName: item.productName,
          itemSpec: item.variantOrSpec || '', sku: item.sku || '',
          specOverride: next ?? '',
          userName: currentUser?.name, socketId: socketRef.current?.id,
        });
        enqueueSnackbar(next ? `規格已改為 ${next}（僅 OMS，蝦皮訂單不變）` : '已還原蝦皮原規格',
          { variant: 'success', autoHideDuration: 2500 });
      } catch (err: any) {
        enqueueSnackbar(`更新規格失敗: ${err.message}`, { variant: 'error' });
        setOverride(item.specOverride || null);
      }
      return;
    }

    if (!trimmed || trimmed === item.variantOrSpec) return;

    // Optimistic update
    setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
      ...o, items: o.items.map(i => i.id === item.id ? { ...i, variantOrSpec: trimmed } : i),
    }));

    try {
      if (order.platform === 'shopify') {
        const lineItemId = item.originalItem?.shopify_line_item_id || item.originalItem?.id;
        if (lineItemId) await api.patch(`/api/shopify/order-items/${lineItemId}/variant`, { variant_title: trimmed });
      } else {
        const itemId = item.originalItem?.id;
        if (itemId) await api.patch(`/api/storefront/order-items/${itemId}/variant`, { variant_title: trimmed });
      }
      enqueueSnackbar(`規格已更新: ${trimmed}`, { variant: 'success', autoHideDuration: 2000 });
    } catch (err: any) {
      enqueueSnackbar(`更新規格失敗: ${err.message}`, { variant: 'error' });
      // Revert
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
        ...o, items: o.items.map(i => i.id === item.id ? { ...i, variantOrSpec: item.variantOrSpec } : i),
      }));
    }
  }, [enqueueSnackbar, currentUser]);

  // ── SKU update (Shopify / Storefront) ────────────────────────────────────

  const handleSkuUpdate = useCallback(async (order: UnifiedOrder, item: UnifiedOrderItem, newSku: string) => {
    const trimmed = newSku.trim();
    // Storefront: propagate SKU to all sibling variants (same productName in this order)
    const propagate = order.platform === 'storefront';
    const targetItems = propagate
      ? order.items.filter(i => i.productName === item.productName)
      : [item];

    setAllOrders(prev => prev.map(o => o.id !== order.id ? o : {
      ...o,
      items: o.items.map(i =>
        targetItems.some(t => t.id === i.id) ? { ...i, sku: trimmed } : i
      ),
    }));
    try {
      if (order.platform === 'shopify') {
        const lineItemId = item.originalItem?.shopify_line_item_id || item.originalItem?.id;
        if (lineItemId) await api.patch(`/api/shopify/order-items/${lineItemId}/sku`, { sku: trimmed });
      } else {
        await Promise.all(targetItems.map(t => {
          const itemId = t.originalItem?.id;
          return itemId ? api.patch(`/api/storefront/order-items/${itemId}/sku`, { sku: trimmed }) : Promise.resolve();
        }));
      }
      const msg = propagate && targetItems.length > 1
        ? `品號已更新 (${targetItems.length} 個變體): ${trimmed || '(空)'}`
        : `品號已更新: ${trimmed || '(空)'}`;
      enqueueSnackbar(msg, { variant: 'success', autoHideDuration: 2000 });
    } catch (err: any) {
      enqueueSnackbar(`更新品號失敗: ${err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // ── Customer name/phone update ──────────────────────────────────────────

  const handleCustomerUpdate = useCallback(async (order: UnifiedOrder, name: string, phone: string) => {
    setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, customerName: name, customerPhone: phone }));
    try {
      if (order.platform === 'shopify') {
        await api.patch(`/api/shopify/orders/${order.orderId}/customer`, { customerName: name, customerPhone: phone });
      } else {
        await api.patch(`/api/storefront/orders/${order.orderId}/customer`, { customerName: name, customerPhone: phone });
      }
      enqueueSnackbar('客戶資料已更新', { variant: 'success', autoHideDuration: 2000 });
    } catch (err: any) {
      enqueueSnackbar(`更新失敗: ${err.message}`, { variant: 'error' });
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, customerName: order.customerName, customerPhone: order.customerPhone }));
    }
  }, [enqueueSnackbar]);

  // ── Address update (Shopify / Storefront 宅配) ────────────────────────

  const handleAddressUpdate = useCallback(async (order: UnifiedOrder, newAddress: string) => {
    if (!newAddress.trim() || newAddress === order.shippingAddress) return;

    setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, shippingAddress: newAddress }));

    try {
      if (order.platform === 'shopify') {
        await api.put(`/api/shopify/orders/${order.orderId}/address`, { shippingAddress: newAddress });
        enqueueSnackbar('地址更新成功', { variant: 'success' });
      } else if (order.platform === 'storefront') {
        const res = await api.patch(
          `/api/storefront/orders/${order.orderId}/address`,
          { shippingAddress: newAddress }
        );
        // The waybill was printed with the OLD address — saving the row does not
        // reprint it. Warn instead of implying the parcel now goes to the new one.
        if (res.data?.labelAlreadyCreated) {
          enqueueSnackbar('地址已更新，但物流單已建立（仍是舊地址），請重新建立或聯絡物流', {
            variant: 'warning', autoHideDuration: 8000,
          });
        } else {
          enqueueSnackbar('地址更新成功', { variant: 'success' });
        }
      } else {
        // No address API for this platform — do not claim a save that never happened.
        throw new Error(`${order.platform} 訂單不支援修改地址`);
      }
    } catch (err: any) {
      enqueueSnackbar(`地址更新失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, shippingAddress: order.shippingAddress }));
    }
  }, [enqueueSnackbar]);

  // ── Storefront: Mark as shipped (已出貨) ────────────────────────────────

  const handleStorefrontShipped = useCallback(async (order: UnifiedOrder) => {
    try {
      await api.patch(`/api/storefront/orders/${order.orderId}/status`, { status: 'SHIPPED' });
      setAllOrders(prev => prev.map(o => o.id !== order.id ? o : { ...o, status: 'SHIPPED' }));
      enqueueSnackbar(`${order.orderId} 已標記為出貨`, { variant: 'success' });

      // Track employee action (feeds EmployeePerformancePage)
      const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 1;
      import('../services/employeeTrackingService').then(module => {
        module.default.trackOrderShipped(order.orderId, itemCount, {
          platform: order.platform,
          manualMark: true,
          user_id: currentUser?.id?.toString(),
          user_name: currentUser?.name,
        });
      }).catch(console.error);
    } catch (err: any) {
      enqueueSnackbar(`出貨失敗: ${err.message}`, { variant: 'error' });
    }
  }, [currentUser, enqueueSnackbar]);

  // ── Modal callbacks ────────────────────────────────────────────────────

  const handleShippingPrepared = useCallback(async (data?: any) => {
    const modalOrder = shippingModalOrder;
    const orderId = modalOrder?.orderId;
    const platform = modalOrder?.platform;
    setShippingModalOrder(null);
    enqueueSnackbar('出貨完成', { variant: 'success' });

    const tn = data?.trackingNumber || data?.paymentNo || '';

    if (orderId) {
      setAllOrders(prev => prev.map(o => o.orderId !== orderId ? o : {
        ...o, status: 'SHIPPED', hasTrackingInfo: true, justShipped: true,
        trackingNumber: data?.trackingNumber || data?.paymentNo,
        paymentNo: data?.paymentNo,
        cvsValidationNo: data?.validationNo,
      }));

      // Track employee action (feeds EmployeePerformancePage)
      const itemCount = modalOrder?.items?.reduce(
        (s: number, i: any) => s + (i.quantity || 1), 0
      ) || 1;
      import('../services/employeeTrackingService').then(module => {
        module.default.trackOrderShipped(orderId, itemCount, {
          platform,
          carrier: 'cvs',
          logisticsSubType: modalOrder?.logistics_sub_type,
          trackingNumber: tn,
          paymentNo: data?.paymentNo,
          user_id: currentUser?.id?.toString(),
          user_name: currentUser?.name,
        });
      }).catch(console.error);

      // Auto-fulfill Shopify order after 超商出貨
      if (platform === 'shopify' && tn) {
        try {
          enqueueSnackbar('正在標記 Shopify 訂單為已出貨...', { variant: 'info' });
          const logisticsType = modalOrder?.logistics_sub_type || 'UNIMARTC2C';
          await api.post(`/api/shopify/orders/${orderId}/fulfill`, {
            trackingNumber: tn,
            trackingCompany: logisticsType === 'FAMIC2C' ? 'Family Mart' : '7-ELEVEN',
          });
          setAllOrders(prev => prev.map(o => o.orderId !== orderId ? o : { ...o, status: 'fulfilled' }));
          enqueueSnackbar('Shopify 訂單已標記為已出貨', { variant: 'success' });
        } catch (err: any) {
          console.error('Auto-fulfill after CVS shipping failed:', err);
          enqueueSnackbar('物流單已建立，但 Shopify 出貨標記失敗，請手動操作', { variant: 'warning' });
        }
      }
    }
  }, [currentUser, enqueueSnackbar, shippingModalOrder]);

  // ── SF International (HK) shipping completion ─────────────────────────────
  const handleSFShippingPrepared = useCallback((data: SFShippingResult) => {
    const modalOrder = sfModalOrder;
    const orderId = modalOrder?.orderId;
    // Keep modal open so admin can click 列印出貨單 immediately after creation.
    enqueueSnackbar(`順豐單已建立 ${data.sfWaybillNo}`, { variant: 'success' });

    if (orderId) {
      setAllOrders(prev => prev.map(o => o.orderId !== orderId ? o : {
        ...o,
        status: 'SHIPPED',
        hasTrackingInfo: true,
        justShipped: true,
        trackingNumber: data.sfWaybillNo,
        // Persist freshly returned SF fields so subsequent renders (including the
        // modal itself) can show label/invoice buttons without a refetch.
        original: {
          ...(o.original || {}),
          sf_waybill_no: data.sfWaybillNo,
          sf_label_url: data.labelUrl,
          sf_invoice_url: data.invoiceUrl,
          sf_customer_order_no: data.customerOrderNo,
          sf_last_error: null,
        },
      }));

      // Track employee action (feeds EmployeePerformancePage)
      const itemCount = modalOrder?.items?.reduce(
        (s: number, i: any) => s + (i.quantity || 1), 0
      ) || 1;
      import('../services/employeeTrackingService').then(module => {
        module.default.trackOrderShipped(orderId, itemCount, {
          platform: modalOrder?.platform,
          carrier: 'sf_intl',
          trackingNumber: data.sfWaybillNo,
          user_id: currentUser?.id?.toString(),
          user_name: currentUser?.name,
        });
      }).catch(console.error);
    }
  }, [currentUser, enqueueSnackbar, sfModalOrder]);

  // ── Parxl (SG) booking completion ─────────────────────────────────────────
  // Mirrors handleSFShippingPrepared, but writes the parxl_* fields. The modal
  // stays open so the admin can print the label straight away.
  const handleParxlShippingPrepared = useCallback((data: ParxlShippingResult) => {
    const modalOrder = parxlModalOrder;
    const orderId = modalOrder?.orderId;
    if (!orderId) return;

    setAllOrders(prev => prev.map(o => o.orderId !== orderId ? o : {
      ...o,
      status: 'SHIPPED',
      hasTrackingInfo: true,
      justShipped: true,
      trackingNumber: data.trackingNo,
      original: {
        ...(o.original || {}),
        parxl_tracking_no: data.trackingNo,
        parxl_merchant_order_no: data.merchantOrderNo,
        parxl_weight_kg: data.weightKg,
        parxl_env: data.env,
        parxl_last_error: null,
      },
    }));

    const itemCount = modalOrder?.items?.reduce(
      (s: number, i: any) => s + (i.quantity || 1), 0
    ) || 1;
    import('../services/employeeTrackingService').then(module => {
      module.default.trackOrderShipped(orderId, itemCount, {
        platform: modalOrder?.platform,
        carrier: 'parxl',
        trackingNumber: data.trackingNo,
        user_id: currentUser?.id?.toString(),
        user_name: currentUser?.name,
      });
    }).catch(console.error);
  }, [currentUser, parxlModalOrder]);

  // ── Render ────────��───────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#fafafa', pb: 4 }}>
      {/* Header */}
      <Box sx={{
        px: { xs: 2, md: 3 }, pt: 2.5, pb: 2,
        bgcolor: '#fff', borderBottom: '1px solid #e8e5e1',
        boxShadow: '0 1px 0 rgba(0,0,0,0.03)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <InventoryIcon sx={{ color: '#4c5e8a', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3748' }}>
            收貨出貨
          </Typography>
          {!loading && (
            <Chip
              label={`${allOrders.length} 筆待處理`}
              size="small"
              sx={{ ml: 1, bgcolor: '#f0f0f0', fontWeight: 500, fontSize: '0.75rem' }}
            />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, bgcolor: '#f5f3f0', borderRadius: '10px', p: 0.5 }}>
            <MuiButton size="small" onClick={() => { setViewMode('orders'); setLinkBatchIndex(0); }}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2,
                bgcolor: viewMode === 'orders' ? '#fff' : 'transparent',
                color: viewMode === 'orders' ? '#1a1a1a' : '#78716c',
                boxShadow: viewMode === 'orders' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>訂單</MuiButton>
            <MuiButton size="small" onClick={() => { setViewMode('products'); setLinkBatchIndex(0); }}
              startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2,
                bgcolor: viewMode === 'products' ? '#fff' : 'transparent',
                color: viewMode === 'products' ? '#1a1a1a' : '#78716c',
                boxShadow: viewMode === 'products' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>叫貨</MuiButton>
            <MuiButton size="small" onClick={() => { setViewMode('bins'); setLinkBatchIndex(0); }}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2,
                bgcolor: viewMode === 'bins' ? '#fff' : 'transparent',
                color: viewMode === 'bins' ? '#1a1a1a' : '#78716c',
                boxShadow: viewMode === 'bins' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>📦 貨架格</MuiButton>
            <MuiButton size="small" onClick={() => { setViewMode('receive'); setLinkBatchIndex(0); }}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2,
                bgcolor: viewMode === 'receive' ? '#fff' : 'transparent',
                color: viewMode === 'receive' ? '#1a1a1a' : '#78716c',
                boxShadow: viewMode === 'receive' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>✓ 收貨</MuiButton>
          </Box>
        </Box>

        {/* Search bar (isolated component — keystrokes don't re-render the page) */}
        <SearchInput
          ref={searchRef}
          onDebouncedChange={setSearchQuery}
          delay={250}
          autoFocus={viewMode === 'receive'}
          setSignal={searchSetSignal}
          placeholder={viewMode === 'receive' ? '掃描或輸入標籤 / SKU / 商品名稱，找出手上這件…' : '掃描或輸入 SKU / 標籤 / 商品名稱 / 顧客姓名 / 電話 / 寄件編號 搜尋訂單…'}
        />

        {/* Category chips — pick 上衣 / 外套 / 下身 / 配件… first, then keyword-search
            inside it. Counts are pre-category, so the row never collapses on you. */}
        {viewMode !== 'bins' && categoryOptions.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#a8a29e', fontWeight: 600, mr: 0.5 }}>分類</Typography>
            <Chip
              label="全部"
              size="small"
              onClick={() => setCategoryFilter('')}
              sx={{
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                bgcolor: !categoryFilter ? '#4c5e8a' : '#fff',
                color: !categoryFilter ? '#fff' : '#57534e',
                border: '1px solid', borderColor: !categoryFilter ? '#4c5e8a' : '#e8e5e1',
                '&:hover': { bgcolor: !categoryFilter ? '#3d4e75' : '#f5f3f0' },
              }}
            />
            {categoryOptions.map(opt => {
              const active = categoryFilter === opt.key;
              return (
                <Chip
                  key={opt.key}
                  label={`${opt.label} ${opt.count}`}
                  size="small"
                  onClick={() => setCategoryFilter(active ? '' : opt.key)}
                  sx={{
                    fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                    bgcolor: active ? '#4c5e8a' : '#fff',
                    color: active ? '#fff' : '#57534e',
                    border: '1px solid', borderColor: active ? '#4c5e8a' : '#e8e5e1',
                    '&:hover': { bgcolor: active ? '#3d4e75' : '#f5f3f0' },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* 「搜尋全部時間」toggle — search the storefront orders DB beyond the on-page window */}
        {viewMode !== 'receive' && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={searchAllLoading ? '搜尋全部時間中…' : (searchAllMode ? '🔍 搜尋全部時間：開' : '🔍 搜尋全部時間')}
              size="small"
              onClick={() => setSearchAllMode(v => !v)}
              sx={{
                fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                bgcolor: searchAllMode ? '#4c5e8a' : '#fff',
                color: searchAllMode ? '#fff' : '#4c5e8a',
                border: '1px solid', borderColor: searchAllMode ? '#4c5e8a' : '#c7d2fe',
                '&:hover': { bgcolor: searchAllMode ? '#3d4d73' : '#eef2ff' },
              }}
            />
            {searchAllMode && (
              <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>
                {searchQuery.trim().length < 2
                  ? '輸入訂單編號 / 顧客姓名 / 電話，連已出貨、已取消的舊官網訂單也找得到'
                  : `找到 ${searchAllOrders.length} 筆官網訂單（含已出貨、已取消）`}
              </Typography>
            )}
            {/* Auto-fired 寄件編號 search — say so, otherwise results appearing without
                the toggle being on looks like a glitch. */}
            {!searchAllMode && looksLikeShipmentCode(searchQuery) && (
              <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>
                {searchAllLoading
                  ? '以寄件編號搜尋全部官網訂單中…'
                  : `寄件編號搜尋：找到 ${searchAllOrders.length} 筆官網訂單（含已出貨）`}
              </Typography>
            )}
          </Box>
        )}

        {/* Variant chips: appear when search is active and matches surface colors/sizes */}
        {viewMode !== 'receive' && searchQuery && (variantOptions.colors.length > 0 || variantOptions.sizes.length > 0) && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {variantOptions.colors.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#a8a29e', fontWeight: 600, mr: 0.5 }}>顏色</Typography>
                {variantOptions.colors.map(c => {
                  const active = colorFilter === c;
                  return (
                    <Chip key={c} label={c} size="small"
                      onClick={() => setColorFilter(active ? '' : c)}
                      sx={{
                        fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                        bgcolor: active ? '#4c5e8a' : '#fff',
                        color: active ? '#fff' : '#57534e',
                        border: '1px solid', borderColor: active ? '#4c5e8a' : '#e8e5e1',
                        '&:hover': { bgcolor: active ? '#3d4e75' : '#f5f3f0' },
                      }}
                    />
                  );
                })}
                {colorFilter && (
                  <Chip label="清除" size="small" onClick={() => setColorFilter('')}
                    sx={{ fontSize: '0.7rem', color: '#a8a29e', bgcolor: 'transparent', cursor: 'pointer' }} />
                )}
              </Box>
            )}
            {variantOptions.sizes.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#a8a29e', fontWeight: 600, mr: 0.5 }}>尺寸</Typography>
                {variantOptions.sizes.map(s => {
                  const active = sizeFilter === s;
                  return (
                    <Chip key={s} label={s} size="small"
                      onClick={() => setSizeFilter(active ? '' : s)}
                      sx={{
                        fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', minWidth: 36,
                        bgcolor: active ? '#4c5e8a' : '#fff',
                        color: active ? '#fff' : '#57534e',
                        border: '1px solid', borderColor: active ? '#4c5e8a' : '#e8e5e1',
                        '&:hover': { bgcolor: active ? '#3d4e75' : '#f5f3f0' },
                      }}
                    />
                  );
                })}
                {sizeFilter && (
                  <Chip label="清除" size="small" onClick={() => setSizeFilter('')}
                    sx={{ fontSize: '0.7rem', color: '#a8a29e', bgcolor: 'transparent', cursor: 'pointer' }} />
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Filter bar */}
        {!loading && viewMode !== 'receive' && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {([
              { key: 'all' as const, label: '全部' },
              { key: 'unreceived' as const, label: '未收到' },
              { key: 'ready' as const, label: '可出貨' },
              { key: 'shipped' as const, label: '已出貨' },
            ]).map(f => (
              <Chip
                key={f.key}
                label={
                  f.key === 'shipped' && shippedStorefrontLoading
                    ? `${f.label} 載入中…`
                    : f.label
                }
                size="small"
                onClick={() => setStatusFilter(f.key)}
                sx={{
                  fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                  bgcolor: statusFilter === f.key ? '#4c5e8a' : '#f5f3f0',
                  color: statusFilter === f.key ? '#fff' : '#57534e',
                  '&:hover': { bgcolor: statusFilter === f.key ? '#3d4e75' : '#e8e5e1' },
                }}
              />
            ))}
            <Box sx={{ mx: 1, width: '1px', height: 20, bgcolor: '#e8e5e1' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#a8a29e' }}>
              {stats.orders} 筆
            </Typography>
            {stats.readyToShip > 0 && (
              <Typography sx={{ fontSize: '0.8rem', color: '#15803d', fontWeight: 600 }}>
                {stats.readyToShip} 可出貨
              </Typography>
            )}
            <Box sx={{ mx: 0.5, width: '1px', height: 20, bgcolor: '#e8e5e1' }} />
            {([
              { key: 'all' as const, label: '全部來源' },
              { key: 'shopee' as const, label: '蝦皮' },
              { key: 'storefront' as const, label: '官網' },
              // 門市 last, after the three shops, because it is not one.
              { key: 'counter' as const, label: '門市' },
            ]).map(f => (
              <Chip key={f.key} label={f.label} size="small"
                onClick={() => setSourceFilter(f.key)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  bgcolor: sourceFilter === f.key ? '#4c5e8a' : '#f5f3f0',
                  color: sourceFilter === f.key ? '#fff' : '#78716c',
                  '&:hover': { bgcolor: sourceFilter === f.key ? '#3d4e75' : '#e8e5e1' },
                }}
              />
            ))}
            <Tooltip title="只看國際訂單（香港/澳門走順豐，新加坡走 Parxl）" arrow>
              <Chip
                label="🌏 國際"
                size="small"
                onClick={() => setHkOnlyFilter(prev => !prev)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  bgcolor: hkOnlyFilter ? '#dc2626' : '#f5f3f0',
                  color: hkOnlyFilter ? '#fff' : '#78716c',
                  '&:hover': { bgcolor: hkOnlyFilter ? '#b91c1c' : '#e8e5e1' },
                }}
              />
            </Tooltip>
            <Box sx={{ mx: 0.5, width: '1px', height: 20, bgcolor: '#e8e5e1' }} />
            <Tooltip title="只看「已收到 5 件以上」的訂單（可先包目標）" arrow>
              <Chip
                label="已收 5+ 件"
                size="small"
                onClick={() => setBigOrderFilter(prev => !prev)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  bgcolor: bigOrderFilter ? '#b45309' : '#f5f3f0',
                  color: bigOrderFilter ? '#fff' : '#78716c',
                  '&:hover': { bgcolor: bigOrderFilter ? '#92400e' : '#e8e5e1' },
                }}
              />
            </Tooltip>
            <Tooltip title="只看已釘選（需注意）的訂單" arrow>
              <Chip
                label="📌 需注意"
                size="small"
                onClick={() => setPinnedOnlyFilter(prev => !prev)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  bgcolor: pinnedOnlyFilter ? '#dc2626' : '#f5f3f0',
                  color: pinnedOnlyFilter ? '#fff' : '#78716c',
                  '&:hover': { bgcolor: pinnedOnlyFilter ? '#b91c1c' : '#e8e5e1' },
                }}
              />
            </Tooltip>
            <Box sx={{ mx: 0.5, width: '1px', height: 20, bgcolor: '#e8e5e1' }} />
            {([
              { key: 'all' as const, label: '全部標籤' },
              { key: 'numbers' as const, label: '僅數字' },
              { key: 'no-numbers' as const, label: '非數字' },
              { key: 'no-tag' as const, label: '無標籤' },
            ]).map(f => (
              <Chip key={f.key} label={f.label} size="small"
                onClick={() => setTagFilter(f.key)}
                sx={{
                  fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                  bgcolor: tagFilter === f.key ? '#78716c' : '#f5f3f0',
                  color: tagFilter === f.key ? '#fff' : '#78716c',
                  '&:hover': { bgcolor: tagFilter === f.key ? '#57534e' : '#e8e5e1' },
                }}
              />
            ))}
            <Box sx={{ ml: 'auto' }} />
            <Tooltip
              title={
                autoPrintLabel
                  ? (printBridgeOnline
                      ? '收到商品時自動列印標籤（橋接已連線）'
                      : '自動列印已啟用，但找不到本機列印橋接 (localhost:9100)')
                  : '啟用後，點「收到」會自動列印商品標籤到本機 Xprinter'
              }
              arrow
            >
              <Chip
                label={autoPrintLabel
                  ? `自動列印標籤 ${printBridgeOnline ? '🟢' : '🔴'}`
                  : '自動列印標籤'}
                size="small"
                onClick={() => setAutoPrintLabel(v => !v)}
                sx={{
                  fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', mr: 1,
                  bgcolor: autoPrintLabel ? '#4c5e8a' : '#f5f3f0',
                  color: autoPrintLabel ? '#fff' : '#57534e',
                  '&:hover': { bgcolor: autoPrintLabel ? '#3d4e75' : '#e8e5e1' },
                }}
              />
            </Tooltip>
            <Chip
              label={sortOrder === 'oldest' ? '最舊優先' : '最新優先'}
              size="small"
              onClick={() => setSortOrder(s => s === 'oldest' ? 'newest' : 'oldest')}
              sx={{
                fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                bgcolor: '#f5f3f0', color: '#57534e',
                '&:hover': { bgcolor: '#e8e5e1' },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2, maxWidth: 1200, mx: 'auto' }}>
        {loading ? (
          <Box>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} variant="rounded" height={120} sx={{ mb: 1.5, borderRadius: '8px' }} />
            ))}
          </Box>
        ) : viewMode === 'products' ? (
          /* ═══ 叫貨 MODE ═══ */
          <Box>
            {/* Time filter bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: '#fafaf8', borderRadius: '10px', border: '1px solid #e8e5e1' }}>
              <Typography sx={{ fontWeight: 600, color: '#5c5854', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>篩選起始時間:</Typography>
              <TextField
                type="datetime-local"
                value={groupingDateDraft}
                // Only the draft follows keystrokes; the list follows on blur,
                // Enter, or once the field sits still (see commitGroupingAfterDate).
                // An incomplete date reads as '' — ignore it rather than treat it
                // as 清除, which would put the whole backlog on screen.
                onChange={(e) => { if (e.target.value) setGroupingDateDraft(e.target.value); }}
                onBlur={() => commitGroupingAfterDate(groupingDateDraft)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitGroupingAfterDate(groupingDateDraft); }}
                size="small"
                sx={{ minWidth: 220 }}
                InputLabelProps={{ shrink: true }}
              />
              {groupingAfterDate && (
                <MuiButton size="small" onClick={() => {
                  // In 多帳號模式 clearing the cutoff hands every pending 款 in the
                  // backlog a cart slot, and slots don't come back until the cycle ends.
                  if (multiAccountMode && !window.confirm('清除起始時間會把所有待叫款式分配進購物車帳號，確定要清除？')) return;
                  commitGroupingAfterDate('');
                }} variant="outlined" color="secondary"
                  sx={{ textTransform: 'none' }}>清除</MuiButton>
              )}
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* 多帳號模式 — a Sinsang cart holds 99 款; turn this on for a
                    cycle too big for one login (e.g. after a Korean holiday). */}
                {canSeeLinks && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={multiAccountMode}
                        onChange={(e) => {
                          if (e.target.checked) handleToggleMultiAccount(true);
                          else setCartClearConfirm(true);
                        }}
                        sx={{ color: '#8b7355', '&.Mui-checked': { color: '#8b7355' } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#5c5854' }}>
                        多帳號模式
                      </Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                )}
                <ShoppingCartIcon sx={{ color: '#8b7355', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>
                  {visibleGroups.length} 個產品，共 {visibleGroups.reduce((s, g) => s + g.totalQty, 0)} 件
                </Typography>
              </Box>
            </Box>

            {/* Account overview — one chip per Sinsang cart. Everything starts
                collapsed; tap a chip (or its section header) to open just that
                cart's 款. */}
            {canSeeLinks && multiAccountMode && cartAccountFill.length > 0 && (
              <Box sx={{
                mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                position: 'sticky', top: 0, zIndex: 11, bgcolor: 'white',
                borderRadius: '10px', border: '1px solid #e8e5e1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#5c5854', mr: 0.5 }}>
                  購物車帳號
                </Typography>
                {cartAccountFill.map(f => {
                  const key = String(f.account);
                  const open = expandedAccounts.has(key);
                  const full = f.count >= cartCapacity;
                  const pending = pendingByAccount.get(key) || 0;
                  return (
                    <Chip
                      key={key}
                      size="small"
                      onClick={() => toggleAccountExpanded(key)}
                      label={`帳號${f.account} · ${f.count}/${cartCapacity}${pending ? ` · 待叫 ${pending}` : ''}`}
                      icon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        fontWeight: 700, cursor: 'pointer',
                        bgcolor: open ? '#8b7355' : (full ? '#fef3c7' : '#dbeafe'),
                        color: open ? '#fff' : (full ? '#92400e' : '#1e40af'),
                        '& .MuiChip-icon': { color: 'inherit' },
                        '&:hover': { bgcolor: open ? '#6b5c42' : '#e8e5e1' },
                      }}
                    />
                  );
                })}
                {(pendingByAccount.get('none') || 0) > 0 && (
                  <Chip
                    size="small"
                    onClick={() => toggleAccountExpanded('none')}
                    label={`未分配 · ${pendingByAccount.get('none')}`}
                    icon={expandedAccounts.has('none')
                      ? <ExpandLessIcon sx={{ fontSize: 16 }} />
                      : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      fontWeight: 700, cursor: 'pointer',
                      bgcolor: expandedAccounts.has('none') ? '#8b7355' : '#f0ede9',
                      color: expandedAccounts.has('none') ? '#fff' : '#5c5854',
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  {expandedAccounts.size > 0 && (
                    <MuiButton size="small" onClick={() => setExpandedAccounts(new Set())}
                      sx={{ textTransform: 'none', color: '#8c8580' }}>全部收合</MuiButton>
                  )}
                  <MuiButton size="small"
                    onClick={() => setExpandedAccounts(new Set([
                      ...cartAccountFill.map(f => String(f.account)),
                      ...((pendingByAccount.get('none') || 0) > 0 ? ['none'] : []),
                    ]))}
                    sx={{ textTransform: 'none', color: '#8b7355' }}>全部展開</MuiButton>
                </Box>
              </Box>
            )}

            {/* Batch open links — sticky bar. In 多帳號模式 each account gets its
                own opener in its section header, so every tab you open belongs
                to the cart you're currently logged into. */}
            {canSeeLinks && !multiAccountMode && (() => {
              // groupsWithLinks: each group paired with its first link (the one batch-opened)
              const groupsWithLinks = visibleGroups
                .map(g => ({ group: g, link: (getProductLink(g.sku).allLinks?.[0] || getProductLink(g.sku).link || '') }))
                .filter(x => x.link);
              const remaining = groupsWithLinks.length - linkBatchIndex;
              const batchSize = Math.min(10, remaining);
              return groupsWithLinks.length > 0 ? (
                <Box sx={{
                  mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                  position: 'sticky', top: 0, zIndex: 10,
                  bgcolor: 'white', py: 1.5, px: 2,
                  borderBottom: '1px solid #e8e5e1', borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <MuiButton variant="outlined" size="small" startIcon={<OpenInNewIcon />} disabled={remaining <= 0}
                    onClick={() => {
                      const batch = groupsWithLinks.slice(linkBatchIndex, linkBatchIndex + 10);
                      batch.forEach((x, i) => setTimeout(() => window.open(withSinsangOrder(x.link, x.group, sinsangOptionMap), '_blank', 'noopener'), i * 300));
                      setLinkBatchIndex(linkBatchIndex + batch.length);
                    }}
                    sx={{ textTransform: 'none', borderColor: '#8b7355', color: '#8b7355', '&:hover': { bgcolor: '#f5f3f0', borderColor: '#6b5c42' } }}>
                    開啟下{batchSize}個連結 ({linkBatchIndex}/{groupsWithLinks.length})
                  </MuiButton>
                  {sinsangBatchReady && (
                    <MuiButton variant="contained" size="small" disabled={remaining <= 0}
                      onClick={() => {
                        startSinsangBatch(groupsWithLinks.slice(linkBatchIndex)
                          .map(x => withSinsangOrder(x.link, x.group, sinsangOptionMap)));
                        setLinkBatchIndex(groupsWithLinks.length);
                      }}
                      sx={{ textTransform: 'none', bgcolor: '#8b7355', '&:hover': { bgcolor: '#6b5c42' } }}>
                      自動叫貨剩下 {Math.max(0, remaining)} 個
                    </MuiButton>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#5c5854' }}>從第</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={linkBatchIndex}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isNaN(v)) { setLinkBatchIndex(0); return; }
                        const clamped = Math.max(0, Math.min(groupsWithLinks.length, v));
                        setLinkBatchIndex(clamped);
                      }}
                      inputProps={{ min: 0, max: groupsWithLinks.length, style: { width: 56, textAlign: 'center', padding: '6px 4px' } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                    />
                    <Typography sx={{ fontSize: '0.8rem', color: '#5c5854' }}>個開始 / 共 {groupsWithLinks.length}</Typography>
                  </Box>
                  {linkBatchIndex > 0 && (
                    <MuiButton size="small" onClick={() => setLinkBatchIndex(0)} sx={{ textTransform: 'none', color: '#8c8580' }}>重置</MuiButton>
                  )}
                </Box>
              ) : null;
            })()}

            {sinsangBatch && sinsangBatch.total > 0 && (
              <Box sx={{
                position: 'fixed', left: 16, bottom: 16, zIndex: 1300,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                bgcolor: 'white', border: '1px solid #e8e5e1', borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)', px: 2, py: 1.25, maxWidth: 'calc(100vw - 32px)',
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
                  自動叫貨 {sinsangBatch.done}/{sinsangBatch.total}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#5c5854' }}>
                  進行中 {sinsangBatch.open} · 排隊 {sinsangBatch.queued}
                </Typography>
                {/* Paused so the PC doesn't drown in tabs; closing any tab resumes. */}
                {sinsangBatch.blocked && (
                  <Typography sx={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 700 }}>
                    {sinsangBatch.blocked === 'held'
                      ? '已暫停：待確認/失敗的分頁佔滿名額，處理完關掉就會繼續'
                      : `已暫停：Chrome 已開 ${sinsangBatch.maxChromeTabs ?? 30} 個分頁，關掉一些就會繼續`}
                  </Typography>
                )}
                {sinsangBatch.needsYou > 0 && (
                  <Chip size="small" label={`待確認 ${sinsangBatch.needsYou}`} sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                )}
                {sinsangBatch.failed > 0 && (
                  <Chip size="small" label={`失敗 ${sinsangBatch.failed}`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                )}
                {sinsangBatch.running ? (
                  <MuiButton size="small" onClick={stopSinsangBatch} sx={{ textTransform: 'none', color: '#8c8580' }}>停止</MuiButton>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: '#2e7d32' }}>完成，剩下的分頁請手動處理</Typography>
                )}
              </Box>
            )}

            {visibleGroups.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                目前沒有待叫貨的產品 🎉
              </Alert>
            )}
            {productRows.map(row => {
              if (row.kind === 'header') {
                const { account, groups: sectionGroups } = row;
                const fill = account == null
                  ? null
                  : cartAccountFill.find(f => f.account === account);
                // Own count comes from the server map: a 款 already received
                // still holds its cart slot until you check out, so it must
                // count against the 99 even though it's off this list.
                const owned = fill ? fill.count : sectionGroups.length;
                const full = account != null && owned >= cartCapacity;
                const openable = canSeeLinks
                  ? sectionGroups
                      .map(g => {
                        const link = getProductLink(g.sku).allLinks?.[0] || getProductLink(g.sku).link || '';
                        return link ? withSinsangOrder(link, g, sinsangOptionMap) : '';
                      })
                      .filter(Boolean)
                  : [];
                const sectionKey = account == null ? 'none' : String(account);
                const expanded = expandedAccounts.has(sectionKey);
                const cursor = account == null ? 0 : (accountBatchIndex[account] || 0);
                const remainingLinks = openable.length - cursor;
                const perBatch = Math.max(1, accountBatchSize[sectionKey] || 10);
                const batchSize = Math.min(perBatch, Math.max(0, remainingLinks));
                return (
                  <Box key={`header-${account ?? 'none'}`} sx={{
                    mt: 3, mb: 1.5, px: 2, py: 1.5, display: 'flex', alignItems: 'center',
                    gap: 1.5, flexWrap: 'wrap', borderRadius: '10px',
                    bgcolor: account == null ? '#faf7f2' : '#f5f3f0',
                    border: '1px solid', borderColor: full ? '#e9c46a' : '#e8e5e1',
                  }}>
                    <Box
                      onClick={() => toggleAccountExpanded(sectionKey)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                    >
                      {expanded
                        ? <ExpandLessIcon sx={{ fontSize: 20, color: '#8b7355' }} />
                        : <ExpandMoreIcon sx={{ fontSize: 20, color: '#8b7355' }} />}
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>
                        {account == null ? '未分配（無採購連結）' : `帳號 ${account}`}
                      </Typography>
                    </Box>
                    {account != null && (
                      <Chip
                        size="small"
                        label={`${owned} / ${cartCapacity} 款`}
                        sx={{
                          fontWeight: 700,
                          bgcolor: full ? '#fef3c7' : '#dbeafe',
                          color: full ? '#92400e' : '#1e40af',
                        }}
                      />
                    )}
                    {full && (
                      <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>
                        已滿，新款式會進下一個帳號
                      </Typography>
                    )}
                    <Chip size="small" variant="outlined" label={`本次待叫 ${sectionGroups.length} 款`}
                      sx={{ borderColor: '#d1cdc8', color: '#8c8580' }} />
                    {account != null && openable.length > 0 && (
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Per-account batch size: how many tabs one press opens. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.78rem', color: '#5c5854' }}>每次</Typography>
                          <TextField
                            type="number"
                            size="small"
                            value={perBatch}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              setBatchSizeFor(sectionKey, Number.isNaN(v) ? 10 : Math.max(1, Math.min(50, v)));
                            }}
                            inputProps={{ min: 1, max: 50, style: { width: 44, textAlign: 'center', padding: '6px 4px' } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: '#fff' } }}
                          />
                          <Typography sx={{ fontSize: '0.78rem', color: '#5c5854' }}>個</Typography>
                        </Box>
                        <MuiButton variant="outlined" size="small" startIcon={<OpenInNewIcon />}
                          disabled={remainingLinks <= 0}
                          onClick={() => {
                            const batch = openable.slice(cursor, cursor + perBatch);
                            batch.forEach((link, i) => setTimeout(() => window.open(link, '_blank', 'noopener'), i * 300));
                            setAccountBatchIndex(prev => ({ ...prev, [account]: cursor + batch.length }));
                          }}
                          sx={{ textTransform: 'none', borderColor: '#8b7355', color: '#8b7355', '&:hover': { bgcolor: '#f5f3f0', borderColor: '#6b5c42' } }}>
                          開啟下{batchSize}個連結 ({cursor}/{openable.length})
                        </MuiButton>
                        {sinsangBatchReady && (
                          <MuiButton variant="contained" size="small" disabled={remainingLinks <= 0}
                            onClick={() => {
                              startSinsangBatch(openable.slice(cursor));
                              setAccountBatchIndex(prev => ({ ...prev, [account]: openable.length }));
                            }}
                            sx={{ textTransform: 'none', bgcolor: '#8b7355', '&:hover': { bgcolor: '#6b5c42' } }}>
                            自動叫貨剩下 {Math.max(0, remainingLinks)} 個
                          </MuiButton>
                        )}
                        {cursor > 0 && (
                          <MuiButton size="small" onClick={() => setAccountBatchIndex(prev => ({ ...prev, [account]: 0 }))}
                            sx={{ textTransform: 'none', color: '#8c8580' }}>重置</MuiButton>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              }
              const group = row.group;
              const linkInfo = canSeeLinks ? getProductLink(group.sku) : {};
              const handleProductLinkClick = (link: string) => {
                window.open(withSinsangOrder(link, group, sinsangOptionMap), '_blank', 'noopener');
              };
              return (
                <Box key={group.key} sx={{ mb: 2, borderRadius: '14px', border: '1px solid #e8e5e1', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    {group.imageUrl && (
                      <Box sx={{ flexShrink: 0 }}>
                        <HoverZoomImage
                          src={group.imageUrl}
                          width={80}
                          height={80}
                          objectFit="cover"
                          borderColor="#e8e5e1"
                          thumbSx={{
                            borderRadius: '10px',
                            border: '1px solid #e8e5e1',
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }} noWrap>{group.productName}</Typography>
                          {group.sku && <Chip label={group.sku} size="small" sx={{ bgcolor: '#f0ede9', color: '#5c5854', fontWeight: 600 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={`共 ${group.totalQty} 件`} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                          <Chip label={`${group.orderCount} 筆`} size="small" variant="outlined" sx={{ borderColor: '#d1cdc8', color: '#8c8580' }} />
                          {/* Link buttons — allowlisted users only. */}
                          {canSeeLinks && linkInfo.allLinks && linkInfo.allLinks.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {linkInfo.allLinks.map((link: string, idx: number) => (
                                <Tooltip key={idx} title={`開啟連結 ${idx + 1}`}>
                                  <IconButton onClick={() => handleProductLinkClick(link)} size="small"
                                    sx={{ color: idx === 0 ? '#3b82f6' : '#8b7355', width: 32, height: 32, border: '1px solid', borderColor: idx === 0 ? '#bfdbfe' : '#d1cdc8', borderRadius: '6px' }}>
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          )}
                          {canSeeLinks && !linkInfo.allLinks && linkInfo.link && (
                            <Tooltip title="開啟產品頁面">
                              <IconButton onClick={() => handleProductLinkClick(linkInfo.link!)} size="small"
                                sx={{ color: '#3b82f6', width: 32, height: 32, border: '1px solid #bfdbfe', borderRadius: '6px' }}>
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      {/* Variant chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {group.specs.map((spec, idx) => {
                          const specId = `${group.key}|${spec.variant}`;
                          const isExpanded = expandedGroupSpecs.has(specId);
                          return (
                            <Chip key={idx} variant="outlined" size="small"
                              onClick={() => setExpandedGroupSpecs(prev => {
                                const next = new Set(prev);
                                if (next.has(specId)) next.delete(specId); else next.add(specId);
                                return next;
                              })}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span style={{ fontWeight: 600 }}>{spec.variant}</span>
                                  <span style={{ fontWeight: 700, color: '#3b82f6' }}>× {spec.totalQty}</span>
                                  <span style={{ fontSize: '0.7rem', color: '#8b7355', marginLeft: 2 }}>
                                    ({spec.orders.length}筆)
                                  </span>
                                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 14, ml: 0.3 }} /> : <ExpandMoreIcon sx={{ fontSize: 14, ml: 0.3 }} />}
                                </Box>
                              }
                              sx={{
                                height: 'auto', py: 0.5, borderRadius: '8px', cursor: 'pointer',
                                borderColor: isExpanded ? '#8b7355' : '#e8e5e1',
                                bgcolor: isExpanded ? '#f5f3f0' : 'transparent',
                                '&:hover': { borderColor: '#8b7355', bgcolor: '#f5f3f0' },
                                '& .MuiChip-label': { whiteSpace: 'normal', px: 1 },
                              }}
                            />
                          );
                        })}
                      </Box>
                      {/* Expanded: show full order cards for expanded variants */}
                      {(() => {
                        const expandedSpecs = group.specs.filter(s => expandedGroupSpecs.has(`${group.key}|${s.variant}`));
                        if (expandedSpecs.length === 0) return null;
                        const orderNumSet = new Set<string>();
                        expandedSpecs.forEach(s => s.orders.forEach(o => orderNumSet.add(o)));
                        const ordersToShow = allOrders.filter(o =>
                          orderNumSet.has(o.orderNumber) && (o.demandSource || 'customer') === group.demandSource);
                        if (ordersToShow.length === 0) return null;
                        return (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e8e5e1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {ordersToShow.map(o => {
                              const relevantItems = o.items.filter(it => {
                                if (it.received || it.outOfStock) return false;
                                // group.key is source-prefixed (see demandGrouping.ts), so it can
                                // never equal a bare SKU — compare against the style key.
                                const key = it.sku || it.productName;
                                if (key !== styleKeyOfKey(group.key)) return false;
                                const variant = normalizeVariant(it.specOverride || it.variantOrSpec);
                                return expandedSpecs.some(s => s.variant === variant);
                              });
                              if (relevantItems.length === 0) return null;
                              return (
                                <UnifiedOrderCard
                                  key={o.id}
                                  order={o}
                                  visibleItems={relevantItems}
                                  onToggleReceived={handleToggleReceived}
                                  onUpdateField={handleUpdateField}
                                  onShip={handleShip}
                                  onPrintLabel={handlePrintLabel}
                                  onQuickShip={handleQuickShip}
                                  onHsinchuShip={handleShip}
                                  onFulfill={handleFulfill}
                                  onReship={handleReship}
                                  onOutOfStockNotify={handleOutOfStockNotify}
                                  onRefund={handleRefund}
                                  onCancelOrder={handleCancelOrder}
                                  onRevertShipped={handleRevertShipped}
                                  onToggleOutOfStock={handleToggleOutOfStock}
                                  onVariantUpdate={handleVariantUpdate}
                                  onSkuUpdate={handleSkuUpdate}
                                  onCustomerUpdate={handleCustomerUpdate}
                                  onAddressUpdate={handleAddressUpdate}
                                  onStorefrontShipped={handleStorefrontShipped}
                                  onTogglePin={handleTogglePin}
                                  slotInfo={slotInfoById.get(o.id)}
                                  onResetSlot={handleResetSlot}
                                  onOpenColorPlate={handleOpenColorPlate}
                                  onCounterReceive={handleCounterReceive}
                                  onCounterCancelLine={handleCounterCancelLine}
                                  onCounterCancelOrder={handleCounterCancelOrder}
                                  counterQueue={counterQueue}
                                  showLinks={canSeeLinks}
                                  getProductLink={canSeeLinks ? getProductLink : undefined}
                                  shippingInProgress={shippingOrderId === o.id}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : viewMode === 'bins' ? (
          /* ═══ 依貨架格 MODE — walk the shelf, fill each numbered bin ═══ */
          <Box>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#eef2ff', borderRadius: '10px', border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, color: '#4338ca', fontSize: '0.9rem' }}>
                  共 {binGroups.length} 個貨架格 · {binGroups.reduce((s, [, e]) => s + e.length, 0)} 件商品
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#6366f1', mt: 0.25 }}>
                  依序到每個格子，把下面列出的商品放進去
                </Typography>
              </Box>
            </Box>
            {orphanBins.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1.5 }}>
                  <AlertTitle>{orphanBins.reduce((s, [, e]) => s + e.length, 0)} 件商品的訂單已失效</AlertTitle>
                  這些訂單已無法從系統抓到（可能是蝦皮已取消／退款，或訂單內容已變更）。商品可能還在貨架上，請確認後把商品取下並點「移除」，將格子空出來。
                </Alert>
                {orphanBins.map(([bin, entries]) => (
                  <Box key={`orphan-${bin}`} sx={{ mb: 1.5, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #fecaca', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, height: 44, borderRadius: '10px', bgcolor: '#b91c1c', color: '#fff', fontWeight: 800, fontSize: '1.15rem' }}>{bin}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: '#7f1d1d', fontSize: '1rem' }}>{bin} 號格 · 訂單已失效</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#b91c1c' }}>{entries.length} 件 · 請取下商品後移除</Typography>
                      </Box>
                    </Box>
                    {entries.map((it, idx) => (
                      <Box key={`${it.platform}-${it.orderId}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: idx < entries.length - 1 ? '1px solid #f0eeea' : 'none' }}>
                        {/* Staff have to FIND this item on the shelf to pull it — the photo is the
                            only thing that makes that fast, so orphan rows get the same thumbnail
                            as normal bin rows (served by /api/receiving/slots, since the order
                            itself is gone from the live feed). */}
                        {it.imageUrl
                          ? <Box component="img" src={it.imageUrl} alt="" loading="lazy" sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: '1px solid #fecaca' }} />
                          : <Box sx={{ width: 44, height: 44, borderRadius: '8px', bgcolor: '#fef2f2', flexShrink: 0, border: '1px solid #fecaca' }} />}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.85rem', color: '#1a1a1a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.productName}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', color: '#78716c' }}>{it.variantSpec || '—'}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontFamily: '"DM Mono", monospace', color: '#4c5e8a', fontWeight: 600 }}>{it.orderId}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>{it.platform}</Typography>
                        </Box>
                        <MuiButton size="small" variant="outlined" color="error"
                          onClick={() => handleReleaseOrphan(it.platform, it.orderId)}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          移除
                        </MuiButton>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            )}
            {unbinnedItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning" sx={{ borderRadius: '8px', mb: 1.5 }}>
                  <AlertTitle>{unbinnedItems.length} 件已收貨商品沒有貨架格</AlertTitle>
                  包包、配件等不放貨架格的商品，出貨時容易漏拿。請確認這些商品放在哪裡，出貨時記得一起裝。
                </Alert>
                <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #fde68a', overflow: 'hidden' }}>
                  {unbinnedItems.map(({ order, item }, idx) => (
                    <Box key={`unbinned-${order.id}-${item.id}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: idx < unbinnedItems.length - 1 ? '1px solid #f0eeea' : 'none' }}>
                      {item.imageUrl
                        ? <Box component="img" src={item.imageUrl} alt="" loading="lazy" sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: '1px solid #fde68a' }} />
                        : <Box sx={{ width: 44, height: 44, borderRadius: '8px', bgcolor: '#fffbeb', flexShrink: 0, border: '1px solid #fde68a' }} />}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#1a1a1a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.productName}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#78716c' }}>{item.specOverride || item.variantOrSpec || '—'}{item.tag ? ` · ${item.tag}` : ''}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: '"DM Mono", monospace', color: '#4c5e8a', fontWeight: 600 }}>{order.orderNumber}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>{order.customerName}</Typography>
                      </Box>
                      {order.allReceived
                        ? <Chip label="可出貨" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                        : <Chip label="待齊" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {binGroups.length === 0 && orphanBins.length === 0 && unbinnedItems.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                目前沒有任何貨架格分配（收齊多件訂單的部分商品後會自動分配）
              </Alert>
            ) : binGroups.map(([bin, entries]) => {
              const readyCount = entries.filter(e => e.order.allReceived && !e.order.hasTrackingInfo).length;
              return (
                <Box key={bin} sx={{ mb: 2, bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e8e5e1', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, bgcolor: '#eef2ff', borderBottom: '1px solid #c7d2fe' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, height: 44, borderRadius: '10px', bgcolor: '#4338ca', color: '#fff', fontWeight: 800, fontSize: '1.15rem' }}>{bin}</Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#312e81', fontSize: '1rem' }}>{bin} 號格</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: '#6366f1' }}>{entries.length} 件{readyCount > 0 ? ` · ${readyCount} 件可出貨` : ''}</Typography>
                    </Box>
                  </Box>
                  {entries.map(({ order, item }, idx) => (
                    <Box key={`${order.id}-${item.id}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: idx < entries.length - 1 ? '1px solid #f0eeea' : 'none' }}>
                      {item.imageUrl
                        ? <Box component="img" src={item.imageUrl} alt="" sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: '1px solid #e8e5e1' }} />
                        : <Box sx={{ width: 44, height: 44, borderRadius: '8px', bgcolor: '#f5f3f0', flexShrink: 0 }} />}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#1a1a1a', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.productName}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#78716c' }}>{item.specOverride || item.variantOrSpec || '—'}{item.tag ? ` · ${item.tag}` : ''}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: '"DM Mono", monospace', color: '#4c5e8a', fontWeight: 600 }}>{order.orderNumber}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>{order.customerName}</Typography>
                      </Box>
                      {order.hasTrackingInfo
                        ? <Chip label="已出貨" size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                        : order.allReceived
                          ? <Chip label="可出貨" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                          : <Chip label="待齊" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        ) : viewMode === 'receive' ? (
          /* ═══ 收貨 MODE — match the style by photo, tap the variant, system credits the oldest order ═══ */
          <Box>
            {/* Confirmation strip / 可出貨 banner — pinned, stays until the next receive */}
            {receiveConfirm && (() => {
              // Live lookup: optimistic updates land in allOrders, so completeness +
              // bin badges refresh in place while the snapshot keeps 復原/換訂單 valid.
              const live = allOrders.find(o => o.id === receiveConfirm.order.id) || receiveConfirm.order;
              const bins = slotInfoById.get(live.id)?.slots || [];
              // Banner survives the ship action (hasTrackingInfo flips true) so 復原
              // stays reachable; the 出貨 button reflects the shipped state instead.
              const complete = live.allReceived;
              const shipped = !!live.hasTrackingInfo;
              const binPending = !complete && !live.hasTrackingInfo && live.totalCount >= 2 && bins.length === 0;
              const greenBtnSx = { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: '#86efac', color: '#15803d', '&:hover': { borderColor: '#4ade80', bgcolor: '#bbf7d0' } };
              const whiteBtnSx = { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.6)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } };
              return complete ? (
                /* 🎉 order fully received — success banner + the order's FULL card inline,
                   so staff verify the physical bag against the order before shipping.
                   Card is fed from the order already in memory — no refetch, no view
                   switch — and mounts ONLY in this completed-banner state (perf guard).
                   Ship/print/reship handlers operate on the order object + page-level
                   modals, so they work regardless of the active view mode. */
                <>
                  <Box sx={{
                    position: 'sticky', top: 8, zIndex: 5, mb: 1.5, px: 2.5, py: 2,
                    bgcolor: '#16a34a', borderRadius: '12px',
                    boxShadow: '0 4px 14px rgba(22,163,74,0.35)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem', lineHeight: 1.3 }}>
                          {shipped ? `✅ ${live.orderNumber} 已出貨` : `🎉 ${live.orderNumber} 全部到齊，可出貨`}
                        </Typography>
                        <Typography sx={{ color: '#dcfce7', fontSize: '0.9rem', mt: 0.25 }}>
                          {live.customerName}
                          {bins.length > 0 && ` · 其餘商品在 ${bins.join('、')} 號格，記得一起拿`}
                          {!shipped && ' · 核對下方訂單內容後直接出貨'}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setReceiveConfirm(null)}
                        sx={{ color: '#dcfce7', mt: -0.5, mr: -1, fontSize: '1rem', fontWeight: 700 }}>✕</IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                      <MuiButton size="small" variant="outlined" startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
                        onClick={handleReceiveUndo} sx={whiteBtnSx}>
                        復原
                      </MuiButton>
                      {receiveConfirm.candidates.length > 1 && (
                        <MuiButton size="small" variant="outlined" onClick={() => setReceiveSwitchOpen(true)} sx={whiteBtnSx}>
                          換訂單
                        </MuiButton>
                      )}
                      {/* Fallback: open the full 訂單 view for anything the inline card can't do */}
                      <MuiButton size="small" onClick={handleReceiveViewOrder}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', color: '#dcfce7', textDecoration: 'underline', minWidth: 0, px: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        在訂單頁開啟
                      </MuiButton>
                    </Box>
                  </Box>
                  {/* Inline order card — same props wiring as the 訂單 view render site */}
                  <Box sx={{ mb: 2.5 }}>
                    <UnifiedOrderCard
                      key={live.id}
                      order={live}
                      visibleItems={live.items}
                      onToggleReceived={handleToggleReceived}
                      onUpdateField={handleUpdateField}
                      onShip={handleShip}
                      onPrintLabel={handlePrintLabel}
                      onQuickShip={handleQuickShip}
                      onHsinchuShip={handleShip}
                      onFulfill={handleFulfill}
                      onReship={handleReship}
                      onOutOfStockNotify={handleOutOfStockNotify}
                      onRefund={handleRefund}
                      onCancelOrder={handleCancelOrder}
                      onRevertShipped={handleRevertShipped}
                      onToggleOutOfStock={handleToggleOutOfStock}
                      onVariantUpdate={handleVariantUpdate}
                      onSkuUpdate={handleSkuUpdate}
                      onCustomerUpdate={handleCustomerUpdate}
                      onAddressUpdate={handleAddressUpdate}
                      onStorefrontShipped={handleStorefrontShipped}
                      onTogglePin={handleTogglePin}
                      slotInfo={slotInfoById.get(live.id)}
                      onResetSlot={handleResetSlot}
                      onOpenColorPlate={handleOpenColorPlate}
                                  onCounterReceive={handleCounterReceive}
                                  onCounterCancelLine={handleCounterCancelLine}
                                  onCounterCancelOrder={handleCounterCancelOrder}
                                  counterQueue={counterQueue}
                      showLinks={canSeeLinks}
                      getProductLink={canSeeLinks ? getProductLink : undefined}
                      shippingInProgress={shippingOrderId === live.id}
                      isExpanded={true}
                      onExpandAll={(o) => setExpandedOrderIds(prev => {
                        const next = new Set(prev);
                        if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                        return next;
                      })}
                    />
                  </Box>
                </>
              ) : (
                /* normal confirmation strip — with the 貨架格 destination */
                <Box sx={{
                  position: 'sticky', top: 8, zIndex: 5, mb: 2, px: 2, py: 1.25,
                  bgcolor: '#dcfce7', border: '1px solid #86efac', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <Typography sx={{ fontWeight: 700, color: '#15803d', fontSize: '0.9rem', flex: 1, minWidth: 200 }}>
                    ✓ 已收 {receiveConfirm.productName}{receiveConfirm.variant && receiveConfirm.variant !== '-' ? ` ${receiveConfirm.variant}` : ''} → {live.orderNumber}
                    {bins.length > 0 && (
                      <Box component="span" sx={{ ml: 1, bgcolor: '#4338ca', color: '#fff', borderRadius: '6px', px: 1, py: 0.25, fontSize: '0.85rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        放 {bins.join('、')} 號格
                      </Box>
                    )}
                    {bins.length === 0 && binPending && (
                      <Box component="span" sx={{ ml: 1, color: '#78716c', fontSize: '0.8rem', fontWeight: 600 }}>
                        貨架格分配中…
                      </Box>
                    )}
                  </Typography>
                  <MuiButton size="small" variant="outlined" startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
                    onClick={handleReceiveUndo} sx={greenBtnSx}>
                    復原
                  </MuiButton>
                  {receiveConfirm.candidates.length > 1 && (
                    <MuiButton size="small" variant="outlined" onClick={() => setReceiveSwitchOpen(true)} sx={greenBtnSx}>
                      換訂單
                    </MuiButton>
                  )}
                </Box>
              );
            })()}

            {!searchQuery.trim() && !categoryFilter ? (
              <Box sx={{ textAlign: 'center', py: 8, px: 2, color: '#a8a29e' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#78716c' }}>先選上面的分類，或掃描商品標籤開始收貨</Typography>
                <Typography sx={{ fontSize: '0.85rem', mt: 0.75 }}>先從照片認出手上這件，再點衣服標籤上的顏色/尺寸，系統會自動記到最早的訂單</Typography>
              </Box>
            ) : receiveGroups.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                {searchQuery.trim()
                  ? `找不到未收商品「${searchQuery}」${categoryFilter ? `（分類：${CATEGORY_LABELS[categoryFilter]}）` : ''}`
                  : `分類「${CATEGORY_LABELS[categoryFilter as CategoryKey]}」目前沒有未收商品`}
              </Alert>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5, alignItems: 'start' }}>
                {receiveGroups.map(g => (
                  <Box key={g.key} sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e8e5e1', overflow: 'hidden' }}>
                    {/* Photo — tap to open the image gallery and verify the garment */}
                    <Box role="button" onClick={() => handleReceiveOpenGallery(g)}
                      sx={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', bgcolor: '#f5f3f0', cursor: 'pointer', overflow: 'hidden' }}>
                      {g.imageUrl
                        ? <Box component="img" src={g.imageUrl} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c7c2bb', fontSize: '0.8rem' }}>無圖</Box>}
                      <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#4c5e8a', color: '#fff', borderRadius: '999px', px: 1.25, py: 0.3, fontSize: '0.82rem', fontWeight: 800, boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}>
                        還需 {g.totalCount} 件
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '6px', px: 0.75, py: 0.25, fontSize: '0.7rem', fontWeight: 600 }}>
                        🔍 點圖看更多照片
                      </Box>
                    </Box>
                    <Box sx={{ p: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.productName}</Typography>
                        {g.tag && <Chip label={g.tag} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f5f3f0', color: '#78716c', flexShrink: 0, maxWidth: 120 }} />}
                      </Box>
                      {/* 選色 — which of these variant names is the garment in hand? */}
                      {g.variants.length > 1 && (
                        <Chip
                          label="選色對照"
                          size="small"
                          onClick={() => handleOpenColorPlate({ sku: g.sku, productName: g.productName })}
                          sx={{
                            mt: 0.75, height: 26, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                            bgcolor: '#fff', color: '#2B3A4E', border: '1px solid #e8e5e1',
                            '&:hover': { bgcolor: '#faf9f7', borderColor: '#2B3A4E' },
                          }}
                        />
                      )}
                      {/* Variant buttons — tap = receive that variant (FIFO oldest order) */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {g.variants.map(v => {
                          const nextQty = v.candidates[0]?.item.quantity || 1;
                          const label = v.color && v.size ? `${v.color}/${v.size}` : (v.variant !== '-' ? v.variant : '單一規格');
                          return (
                            <MuiButton key={v.variant} variant="outlined" onClick={() => handleReceiveVariantTap(g, v)}
                              sx={{
                                minHeight: 44, px: 1.5, textTransform: 'none', borderRadius: '10px',
                                borderColor: '#d6d3d1', color: '#1a1a1a', fontWeight: 700, fontSize: '0.85rem',
                                display: 'inline-flex', alignItems: 'center', gap: 0.75, lineHeight: 1.2,
                                '&:hover': { borderColor: '#4c5e8a', bgcolor: '#eef2ff' },
                                '&:active': { transform: 'scale(0.97)' },
                              }}>
                              <span>{label}</span>
                              <Box component="span" sx={{ bgcolor: '#4c5e8a', color: '#fff', borderRadius: '999px', px: 0.9, py: 0.1, fontSize: '0.75rem', fontWeight: 800 }}>×{v.lineCount}</Box>
                              {nextQty > 1 && (
                                <Box component="span" sx={{ bgcolor: '#b45309', color: '#fff', borderRadius: '6px', px: 0.6, py: 0.1, fontSize: '0.68rem', fontWeight: 800 }}>本筆×{nextQty}</Box>
                              )}
                            </MuiButton>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '8px' }}>
            {searchQuery ? `找不到符合「${searchQuery}」的訂單` : '目前沒有待處理訂單'}
          </Alert>
        ) : (
          <>
            {paginatedOrders.map(order => (
              <UnifiedOrderCard
                key={order.id}
                order={order}
                visibleItems={expandedOrderIds.has(order.id) ? order.items : order.matchedItems}
                onToggleReceived={handleToggleReceived}
                onUpdateField={handleUpdateField}
                onShip={handleShip}
                onPrintLabel={handlePrintLabel}
                onQuickShip={handleQuickShip}
                onHsinchuShip={handleShip}
                onFulfill={handleFulfill}
                onReship={handleReship}
                onOutOfStockNotify={handleOutOfStockNotify}
                onRefund={handleRefund}
                onCancelOrder={handleCancelOrder}
                onRevertShipped={handleRevertShipped}
                onToggleOutOfStock={handleToggleOutOfStock}
                onVariantUpdate={handleVariantUpdate}
                onSkuUpdate={handleSkuUpdate}
                onCustomerUpdate={handleCustomerUpdate}
                onAddressUpdate={handleAddressUpdate}
                onStorefrontShipped={handleStorefrontShipped}
                onTogglePin={handleTogglePin}
                slotInfo={slotInfoById.get(order.id)}
                onResetSlot={handleResetSlot}
                onOpenColorPlate={handleOpenColorPlate}
                                  onCounterReceive={handleCounterReceive}
                                  onCounterCancelLine={handleCounterCancelLine}
                                  onCounterCancelOrder={handleCounterCancelOrder}
                                  counterQueue={counterQueue}
                showLinks={canSeeLinks}
                getProductLink={canSeeLinks ? getProductLink : undefined}
                shippingInProgress={shippingOrderId === order.id}
                isExpanded={expandedOrderIds.has(order.id)}
                onExpandAll={(o) => setExpandedOrderIds(prev => {
                  const next = new Set(prev);
                  if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                  return next;
                })}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* 選色對照 — storefront flat lay + labelled colour markers */}
      <ColorPlateDialog
        open={!!colorPlate}
        onClose={() => setColorPlate(null)}
        sku={colorPlate?.sku}
        productName={colorPlate?.productName}
        highlightColor={colorPlate?.color}
      />

      {/* Turning 多帳號模式 off wipes the SKU → 帳號 map, which is the only
          record of which cart holds which 款. Confirm before dropping it. */}
      <Dialog open={cartClearConfirm} onClose={() => setCartClearConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>關閉多帳號模式？</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: '#5c5854' }}>
            會清除所有「款式 → 購物車帳號」的分配紀錄，下次重新開啟時會從空的帳號1
            重新分配。請確認這一輪的購物車都已經結帳，否則之後補單時將無法得知某個
            款式當初加在哪一個帳號。
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCartClearConfirm(false)} sx={{ textTransform: 'none', color: '#8c8580' }}>
            取消
          </MuiButton>
          <MuiButton onClick={handleClearCartAccounts} variant="contained" color="warning" sx={{ textTransform: 'none' }}>
            關閉並清除
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      {shippingModalOrder && (
        <ShippingPreparationModal
          open={!!shippingModalOrder}
          onClose={() => setShippingModalOrder(null)}
          order={shippingModalOrder}
          enqueueSnackbar={enqueueSnackbar}
          onShippingPrepared={handleShippingPrepared}
        />
      )}
      {/* 收貨 換訂單 picker — move the credit to a different candidate order */}
      <Dialog open={receiveSwitchOpen} onClose={() => setReceiveSwitchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>換一筆訂單記收貨</DialogTitle>
        <DialogContent dividers>
          {receiveConfirm && (
            <>
              <Typography sx={{ fontSize: '0.85rem', color: '#78716c', mb: 1.5 }}>
                {receiveConfirm.productName}{receiveConfirm.variant !== '-' ? ` ${receiveConfirm.variant}` : ''} — 目前記在 {receiveConfirm.order.orderNumber}
              </Typography>
              <Stack spacing={1}>
                {receiveConfirm.candidates.map(c => {
                  const isCurrent = c.item.id === receiveConfirm.item.id;
                  return (
                    <Box key={`${c.order.id}-${c.item.id}`}
                      onClick={() => { if (!isCurrent) handleReceiveSwitchOrder(c); }}
                      sx={{
                        p: 1.25, borderRadius: '8px', border: '1px solid',
                        borderColor: isCurrent ? '#4c5e8a' : '#e8e5e1',
                        bgcolor: isCurrent ? '#eef2ff' : '#fff',
                        cursor: isCurrent ? 'default' : 'pointer',
                        '&:hover': isCurrent ? {} : { bgcolor: '#f5f3f0', borderColor: '#4c5e8a' },
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#4c5e8a', fontFamily: '"DM Mono", monospace' }}>{c.order.orderNumber}</Typography>
                        {isCurrent && <Chip label="目前" size="small" sx={{ height: 18, fontSize: '0.68rem', bgcolor: '#4c5e8a', color: '#fff' }} />}
                        <Typography sx={{ ml: 'auto', fontSize: '0.75rem', color: '#a8a29e' }}>{c.order.orderDateDisplay?.slice(0, 16)}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.8rem', color: '#57534e' }}>{c.order.customerName}</Typography>
                      {c.item.note && (
                        <Typography sx={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 600, mt: 0.25 }}>備註：{c.item.note}</Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setReceiveSwitchOpen(false)} sx={{ textTransform: 'none' }}>取消</MuiButton>
        </DialogActions>
      </Dialog>
      {/* 收貨 image gallery — tap the card photo to verify the garment */}
      <Dialog open={!!receiveGallery} onClose={() => setReceiveGallery(null)} maxWidth="md" fullWidth>
        {receiveGallery && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>{receiveGallery.title}</DialogTitle>
            <DialogContent dividers sx={{ p: 1.5 }}>
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f3f0', borderRadius: '8px', minHeight: 320 }}>
                <Box component="img" src={receiveGallery.images[receiveGalleryIndex] || receiveGallery.images[0]} alt=""
                  sx={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', display: 'block' }} />
                {receiveGallery.images.length > 1 && (
                  <>
                    <IconButton
                      onClick={() => setReceiveGalleryIndex(i => (i - 1 + receiveGallery.images.length) % receiveGallery.images.length)}
                      sx={{ position: 'absolute', left: 8, width: 44, height: 44, bgcolor: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: '1.4rem', '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' } }}>
                      ‹
                    </IconButton>
                    <IconButton
                      onClick={() => setReceiveGalleryIndex(i => (i + 1) % receiveGallery.images.length)}
                      sx={{ position: 'absolute', right: 8, width: 44, height: 44, bgcolor: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: '1.4rem', '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' } }}>
                      ›
                    </IconButton>
                    <Chip label={`${receiveGalleryIndex + 1}/${receiveGallery.images.length}`} size="small"
                      sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem', height: 20 }} />
                  </>
                )}
              </Box>
              {receiveGallery.images.length > 1 && (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, overflowX: 'auto', pb: 0.5 }}>
                  {receiveGallery.images.map((img, idx) => (
                    <Box key={idx} component="img" src={img} alt="" loading="lazy"
                      onClick={() => setReceiveGalleryIndex(idx)}
                      sx={{
                        width: 56, height: 56, objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                        border: '2px solid', borderColor: idx === receiveGalleryIndex ? '#4c5e8a' : 'transparent',
                      }} />
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <MuiButton onClick={() => setReceiveGallery(null)} sx={{ textTransform: 'none' }}>關閉</MuiButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* 國際宅配 — SG via Parxl (separate carrier, separate dialog) */}
      {parxlModalOrder && (
        <ParxlShippingModal
          open={!!parxlModalOrder}
          onClose={() => setParxlModalOrder(null)}
          order={parxlModalOrder}
          enqueueSnackbar={enqueueSnackbar}
          onShippingPrepared={handleParxlShippingPrepared}
        />
      )}

      {/* 國際宅配 — SF International (IUOP), HK/MO */}
      {sfModalOrder && (
        <SFInternationalShippingModal
          open={!!sfModalOrder}
          onClose={() => setSfModalOrder(null)}
          order={sfModalOrder}
          country={sfCountryOf(sfModalOrder) || 'HK'}
          enqueueSnackbar={enqueueSnackbar}
          onShippingPrepared={handleSFShippingPrepared}
        />
      )}
      {/* 補寄 Dialog */}
      <Dialog open={reshipDialogOpen} onClose={() => { setReshipDialogOpen(false); setReshipOrder(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {reshipIsHK
            ? `補寄 — ${reshipRegion?.nameZh || '香港'} (順豐)`
            : reshipIsSfTw ? '補寄 — 台灣宅配 (順豐)' : '補寄'}
        </DialogTitle>
        <DialogContent>
          {reshipOrder && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                訂單編號: {reshipOrder.orderId}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                客戶: {reshipOrder.customerName}
              </Typography>
              {reshipIsHK || reshipIsSfTw ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  收件地址: {reshipOrder.shippingAddress || '—'}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  門市: {reshipOrder.storeName || reshipOrder.storeId}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                金額: NT${Math.floor(reshipOrder.totalAmount || 0).toLocaleString()}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {reshipIsHK ? (
                <Alert severity="info" sx={{ mt: 0 }}>
                  系統將向順豐建立新的補寄單（customerOrderNo 自動加 -R{(reshipOrder.reships?.filter(r => r.carrier === 'sf').length || 0) + 1} 後綴），
                  成功後會自動開啟順豐出貨單新分頁。收件人地址沿用原訂單。
                </Alert>
              ) : reshipIsSfTw ? (
                <Alert severity="info" sx={{ mt: 0 }}>
                  系統將向順豐建立<strong>全新的宅配單號</strong>（customerOrderNo 自動加 -R{reshipSfTwNextNo} 後綴），
                  不會沿用原本的順豐單號 {reshipOrder.original?.sf_waybill_no || ''}，成功後自動開啟面單新分頁。
                  收件人地址沿用原訂單（如需改地址，請先在訂單卡片修改再補寄）。
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>付款方式</Typography>
                  <RadioGroup value={reshipPaymentType} onChange={(e) => setReshipPaymentType(e.target.value as 'Y' | 'N')}>
                    <FormControlLabel value="N" control={<Radio size="small" />} label="取貨不用付款（已線上付款）" />
                    <FormControlLabel value="Y" control={<Radio size="small" />} label="貨到付款" />
                  </RadioGroup>
                  {reshipPaymentType === 'Y' && (
                    <TextField
                      fullWidth size="small" type="number" label="貨到付款金額 (NT$)"
                      value={reshipCodAmount}
                      onChange={(e) => setReshipCodAmount(e.target.value)}
                      sx={{ mt: 2 }}
                      inputProps={{ min: 1 }}
                      helperText={`訂單原金額: NT$${Math.floor(reshipOrder.totalAmount || 0).toLocaleString()}`}
                    />
                  )}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    系統將自動產生新的寄件編號（如 {reshipOrder.orderId}-1）以避免重複
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => { setReshipDialogOpen(false); setReshipOrder(null); }}>取消</MuiButton>
          <MuiButton variant="contained" color="primary" onClick={handleReshipSubmit}>
            {reshipIsHK || reshipIsSfTw ? '確認補寄並列印順豐單' : '確認補寄'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* 退刷 Dialog (storefront) */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => { if (!refundProcessing) { setRefundDialogOpen(false); setRefundOrder(null); setRefundHistory([]); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          退刷 {refundOrder && `(${refundOrder.paymentMethod === 'line_pay' ? 'LINE Pay' : '信用卡'})`}
        </DialogTitle>
        <DialogContent>
          {refundOrder && (() => {
            const order = refundOrder;
            const ctx = buildRefundCtx(order);
            const oosItems = order.items.filter(i => i.outOfStock);
            const oosSum = oosItems.reduce((s, i) => s + (ctx.itemLineTotals.get(i.id) || 0), 0);
            const selectedSum = Array.from(refundSelectedIds).reduce(
              (s, id) => s + (ctx.itemLineTotals.get(id) || 0), 0,
            );
            const remaining = Math.max(0, ctx.paidTotal - alreadyRefunded);
            const fullyRefunded = remaining === 0 && alreadyRefunded > 0;
            return (
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">訂單: {order.orderId}</Typography>
                  <Typography variant="body2" color="text.secondary">{order.customerName}</Typography>
                </Stack>
                <Box sx={{ mb: 2, fontSize: 13, color: 'text.secondary', lineHeight: 1.7 }}>
                  <div>
                    商品原價合計: NT${Math.floor(ctx.itemsSubtotal).toLocaleString()}
                    {ctx.discountAmount > 0 && <>　｜　訂單促銷: -NT${ctx.discountAmount.toLocaleString()}</>}
                    {ctx.shippingFee > 0 && <>　｜　運費: NT${ctx.shippingFee.toLocaleString()}</>}
                  </div>
                  <div>
                    <strong>訂單原金額: NT${ctx.originalTotal.toLocaleString()}</strong>
                    {ctx.storeCreditUsed > 0 && (
                      <>　｜　購物金折抵: -NT${ctx.storeCreditUsed.toLocaleString()}　｜　<strong>實付: NT${ctx.paidTotal.toLocaleString()}</strong></>
                    )}
                    {ctx.oosDeductionApplied > 0 && (
                      <span style={{ color: '#92400e' }}>
                        　｜　已套用斷貨通知扣款: NT${ctx.oosDeductionApplied.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {ctx.keepsTier && (
                    <div style={{ color: '#92400e' }}>
                      扣除斷貨後仍達滿額門檻 — 斷貨商品以原價全額退，客人保留 NT${ctx.discountAmount.toLocaleString()} 折扣
                    </div>
                  )}
                </Box>

                {/* 已退刷紀錄 — populated from /orders/:orderId/refunds (payment_refunds ledger) */}
                {refundHistoryLoading ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    讀取退刷紀錄中…
                  </Typography>
                ) : refundHistory.length > 0 ? (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff7ed', borderRadius: 1, border: '1px solid #fed7aa' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#9a3412', display: 'block', mb: 0.5 }}>
                      已退刷紀錄（{refundHistory.length} 筆，累計 NT${alreadyRefunded.toLocaleString()}）
                    </Typography>
                    {refundHistory.map(r => {
                      const ts = (() => { try { return new Date(r.created_at).toLocaleString('zh-TW', { hour12: false }); } catch { return r.created_at; } })();
                      const failed = r.rtn_code && r.rtn_code !== '1' && r.rtn_code !== '0000';
                      return (
                        <Typography key={r.id} variant="caption" sx={{ display: 'block', color: failed ? '#991b1b' : '#9a3412', lineHeight: 1.6 }}>
                          {ts}　·　NT${(r.amount || 0).toLocaleString()}
                          {r.created_by && <>　·　{r.created_by}</>}
                          {failed && <>　·　失敗（{r.rtn_message || r.rtn_code}）</>}
                        </Typography>
                      );
                    })}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600, color: '#9a3412' }}>
                      可退餘額: NT${remaining.toLocaleString()}
                    </Typography>
                  </Box>
                ) : null}

                {fullyRefunded && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    此訂單已全額退刷完畢，無剩餘可退金額
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <RadioGroup
                  row
                  value={refundMode}
                  onChange={(e) => handleRefundModeChange(e.target.value as 'full' | 'partial')}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel value="full" control={<Radio size="small" />} label="全額退刷" />
                  <FormControlLabel value="partial" control={<Radio size="small" />} label="部分退刷" />
                </RadioGroup>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {refundMode === 'partial'
                    ? '勾選要退刷的商品會自動加總（已分攤促銷折扣）；也可手動修改下方金額'
                    : '整筆訂單原金額將全數退刷（含運費）'}
                </Typography>

                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      {refundMode === 'partial' && <TableCell padding="checkbox" />}
                      <TableCell sx={{ fontWeight: 600 }}>商品</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 60 }}>數量</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, width: 110 }}>實付小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map(item => {
                      const lineTotal = ctx.itemLineTotals.get(item.id) || 0;
                      const checked = refundSelectedIds.has(item.id);
                      return (
                        <TableRow
                          key={item.id}
                          sx={{ bgcolor: item.outOfStock ? '#fff8e1' : 'transparent' }}
                        >
                          {refundMode === 'partial' && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => toggleRefundItem(item.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.productName}
                              {item.outOfStock && (
                                <Chip label="斷貨" size="small"
                                  sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: '#fde68a', color: '#92400e' }} />
                              )}
                            </Typography>
                            {item.variantOrSpec && (
                              <Typography variant="caption" color="text.secondary">{item.specOverride || item.variantOrSpec}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">NT${lineTotal.toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {oosSum > 0 && refundMode === 'partial' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    斷貨商品實付合計: NT${oosSum.toLocaleString()}
                  </Typography>
                )}

                <TextField
                  fullWidth size="small" type="number" label="退刷金額 (NT$)"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  inputProps={{ min: 1, max: remaining }}
                  disabled={fullyRefunded}
                  helperText={
                    refundMode === 'partial' && selectedSum !== parseInt(refundAmount || '0', 10)
                      ? `勾選合計 NT$${selectedSum.toLocaleString()}（已手動調整）`
                      : alreadyRefunded > 0
                        ? `可退餘額 NT$${remaining.toLocaleString()}（實付 NT$${ctx.paidTotal.toLocaleString()} − 已退 NT$${alreadyRefunded.toLocaleString()}）`
                        : `最高 NT$${remaining.toLocaleString()}（原付款金額）`
                  }
                />

                <Alert severity="warning" sx={{ mt: 2 }}>
                  退刷操作無法撤銷，請確認金額後送出
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => { setRefundDialogOpen(false); setRefundOrder(null); setRefundHistory([]); }}
            disabled={refundProcessing}
          >
            取消
          </MuiButton>
          <MuiButton
            variant="contained"
            color="warning"
            onClick={handleRefundSubmit}
            disabled={(() => {
              if (refundProcessing) return true;
              const n = parseInt(refundAmount || '0', 10);
              if (!n) return true;
              if (refundOrder) {
                const ctx = buildRefundCtx(refundOrder);
                if (n > Math.max(0, ctx.paidTotal - alreadyRefunded)) return true;
              }
              return false;
            })()}
          >
            {refundProcessing ? '處理中...' : `確認退刷 NT$${parseInt(refundAmount || '0').toLocaleString()}`}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Admin 斷貨 follow-up dialog — sell out one colour, or delist the product */}
      <Dialog
        open={delistDialogOpen}
        onClose={() => { if (!delistProcessing) { setDelistDialogOpen(false); setDelistItem(null); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>斷貨後續處理</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {delistItem?.productName}（SKU: {delistItem?.sku}
            {delistItem?.variantOrSpec ? ` · ${delistItem.variantOrSpec}` : ''}）已標記為斷貨，要怎麼處理商品頁？
          </Typography>

          {oosOptionsLoading && (
            <Typography variant="caption" color="text.secondary">讀取規格中…</Typography>
          )}

          <RadioGroup
            value={delistMode}
            onChange={(e) => setDelistMode(e.target.value as 'variant' | 'product')}
          >
            <FormControlLabel
              value="variant"
              disabled={!hasSplittableOptions}
              control={<Radio />}
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  只停售斷貨的顏色／尺寸（其他規格照常販售）
                </Typography>
              }
            />
            {delistMode === 'variant' && (
              <Box sx={{ pl: 4, pb: 1 }}>
                {(oosOptions?.storefront?.optionGroups || []).map(group => (
                  <Box key={group.position} sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {group.name}
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0 }}>
                      {group.values.map(v => (
                        <FormControlLabel
                          key={`${group.position}-${v.value}`}
                          sx={{ mr: 2 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={v.disabled || selectedOptionValues.includes(v.value)}
                              disabled={v.disabled}
                              onChange={(e) => setSelectedOptionValues(prev =>
                                e.target.checked
                                  ? [...prev, v.value]
                                  : prev.filter(x => x !== v.value))}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {v.value}
                              {v.disabled && (
                                <Chip label="已停售" size="small" sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
                              )}
                            </Typography>
                          }
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
                {!oosOptionsLoading && !hasSplittableOptions && (
                  <Typography variant="caption" color="text.secondary">
                    這個商品沒有多個顏色／尺寸可分開停售。
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              value="product"
              control={<Radio />}
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  整個商品下架
                </Typography>
              }
            />
          </RadioGroup>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            套用到
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={delistStorefront}
                  onChange={(e) => setDelistStorefront(e.target.checked)}
                />
              }
              label={delistMode === 'variant' ? '官網停售此規格 (metanoia.tw)' : '從官網下架 (metanoia.tw)'}
            />
            <br />
            <FormControlLabel
              control={
                <Checkbox
                  checked={delistShopee}
                  onChange={(e) => setDelistShopee(e.target.checked)}
                />
              }
              label={delistMode === 'variant' ? '蝦皮該規格庫存設為 0（售完）' : '從蝦皮下架 (UNLIST)'}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            {delistMode === 'variant'
              ? '只影響勾選的規格，其他顏色／尺寸仍可下單。此操作不會影響此訂單。'
              : '此操作只針對該 SKU 的商品，不會影響此訂單或其他未斷貨商品。'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => { setDelistDialogOpen(false); setDelistItem(null); }}
            disabled={delistProcessing}
          >
            略過
          </MuiButton>
          {delistMode === 'variant' ? (
            <MuiButton
              variant="contained"
              color="warning"
              onClick={handleDisableVariantSubmit}
              disabled={delistProcessing || selectedOptionValues.length === 0 || (!delistStorefront && !delistShopee)}
            >
              {delistProcessing
                ? '停售中...'
                : `確認停售${selectedOptionValues.length ? ` ${selectedOptionValues.join('、')}` : ''}`}
            </MuiButton>
          ) : (
            <MuiButton
              variant="contained"
              color="warning"
              onClick={handleDelistSubmit}
              disabled={delistProcessing || (!delistStorefront && !delistShopee)}
            >
              {delistProcessing ? '下架中...' : '確認下架'}
            </MuiButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceivingQueuePage;
